// ============================================================================
// AdminDashboard — Pannello completo per il datore di lavoro
// ============================================================================

import { useState, useMemo, useEffect, useRef } from "react";
import {
  Home, Clock, Users, Building2, BarChart3, Bell, Settings,
  LogOut, Menu, X, Search, Download, Filter, Plus, FileSpreadsheet,
  ChevronLeft, ChevronRight, Calendar, Maximize2, Minimize2,
  AlertTriangle, Truck, Edit3, Check, Save, GraduationCap,
} from "lucide-react";
import { CANTIERI as CANTIERI_DEFAULT, AVATAR_COLORS, STATUS_COLORS } from "../../lib/constants";
import { COLONNE_VISITE, scadenzaColor } from "../../lib/visiteDipendenti";
import { formatDate, todayStr, filterByPeriod, getInitials } from "../../lib/helpers";
import { useScadenze, useAppSettings } from "../../hooks/useOreData";
import { supabase, isSupabaseConfigured } from "../../lib/supabase";
import { sendVisitPush } from "../../lib/sendVisitPush";

import { useSchedaDipendenti } from "../../hooks/useSchedaDipendenti";
import { useAutomezzi } from "../../hooks/useAutomezzi";
import { useClassi } from "../../hooks/useClassi";
import { CANTIERI_META_DEFAULT } from "../../lib/cantieriMeta";
import * as XLSX from "xlsx";

// Helper: calcola tutte le chiavi degli alert urgenti (stesso criterio di ScadenzePage)
function computeAlertKeys(dipendenti, schedaDipendenti, automezzi) {
  const keys = [];
  const today = new Date();
  dipendenti.forEach((d) => {
    const s = schedaDipendenti[d.id];
    if (!s) return;
    const check = (dateStr, label, days) => {
      if (!dateStr) return;
      const diff = (new Date(dateStr) - today) / (1000 * 60 * 60 * 24);
      if (diff <= days) keys.push(`dip-${d.id}-${label}`);
    };
    check(s.form_gen, "Formazione Gen. + Spec.", 30);
    check(s.antincendio, "Antincendio/Emergenza", 30);
    check(s.primo_soccorso, "Primo Soccorso", 30);
    check(s.piattaforma, "Piattaforma", 30);
    check(s.rls, "RLS", 30);
    check(s.escavatore, "Escavatore", 30);
    check(s.gru, "Gru", 30);
    check(s.lav_quota, "Lavori in Quota", 30);
    check(s.preposto, "Preposto", 30);
    check(s.scad_pds, "Permesso di Soggiorno", 30);
    check(s.scad_contratto, "Scadenza Contratto", 15);
    check(s.visita_medica, "Visita Medica", 15);
  });
  automezzi.forEach((a) => {
    const check = (dateStr, label) => {
      if (!dateStr) return;
      const diff = (new Date(dateStr) - today) / (1000 * 60 * 60 * 24);
      if (diff <= 30) keys.push(`auto-${a.id}-${label}`);
    };
    check(a.scad_assicurazione, "Assicurazione");
    check(a.scad_revisione, "Revisione");
    check(a.scad_tagliando, "Tagliando");
  });
  return keys;
}

export default function AdminDashboard({ onLogout, oreData, setOreData, upsertOreEntry, deleteOreEntry, cantieri, addCantiere, deleteCantiere, restoreCantiere, archivedCantieri, promemoria = [], addPromemoria = () => {}, deletePromemoria = () => {}, dipendenti: dipendentiProp = [], saveDipendenti = () => {} }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [redTheme, setRedTheme] = useState(() => localStorage.getItem("adminTheme") === "red");

  const toggleTheme = () => {
    setRedTheme((prev) => {
      const next = !prev;
      localStorage.setItem("adminTheme", next ? "red" : "dark");
      return next;
    });
  };
  const [filterDip, setFilterDip] = useState("");
  const [filterCantiere, setFilterCantiere] = useState("");
  const [filterPeriodo, setFilterPeriodo] = useState("tutto");
  const [searchText, setSearchText] = useState("");
  // Dipendenti: arrivano dal parent (App.jsx) via Supabase, con realtime sync automatico
  const dipendenti = dipendentiProp;
  const setDipendenti = (updaterOrList) => {
    const newList = typeof updaterOrList === "function" ? updaterOrList(dipendenti) : updaterOrList;
    saveDipendenti(newList);
  };

  const { saveScadenza, saveNote, mergeDipendenti } = useScadenze();
  const { schedaDipendenti, saveSchedaDipendente } = useSchedaDipendenti();
  const { automezzi, saveAutomezzo, addAutomezzo, deleteAutomezzo } = useAutomezzi();
  const classiHook = useClassi();
  const [openDipId, setOpenDipId] = useState(null);
  const [openAutoId, setOpenAutoId] = useState(null);

  // ---- Override ore cantieri — sincronizzato via Supabase ----
  const [oreOverride, saveOreOverride] = useAppSettings("oreOverrideCantieri", {});
  const setOreOverride = saveOreOverride;
  const commitOreOverride = async (key, value) => {
    // Read-merge-write: fetch current value from Supabase to avoid overwriting concurrent changes
    let current = { ...oreOverride };
    if (isSupabaseConfigured) {
      try {
        const { data } = await supabase
          .from("app_settings")
          .select("value")
          .eq("key", "oreOverrideCantieri")
          .maybeSingle();
        if (data?.value && typeof data.value === "object") {
          current = { ...data.value };
        }
      } catch (e) { /* fallback to local state */ }
    }
    if (value === "" || value === null || value === undefined) {
      delete current[key];
    } else {
      current[key] = Number(value);
    }
    saveOreOverride(current);
  };

  // ---- PIN dipendenti — sincronizzati via Supabase ----
  const [dipendentiPins, saveDipendentiPins] = useAppSettings("dipendentiPins", {});

  // ---- Nomi override — per modificare nomi dipendenti base (persistiti via Supabase) ----
  const [nomiOverride, saveNomiOverride] = useAppSettings("dipendentiNomiOverride", {});

  // ---- Visite programmate (appuntamenti) — sincronizzate via Supabase ----
  const [visiteProgrammate, saveVisiteProgrammateRaw] = useAppSettings("visiteProgrammate", {});

  // ---- Formazioni programmate — sincronizzate via Supabase ----
  const [formazioneProgrammate, saveFormazioneProgrammateRaw] = useAppSettings("formazioneProgrammate", {});

  // ---- Metadati cantieri — sincronizzati via Supabase ----
  const [cantieriMeta, saveCantieriMeta_] = useAppSettings("cantieriMeta", CANTIERI_META_DEFAULT);
  const saveCantieriMeta = (cantiere, field, value) => {
    const next = { ...cantieriMeta, [cantiere]: { ...(cantieriMeta[cantiere] || {}), [field]: value } };
    saveCantieriMeta_(next);
  };

  // ---- Cronologia azioni ----
  const [actionHistory, setActionHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("actionHistory") || "[]"); }
    catch { return []; }
  });

  const addToHistory = (action) => {
    setActionHistory((prev) => {
      const next = [{ ...action, id: Date.now(), timestamp: new Date().toISOString() }, ...prev].slice(0, 100);
      localStorage.setItem("actionHistory", JSON.stringify(next));
      return next;
    });
  };

  const handleAddCantiere = (nome) => {
    addCantiere(nome);
    addToHistory({ type: "add_cantiere", cantiere: nome, label: `Cantiere aggiunto: ${nome}` });
  };

  const handleDeleteCantiere = (nome) => {
    deleteCantiere(nome);
    addToHistory({ type: "delete_cantiere", cantiere: nome, label: `Cantiere eliminato: ${nome}` });
  };

  const handleRestoreCantiere = (nome) => {
    restoreCantiere(nome);
    addToHistory({ type: "restore_cantiere", cantiere: nome, label: `Cantiere ripristinato: ${nome}` });
  };

  const undoAction = (action) => {
    if (action.type === "delete_cantiere") {
      restoreCantiere(action.cantiere);
      addToHistory({ type: "restore_cantiere", cantiere: action.cantiere, label: `Annullato — ripristinato: ${action.cantiere}`, isUndo: true });
    } else if (action.type === "add_cantiere" || action.type === "restore_cantiere") {
      deleteCantiere(action.cantiere);
      addToHistory({ type: "delete_cantiere", cantiere: action.cantiere, label: `Annullato — eliminato: ${action.cantiere}`, isUndo: true });
    }
    setActionHistory((prev) => {
      const next = prev.filter((a) => a.id !== action.id);
      localStorage.setItem("actionHistory", JSON.stringify(next));
      return next;
    });
  };

  // Applica scadenze da Supabase sopra quelle default
  const dipendentiMerged = useMemo(() => {
    const base = mergeDipendenti(dipendenti);
    if (nomiOverride && Object.keys(nomiOverride).length > 0) {
      return base.map((d) => {
        const override = nomiOverride[d.id];
        if (override) {
          const avatar = override.split(" ").map(p => p[0]).join("").substring(0, 2).toUpperCase();
          return { ...d, name: override, avatar };
        }
        return d;
      });
    }
    return base;
  }, [dipendenti, mergeDipendenti, nomiOverride]);

  // Dipendenti attivi (senza eliminati) — per la UI
  const dipendentiAttivi = useMemo(() => dipendentiMerged.filter((d) => !d.eliminatoIl), [dipendentiMerged]);

  // Dipendenti eliminati — per la sezione archivio
  const dipendentiEliminati = useMemo(() => dipendentiMerged.filter((d) => !!d.eliminatoIl), [dipendentiMerged]);

  // ---- Wrapper saveVisite/Formazione: dopo il salvataggio, invia push se data nuova/modificata
  const saveVisiteProgrammate = async (next) => {
    const prev = visiteProgrammate || {};
    await saveVisiteProgrammateRaw(next);
    try {
      Object.keys(next || {}).forEach((dipId) => {
        const cur = next[dipId];
        if (!cur?.data) return;
        const before = prev[dipId];
        const cambiata = !before || before.data !== cur.data || (before.ora || null) !== (cur.ora || null);
        if (!cambiata) return;
        const dip = dipendentiMerged.find((d) => String(d.id) === String(dipId));
        sendVisitPush({
          dipId,
          dipName: dip?.name || null,
          data: cur.data,
          ora: cur.ora || null,
          tipo: "visita",
        });
      });
    } catch (e) { console.warn("[push visita]", e); }
  };

  const saveFormazioneProgrammate = async (next) => {
    const prev = formazioneProgrammate || {};
    await saveFormazioneProgrammateRaw(next);
    try {
      Object.keys(next || {}).forEach((dipId) => {
        const cur = next[dipId];
        if (!cur?.data) return;
        const before = prev[dipId];
        const cambiata = !before || before.data !== cur.data || (before.ora || null) !== (cur.ora || null);
        if (!cambiata) return;
        const dip = dipendentiMerged.find((d) => String(d.id) === String(dipId));
        sendVisitPush({
          dipId,
          dipName: dip?.name || null,
          data: cur.data,
          ora: cur.ora || null,
          tipo: "formazione",
        });
      });
    } catch (e) { console.warn("[push formazione]", e); }
  };

  // ---- Scadenze nuove — badge giallo nella sidebar ----
  const [scadenzeNewKeys, setScadenzeNewKeys] = useState([]);

  useEffect(() => {
    if (!schedaDipendenti || Object.keys(schedaDipendenti).length === 0) return;
    const currentKeys = computeAlertKeys(dipendentiAttivi, schedaDipendenti, automezzi);
    let seenKeys = [];
    try { seenKeys = JSON.parse(localStorage.getItem("scadenzeSeenKeys") || "[]"); } catch {}
    const seenSet = new Set(seenKeys);
    const nuove = currentKeys.filter((k) => !seenSet.has(k));
    setScadenzeNewKeys(nuove);
  }, [schedaDipendenti, automezzi, dipendentiAttivi]);

  const handleNavigate = (id) => {
    if (id === "scadenze") {
      const currentKeys = computeAlertKeys(dipendentiAttivi, schedaDipendenti, automezzi);
      let seenKeys = [];
      try { seenKeys = JSON.parse(localStorage.getItem("scadenzeSeenKeys") || "[]"); } catch {}
      const merged = [...new Set([...seenKeys, ...currentKeys])];
      localStorage.setItem("scadenzeSeenKeys", JSON.stringify(merged));
      setScadenzeNewKeys([]);
    }
    setPage(id);
    setSidebarOpen(false);
  };

  const oggi = todayStr();

  // ---- Filtered data ----
  const filtered = useMemo(() => {
    let result = filterByPeriod(oreData, filterPeriodo);
    if (filterDip) result = result.filter((o) => o.dipendente === filterDip);
    if (filterCantiere) result = result.filter((o) => o.cantiere === filterCantiere);
    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (o) => o.dipendente.toLowerCase().includes(q) || o.cantiere.toLowerCase().includes(q)
      );
    }
    return result;
  }, [oreData, filterPeriodo, filterDip, filterCantiere, searchText]);

  // ---- Stats ----
  const totalOre = filtered.reduce((s, o) => s + o.ore, 0);
  const avgOre = filtered.length ? (totalOre / filtered.length).toFixed(1) : 0;
  const uniqueDip = [...new Set(filtered.map((o) => o.dipendente))].length;
  const uniqueCant = [...new Set(filtered.map((o) => o.cantiere))].length;

  // Missing today
  const todayEntries = oreData.filter((o) => o.data === oggi);
  const todayDips = new Set(todayEntries.map((o) => o.dipendenteId));
  const missing = dipendentiAttivi.filter((d) => !todayDips.has(d.id));

  // Chart: last 7 days
  const last7 = useMemo(() => {
    const data = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const ds = d.toISOString().split("T")[0];
      data.push({
        label: d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric" }),
        value: oreData.filter((o) => o.data === ds).reduce((s, o) => s + o.ore, 0),
        date: ds,
      });
    }
    return data;
  }, [oreData]);
  const max7 = Math.max(...last7.map((d) => d.value), 1);

  // Top cantieri
  const allCant = useMemo(() => {
    const map = {};
    filtered.forEach((o) => {
      if (o.cantiere && o.ore > 0) map[o.cantiere] = (map[o.cantiere] || 0) + o.ore;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [filtered]);
  const topCant = allCant.slice(0, 6);
  const maxCant = topCant.length ? topCant[0][1] : 1;

  // ---- Export Excel ----
  const handleExport = () => {
    const data = filtered.map((o) => ({
      Data: o.data,
      Dipendente: o.dipendente,
      Cantiere: o.cantiere,
      Ore: o.ore,
      Note: o.note || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ore Lavorate");
    XLSX.writeFile(wb, `ore_cantiere_${oggi}.xlsx`);
  };

  // ---- Nav items ----
  const navItems = [
    { id: "dashboard", icon: Home, label: "Dashboard" },
    { id: "ore", icon: Clock, label: "Registro Ore" },
    { id: "dipendenti", icon: Users, label: "Dipendenti" },
    { id: "scadenze", icon: AlertTriangle, label: "Scadenze", newCount: scadenzeNewKeys.length },
    { id: "classi", icon: GraduationCap, label: "Classi" },
    { id: "automezzi", icon: Truck, label: "Automezzi" },
    { id: "cantieri", icon: Building2, label: "Cantieri" },
    { id: "excelore", icon: FileSpreadsheet, label: "File Excel Ore" },
    { id: "excelcantieri", icon: FileSpreadsheet, label: "File Excel Cantieri" },
    { id: "excelvisite", icon: FileSpreadsheet, label: "File Excel Visite" },
  ];

  const barColors = [
    ["var(--primary)", "var(--primary-dark)"],
    ["var(--accent)", "#059669"],
    ["#8b5cf6", "#7c3aed"],
    ["var(--warning)", "#d97706"],
    ["#ec4899", "#db2777"],
    ["#06b6d4", "#0891b2"],
  ];

  // ---- Sidebar — design premium ----
  const mainNav = navItems.slice(0, 4); // Dashboard, Registro, Dipendenti, Scadenze
  const toolsNav = navItems.slice(4);   // Classi, Automezzi, Cantieri, Excel...

  const Sidebar = () => (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: "#0a0a0f",
    }}>
      {/* Logo */}
      <div style={{ padding: "28px 22px 24px", display: "flex", alignItems: "center", gap: 14 }}>
        <svg width="44" height="24" viewBox="0 0 220 120" xmlns="http://www.w3.org/2000/svg">
          <line x1="80" y1="6" x2="214" y2="6" stroke="rgba(255,255,255,0.5)" strokeWidth="3"/>
          <line x1="214" y1="6" x2="214" y2="114" stroke="rgba(255,255,255,0.5)" strokeWidth="3"/>
          <line x1="80" y1="114" x2="214" y2="114" stroke="rgba(255,255,255,0.5)" strokeWidth="3"/>
          <line x1="80" y1="6" x2="80" y2="63" stroke="rgba(255,255,255,0.5)" strokeWidth="3"/>
          <line x1="80" y1="90" x2="80" y2="114" stroke="rgba(255,255,255,0.5)" strokeWidth="3"/>
          <text x="4" y="88" fontFamily="'DM Sans',sans-serif" fontSize="26" fontWeight="700" fill="#C0392B" letterSpacing="-0.8">Mattone Rosso</text>
        </svg>
        <div>
          <p style={{ color: "#fff", fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px" }}>Mattone Rosso</p>
          <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 11, fontWeight: 500 }}>Pannello Datore</p>
        </div>
      </div>

      {/* Main Menu */}
      <nav style={{ flex: 1, padding: "0 14px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
        <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "2px", padding: "16px 12px 10px" }}>Menu principale</p>
        {mainNav.map((item) => {
          const Icon = item.icon;
          const isActive = page === item.id;
          const hasNew = item.newCount > 0;
          return (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              style={{
                display: "flex", alignItems: "center", gap: 14, padding: "13px 14px",
                borderRadius: 14, border: "none", width: "100%", textAlign: "left",
                background: isActive ? "rgba(192,57,43,0.12)" : "transparent",
                color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
                fontWeight: isActive ? 600 : 500, fontSize: 14,
                cursor: "pointer", transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
                fontFamily: "var(--font)",
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                background: isActive ? "#C0392B" : "rgba(255,255,255,0.04)",
                color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
                transition: "all 0.25s",
                boxShadow: isActive ? "0 4px 16px rgba(192,57,43,0.3)" : "none",
              }}>
                <Icon size={18} />
              </div>
              {item.label}
              {hasNew && (
                <span style={{
                  marginLeft: "auto", background: "#f59e0b", color: "#000",
                  borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700,
                }}>{item.newCount}</span>
              )}
            </button>
          );
        })}

        <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "2px", padding: "24px 12px 10px" }}>Strumenti</p>
        {toolsNav.map((item) => {
          const Icon = item.icon;
          const isActive = page === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              style={{
                display: "flex", alignItems: "center", gap: 14, padding: "13px 14px",
                borderRadius: 14, border: "none", width: "100%", textAlign: "left",
                background: isActive ? "rgba(192,57,43,0.12)" : "transparent",
                color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
                fontWeight: isActive ? 600 : 500, fontSize: 14,
                cursor: "pointer", transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
                fontFamily: "var(--font)",
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                background: isActive ? "#C0392B" : "rgba(255,255,255,0.04)",
                color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
                transition: "all 0.25s",
                boxShadow: isActive ? "0 4px 16px rgba(192,57,43,0.3)" : "none",
              }}>
                <Icon size={18} />
              </div>
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: "16px 14px", borderTop: "1px solid rgba(255,255,255,0.04)", display: "flex", flexDirection: "column", gap: 4 }}>

        <button onClick={onLogout} style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
          borderRadius: 14, border: "none", width: "100%", textAlign: "left",
          background: "transparent", color: "rgba(239,68,68,0.6)", fontSize: 13, fontWeight: 500,
          cursor: "pointer", fontFamily: "var(--font)", transition: "all 0.2s",
        }}><LogOut size={18} /> Logout</button>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh",
      background: redTheme
        ? `
          radial-gradient(ellipse at 20% 0%, rgba(192,57,43,0.18) 0%, transparent 55%),
          radial-gradient(ellipse at 80% 100%, rgba(150,40,27,0.14) 0%, transparent 55%),
          var(--bg)
        `
        : "var(--bg)",
      display: "flex",
      position: "relative",
    }}>
      {/* Desktop Sidebar */}
      <div className="desktop-sidebar" style={{
        width: 260,
        background: redTheme
          ? "linear-gradient(180deg, rgba(30,10,10,0.98) 0%, rgba(20,8,8,0.99) 100%)"
          : "var(--surface)",
        borderRight: redTheme ? "1px solid rgba(192,57,43,0.3)" : "1px solid var(--border)",
        position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 20, display: "none",
        boxShadow: redTheme ? "4px 0 24px rgba(192,57,43,0.08)" : "none",
      }}><Sidebar /></div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
          <div onClick={() => setSidebarOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0, width: 280,
            background: redTheme
              ? "linear-gradient(180deg, rgba(30,10,10,0.99) 0%, rgba(20,8,8,1) 100%)"
              : "var(--surface)",
            borderRight: redTheme ? "1px solid rgba(192,57,43,0.3)" : "none",
            boxShadow: redTheme ? "4px 0 24px rgba(192,57,43,0.1)" : "none",
            zIndex: 51, display: "flex", flexDirection: "column",
          }}>
            <button onClick={() => setSidebarOpen(false)} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}><X size={24} /></button>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="main-content" style={{ flex: 1, minHeight: "100vh", minWidth: 0, overflowX: "hidden" }}>
        {/* Topbar */}
        <div className="glass" style={{
          borderBottom: "1px solid rgba(255,255,255,0.04)",
          padding: "14px 20px", display: "flex", alignItems: "center", gap: 12,
          position: "sticky", top: 0, zIndex: 10,
        }}>
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} style={{
            background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4,
          }}><Menu size={24} /></button>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: "var(--radius-sm)", background: "linear-gradient(135deg, var(--warning), #d97706)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#fff" }}>A</div>
          </div>
        </div>

        {/* Page content */}
        <div style={{ padding: 20 }}>

          {/* ===== DASHBOARD ===== */}
          {page === "dashboard" && (
            <DashboardPage
              filterPeriodo={filterPeriodo} setFilterPeriodo={setFilterPeriodo}
              totalOre={totalOre} avgOre={avgOre} uniqueDip={uniqueDip} uniqueCant={uniqueCant}
              last7={last7} max7={max7} oggi={oggi}
              topCant={topCant} allCant={allCant} maxCant={maxCant} barColors={barColors}
              missing={missing} filtered={filtered} oreData={oreData}
              dipendentiMerged={dipendentiAttivi}
              promemoria={promemoria} addPromemoria={addPromemoria} deletePromemoria={deletePromemoria}
              cantieri={cantieri} cantieriMeta={cantieriMeta} saveCantieriMeta={saveCantieriMeta}
              saveScadenza={saveScadenza} setPage={setPage}
              onOpenDipendente={(dipId) => { setOpenDipId(dipId); setPage("dipendenti"); }}
              schedaDipendenti={schedaDipendenti}
              saveSchedaDipendente={saveSchedaDipendente}
              visiteProgrammate={visiteProgrammate}
              saveVisiteProgrammate={saveVisiteProgrammate}
              formazioneProgrammate={formazioneProgrammate}
              saveFormazioneProgrammate={saveFormazioneProgrammate}
              automezzi={automezzi}
            />
          )}

          {/* ===== REGISTRO ORE ===== */}
          {page === "ore" && (
            <RegistroOrePage
              filtered={filtered} filterDip={filterDip} setFilterDip={setFilterDip}
              filterCantiere={filterCantiere} setFilterCantiere={setFilterCantiere}
              filterPeriodo={filterPeriodo} setFilterPeriodo={setFilterPeriodo}
              onExport={handleExport} cantieri={cantieri} dipendenti={dipendentiAttivi}
            />
          )}

          {/* ===== DIPENDENTI ===== */}
          {page === "dipendenti" && (
            <DipendentiPage oreData={oreData} oggi={oggi} dipendenti={dipendentiAttivi} dipendentiEliminati={dipendentiEliminati} setDipendenti={setDipendenti} saveScadenza={saveScadenza} saveNote={saveNote} dipendentiPins={dipendentiPins} saveDipendentiPins={saveDipendentiPins} promemoria={promemoria} addPromemoria={addPromemoria} deletePromemoria={deletePromemoria} schedaDipendenti={schedaDipendenti} saveSchedaDipendente={saveSchedaDipendente} openDipId={openDipId} clearOpenDipId={() => setOpenDipId(null)} visiteProgrammate={visiteProgrammate} saveVisiteProgrammate={saveVisiteProgrammate} formazioneProgrammate={formazioneProgrammate} saveFormazioneProgrammate={saveFormazioneProgrammate} />
          )}

          {/* ===== SCADENZE ===== */}
          {page === "scadenze" && (
            <ScadenzePage dipendenti={dipendentiAttivi} schedaDipendenti={schedaDipendenti} automezzi={automezzi} newKeys={scadenzeNewKeys} setPage={setPage} onOpenDipendente={(dipId) => { setOpenDipId(dipId); setPage("dipendenti"); }} onOpenAutomezzo={(autoId) => { setOpenAutoId(autoId); setPage("automezzi"); }} />
          )}

          {/* ===== CLASSI ===== */}
          {page === "classi" && (
            <ClassiPage
              dipendenti={dipendentiAttivi}
              schedaDipendenti={schedaDipendenti}
              saveSchedaDipendente={saveSchedaDipendente}
              visiteProgrammate={visiteProgrammate}
              saveVisiteProgrammate={saveVisiteProgrammate}
              formazioneProgrammate={formazioneProgrammate}
              saveFormazioneProgrammate={saveFormazioneProgrammate}
              {...classiHook}
            />
          )}

          {/* ===== AUTOMEZZI ===== */}
          {page === "automezzi" && (
            <AutomezziPage automezzi={automezzi} saveAutomezzo={saveAutomezzo} addAutomezzo={addAutomezzo} deleteAutomezzo={deleteAutomezzo} openAutoId={openAutoId} clearOpenAutoId={() => setOpenAutoId(null)} />
          )}

          {/* ===== CANTIERI ===== */}
          {page === "cantieri" && (
            <CantieriPage oreData={oreData} cantieri={cantieri} addCantiere={handleAddCantiere} deleteCantiere={handleDeleteCantiere} restoreCantiere={handleRestoreCantiere} archivedCantieri={archivedCantieri} actionHistory={actionHistory} undoAction={undoAction} dipendenti={dipendentiAttivi} oreOverride={oreOverride} cantieriMeta={cantieriMeta} saveCantieriMeta={saveCantieriMeta} schedaDipendenti={schedaDipendenti} />
          )}

          {/* ===== FILE EXCEL ORE ===== */}
          {page === "excelore" && (
            <FileExcelOrePage oreData={oreData} cantieri={cantieri} setOreData={setOreData} upsertOreEntry={upsertOreEntry} deleteOreEntry={deleteOreEntry} dipendenti={dipendentiMerged} saveNote={saveNote} setDipendenti={setDipendenti} />
          )}

          {/* ===== FILE EXCEL CANTIERI ===== */}
          {page === "excelcantieri" && (
            <FileExcelCantieriPage oreData={oreData} cantieri={cantieri} archivedCantieri={archivedCantieri} dipendenti={dipendentiMerged} oreOverride={oreOverride} setOreOverride={setOreOverride} commitOreOverride={commitOreOverride} cantieriMeta={cantieriMeta} saveCantieriMeta={saveCantieriMeta} schedaDipendenti={schedaDipendenti} />
          )}

          {/* ===== FILE EXCEL VISITE ===== */}
          {page === "excelvisite" && (
            <FileExcelVisitePage dipendenti={dipendentiAttivi} schedaDipendenti={schedaDipendenti} saveSchedaDipendente={saveSchedaDipendente} />
          )}


        </div>
      </div>
    </div>
  );
}

// ---- Calendario interattivo ----

function CalendarioInterattivo({ oggi, dipendentiMerged = [], promemoria = [], addPromemoria, deletePromemoria, cantieri = [], cantieriMeta = {}, setPage, onOpenDipendente, schedaDipendenti = {}, saveSchedaDipendente, visiteProgrammate = {}, saveVisiteProgrammate = () => {}, formazioneProgrammate = {}, saveFormazioneProgrammate = () => {} }) {
  const [calEvents, saveCalEvents] = useAppSettings("calendarEvents", {});
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [newEventText, setNewEventText] = useState("");
  const [newEventTime, setNewEventTime] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const [eventTimes, saveEventTimes] = useAppSettings("calendarEventTimes", {});

  const eventTypeColors = {
    scadenza: { color: "#ef4444", bg: "rgba(239,68,68,0.18)" },
    visita: { color: "#06b6d4", bg: "rgba(6,182,212,0.18)" },
    formazione: { color: "#f59e0b", bg: "rgba(245,158,11,0.18)" },
    cantiere: { color: "#f97316", bg: "rgba(249,115,22,0.18)" },
    custom: { color: "#3b82f6", bg: "rgba(59,130,246,0.18)" },
  };

  const toLocalKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const prevMonth = () => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => { const d = new Date(); setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1)); };

  // Costruisce mappa eventi per data
  const eventsByDate = useMemo(() => {
    const map = {};
    const add = (date, event) => {
      if (!date) return;
      const key = typeof date === "string" ? date.substring(0, 10) : date.toISOString().substring(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(event);
    };

    dipendentiMerged.forEach(d => {
      const s = schedaDipendenti[d.id];
      // Scadenza contratto da scheda dipendente
      if (s?.scad_contratto) {
        add(s.scad_contratto, { type: "scadenza", label: `Scad. ${d.name}`, dipId: d.id, field: "scad_contratto", color: "#ef4444", bg: "rgba(239,68,68,0.18)" });
      }
      // Visita medica programmata (appuntamento fissato dal datore)
      const vp = visiteProgrammate[d.id];
      if (vp?.data) {
        add(vp.data, { type: "visita", label: `🩺 ${d.name}`, dipId: d.id, time: vp.ora || null, color: "#06b6d4", bg: "rgba(6,182,212,0.18)" });
      }
      // Formazione programmata
      const fp = formazioneProgrammate[d.id];
      if (fp?.data) {
        add(fp.data, { type: "formazione", label: `📋 ${d.name}`, dipId: d.id, time: fp.ora || null, color: "#f59e0b", bg: "rgba(245,158,11,0.18)" });
      }
    });
    cantieri.forEach(nome => {
      const meta = cantieriMeta[nome];
      if (meta?.fine) add(meta.fine, { type: "cantiere", label: `🏗 ${nome.split(" - ")[0]}`, cantiere: nome, color: "#f97316", bg: "rgba(249,115,22,0.18)" });
    });
    Object.entries(calEvents || {}).forEach(([date, events]) => {
      (events || []).forEach(ev => {
        const cat = ev.categoria || "custom";
        const tc = eventTypeColors[cat] || eventTypeColors.custom;
        add(date, { ...ev, type: "custom", color: tc.color, bg: tc.bg });
      });
    });
    return map;
  }, [dipendentiMerged, cantieri, cantieriMeta, calEvents, eventTimes, schedaDipendenti, visiteProgrammate, formazioneProgrammate]);

  const days = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    const result = [];
    for (let i = 0; i < startPad; i++) result.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) result.push(new Date(year, month, d));
    return result;
  }, [currentMonth]);

  const selectedKey = selectedDay ? toLocalKey(selectedDay) : null;
  const selectedEvents = selectedKey ? (eventsByDate[selectedKey] || []) : [];

  const addCustomEvent = () => {
    if (!newEventText.trim() || !selectedKey) return;
    const existing = calEvents?.[selectedKey] || [];
    saveCalEvents({ ...calEvents, [selectedKey]: [...existing, { label: newEventText.trim(), id: Date.now(), time: newEventTime || null }] });
    setNewEventText("");
    setNewEventTime("");
  };
  const deleteCustomEvent = (date, evId) => {
    const existing = (calEvents?.[date] || []).filter(e => e.id !== evId);
    const next = { ...calEvents };
    if (existing.length === 0) delete next[date]; else next[date] = existing;
    saveCalEvents(next);
  };
  const deleteSchedaEvent = async (dipId, field) => {
    if (!saveSchedaDipendente) return;
    const updates = { [field]: null };
    if (field === "visita_medica") updates.visita_medica_ora = null;
    await saveSchedaDipendente(dipId, updates);
  };
  const setEventTime = (eventKey, time) => {
    saveEventTimes({ ...eventTimes, [eventKey]: time || null });
  };

  const meseLabel = currentMonth.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const weekDays = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const maxPills = fullscreen ? 4 : 2;

  const fs = fullscreen;
  const calBody = (
    <div style={{ background: "var(--surface)", borderRadius: fs ? 0 : "var(--radius-lg)", border: fs ? "none" : "1px solid var(--border)", display: "flex", flexDirection: "column", height: "100%", boxSizing: "border-box", overflow: "hidden" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: fs ? "20px 28px" : "16px 20px", background: "linear-gradient(135deg, rgba(192,57,43,0.08), rgba(99,102,241,0.05))", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(192,57,43,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Calendar size={18} color="#C0392B" />
          </div>
          <span style={{ color: "var(--text)", fontSize: fs ? 22 : 18, fontWeight: 800, textTransform: "capitalize" }}>{meseLabel}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button onClick={prevMonth} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, width: 34, height: 34, cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"} onMouseLeave={e => e.currentTarget.style.background = "var(--surface-light)"}><ChevronLeft size={16} /></button>
          <button onClick={goToday} style={{ background: "linear-gradient(135deg, #C0392B, #96281B)", border: "none", borderRadius: 8, padding: "0 16px", height: 34, cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "var(--font)", boxShadow: "0 2px 8px rgba(192,57,43,0.3)" }}>Oggi</button>
          <button onClick={nextMonth} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, width: 34, height: 34, cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"} onMouseLeave={e => e.currentTarget.style.background = "var(--surface-light)"}><ChevronRight size={16} /></button>
          <button onClick={() => setFullscreen(f => !f)} title={fs ? "Riduci" : "Espandi"} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, width: 34, height: 34, cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 6 }}>
            {fs ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ── Griglia ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Intestazioni giorni settimana */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "2px solid var(--border)", flexShrink: 0 }}>
            {weekDays.map((w, wi) => (
              <div key={w} style={{ padding: "12px 0", textAlign: "center", color: wi >= 5 ? "rgba(192,57,43,0.6)" : "var(--text-muted)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, borderRight: wi < 6 ? "1px solid var(--border)" : "none" }}>{w}</div>
            ))}
          </div>

          {/* Celle del mese */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridTemplateRows: `repeat(${Math.ceil(days.length / 7)}, 1fr)`, flex: 1, overflow: "hidden" }}>
            {days.map((day, i) => {
              const colIdx = i % 7;
              if (!day) return (
                <div key={`pad-${i}`} style={{ borderRight: colIdx < 6 ? "1px solid var(--border)" : "none", borderBottom: "1px solid var(--border)", background: "rgba(0,0,0,0.08)" }} />
              );
              const key = toLocalKey(day);
              const events = eventsByDate[key] || [];
              const isToday = key === oggi;
              const isSelected = key === selectedKey;
              const isWeekend = colIdx >= 5;
              const hidden = events.length - maxPills;
              return (
                <div
                  key={key}
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  style={{
                    borderRight: colIdx < 6 ? "1px solid var(--border)" : "none",
                    borderBottom: "1px solid var(--border)",
                    background: isSelected ? "rgba(99,102,241,0.12)" : isWeekend ? "rgba(0,0,0,0.04)" : "transparent",
                    cursor: "pointer", padding: fs ? "8px 8px 6px" : "6px 6px 4px", display: "flex", flexDirection: "column", gap: 4, overflow: "hidden",
                    transition: "background 0.15s, box-shadow 0.15s",
                    outline: isSelected ? "2px solid var(--primary)" : "none",
                    outlineOffset: -1,
                    boxShadow: isSelected ? "inset 0 0 12px rgba(99,102,241,0.1)" : "none",
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isWeekend ? "rgba(0,0,0,0.04)" : "transparent"; }}
                >
                  {/* Numero del giorno */}
                  <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: isToday ? 30 : "auto", height: isToday ? 30 : "auto",
                    borderRadius: isToday ? "50%" : 4,
                    background: isToday ? "linear-gradient(135deg, #C0392B, #96281B)" : "transparent",
                    color: isToday ? "#fff" : isSelected ? "var(--primary)" : isWeekend ? "rgba(192,57,43,0.5)" : "var(--text)",
                    fontSize: fs ? 15 : 13, fontWeight: isToday || isSelected ? 800 : 500,
                    alignSelf: "flex-start", lineHeight: 1, padding: isToday ? 0 : "2px 4px",
                    flexShrink: 0,
                    boxShadow: isToday ? "0 2px 8px rgba(192,57,43,0.4)" : "none",
                  }}>{day.getDate()}</span>

                  {/* Pill eventi */}
                  {events.slice(0, maxPills).map((ev, ei) => (
                    <div key={ei} style={{
                      background: ev.bg, borderLeft: `3px solid ${ev.color}`,
                      borderRadius: 5, padding: fs ? "5px 8px" : "4px 7px",
                      fontSize: fs ? 14 : 13, color: ev.color, fontWeight: 700,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      flexShrink: 0, lineHeight: 1.3,
                    }}>{ev.time ? `${ev.time} ` : ""}{ev.label}</div>
                  ))}
                  {hidden > 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-dim)", paddingLeft: 6, fontWeight: 600 }}>+{hidden} altri</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Pannello laterale giorno selezionato ── */}
        {selectedDay && (
          <div style={{ width: fs ? 380 : 300, borderLeft: "2px solid var(--border)", display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden", background: "rgba(0,0,0,0.03)" }}>
            <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "rgba(99,102,241,0.06)" }}>
              <p style={{ color: "var(--text)", fontSize: 18, fontWeight: 800, textTransform: "capitalize" }}>
                {selectedDay.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
              </p>
              <button onClick={() => setSelectedDay(null)} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "var(--text-dim)", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={16} /></button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
              {selectedEvents.length === 0 && <p style={{ color: "var(--text-dim)", fontSize: 16, fontStyle: "italic", padding: "30px 0", textAlign: "center" }}>Nessun evento</p>}
              {selectedEvents.map((ev, i) => (
                <div key={i} style={{ background: ev.bg, border: `1px solid ${ev.color}33`, borderLeft: `5px solid ${ev.color}`, borderRadius: 10, padding: "14px 16px", position: "relative" }}>
                  {/* X per eliminare */}
                  <button
                    onClick={() => {
                      if (ev.type === "custom") deleteCustomEvent(selectedKey, ev.id);
                      else if (ev.type === "visita" && ev.dipId && !ev.field) {
                        // Rimuovi visita programmata
                        const next = { ...visiteProgrammate }; delete next[ev.dipId]; saveVisiteProgrammate(next);
                      } else if (ev.type === "formazione" && ev.dipId) {
                        // Rimuovi formazione programmata
                        const next = { ...formazioneProgrammate }; delete next[ev.dipId]; saveFormazioneProgrammate(next);
                      } else if ((ev.type === "scadenza") && ev.dipId && ev.field) deleteSchedaEvent(ev.dipId, ev.field);
                    }}
                    style={{ position: "absolute", top: 8, right: 8, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, width: 26, height: 26, cursor: "pointer", color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, lineHeight: 1 }}
                    title="Elimina evento"
                  ><X size={14} /></button>
                  <p style={{ color: ev.color, fontSize: 16, fontWeight: 700, paddingRight: 30 }}>{ev.time ? `${ev.time} — ` : ""}{ev.label}</p>
                  {ev.time && (
                    <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
                      <Clock size={13} color="var(--text-muted)" style={{ verticalAlign: "middle", marginRight: 4 }} />
                      Orario: <span style={{ color: ev.color, fontWeight: 700 }}>{ev.time}</span>
                    </p>
                  )}
                  <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {(ev.type === "scadenza" || ev.type === "visita") && (
                      <button onClick={() => onOpenDipendente?.(ev.dipId)} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, cursor: "pointer", color: "var(--text-muted)", fontSize: 14, padding: "6px 14px", fontFamily: "var(--font)", fontWeight: 600 }}>→ Apri scheda</button>
                    )}
                    {ev.type === "cantiere" && (
                      <button onClick={() => setPage("cantieri")} style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 8, cursor: "pointer", color: "#10b981", fontSize: 14, padding: "6px 14px", fontFamily: "var(--font)", fontWeight: 600 }}>→ Cantieri</button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: "16px 18px", borderTop: "1px solid var(--border)", flexShrink: 0, background: "rgba(0,0,0,0.03)" }}>
              <p style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Nuovo evento</p>
              <input
                value={newEventText}
                onChange={e => setNewEventText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addCustomEvent()}
                placeholder="Scrivi un promemoria..."
                style={{ width: "100%", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 16px", color: "var(--text)", fontSize: 15, fontFamily: "var(--font)", boxSizing: "border-box", outline: "none", marginBottom: 8 }}
              />
              <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                <Clock size={16} color="var(--text-dim)" />
                <input
                  type="time"
                  value={newEventTime}
                  onChange={e => setNewEventTime(e.target.value)}
                  style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", color: "var(--text)", fontSize: 15, fontFamily: "var(--font)", boxSizing: "border-box", outline: "none", width: 120 }}
                />
                <span style={{ color: "var(--text-dim)", fontSize: 13 }}>facoltativo</span>
              </div>
              <button onClick={addCustomEvent} style={{ width: "100%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", border: "none", borderRadius: 10, padding: "12px 0", color: "#fff", fontSize: 15, cursor: "pointer", fontFamily: "var(--font)", fontWeight: 700, boxShadow: "0 4px 12px rgba(59,130,246,0.3)" }}>+ Aggiungi evento</button>
            </div>
          </div>
        )}
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 20, padding: "12px 22px", borderTop: "1px solid var(--border)", flexWrap: "wrap", flexShrink: 0, background: "rgba(0,0,0,0.03)" }}>
        {[["#ef4444","Scadenza"],["#06b6d4","Visita medica"],["#f59e0b","Formazione"],["#f97316","Fine cantiere"],["#3b82f6","Evento"]].map(([c,l]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: c, display: "inline-block", boxShadow: `0 0 6px ${c}44` }} />
            <span style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 600 }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return fullscreen ? (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "var(--bg)" }}>
      {calBody}
    </div>
  ) : calBody;
}

// ---- Sub-pages as components ----

function DashboardPage({ filterPeriodo, setFilterPeriodo, totalOre, avgOre, uniqueDip, uniqueCant, last7, max7, oggi, topCant, allCant, maxCant, barColors, missing, filtered, oreData = [], dipendentiMerged = [], promemoria = [], addPromemoria, deletePromemoria, cantieri = [], cantieriMeta = {}, saveCantieriMeta, saveScadenza, setPage, onOpenDipendente, schedaDipendenti = {}, saveSchedaDipendente, visiteProgrammate = {}, saveVisiteProgrammate = () => {}, formazioneProgrammate = {}, saveFormazioneProgrammate = () => {}, automezzi = [] }) {
  const [showAllCant, setShowAllCant] = useState(false);
  const [showAllMissing, setShowAllMissing] = useState(false);
  const [showAllRecent, setShowAllRecent] = useState(false);
  const recentOre = useMemo(() => [...oreData].sort((a, b) => (b.data > a.data ? 1 : b.data < a.data ? -1 : 0)), [oreData]);
  const cantToShow = showAllCant ? allCant : topCant;
  const maxCantAll = allCant.length ? allCant[0][1] : 1;
  const stats = [
    { l: "Dipendenti Attivi", v: dipendentiMerged.length, s: "", c: "#8b5cf6", g: "rgba(139,92,246,0.15)" },
    { l: "Cantieri Attivi", v: cantieri.length, s: "", c: "var(--warning)", g: "var(--warning-glow)" },
  ];

  // Top 5 scadenze urgenti per il widget dashboard
  const scadenzeUrgenti = useMemo(() => {
    const list = [];
    const today = new Date();
    const checkDip = (d, s, dateStr, label) => {
      if (!dateStr) return;
      const dt = new Date(dateStr);
      const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
      if (diff > 60) return;
      list.push({ nome: `${d.name.split(" ")[0]} ${d.name.split(" ")[1]?.[0] || ""}.`, label, data: dateStr, diff, scaduto: diff < 0 });
    };
    dipendentiMerged.forEach((d) => {
      const s = schedaDipendenti[d.id];
      if (!s) return;
      checkDip(d, s, s.scad_contratto, "Contratto");
      checkDip(d, s, s.visita_medica, "Visita medica");
      checkDip(d, s, s.form_gen, "Formazione");
      checkDip(d, s, s.antincendio, "Antincendio");
      checkDip(d, s, s.primo_soccorso, "Primo soccorso");
      checkDip(d, s, s.piattaforma, "Piattaforma");
      checkDip(d, s, s.rls, "RLS");
      checkDip(d, s, s.lav_quota, "Lav. in quota");
      checkDip(d, s, s.scad_pds, "Perm. soggiorno");
    });
    automezzi.forEach((a) => {
      const checkAuto = (dateStr, label) => {
        if (!dateStr) return;
        const dt = new Date(dateStr);
        const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
        if (diff > 60) return;
        list.push({ nome: `${a.modello}`, label, data: dateStr, diff, scaduto: diff < 0 });
      };
      checkAuto(a.scad_assicurazione, "Assicurazione");
      checkAuto(a.scad_revisione, "Revisione");
      checkAuto(a.scad_tagliando, "Tagliando");
    });
    list.sort((a, b) => a.diff - b.diff);
    return list;
  }, [dipendentiMerged, schedaDipendenti, automezzi]);

  // Top 5 per il widget card (sotto il calendario)
  const scadenzeTop5 = scadenzeUrgenti.slice(0, 5);

  // Design system — card stile premium
  const card = {
    background: "#141418",
    borderRadius: 24,
    padding: "28px",
    border: "none",
    boxShadow: "0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03)",
  };
  const subCard = {
    background: "rgba(255,255,255,0.03)",
    borderRadius: 16,
    padding: "14px 16px",
    border: "none",
  };
  const pill = (color, bg) => ({
    background: bg, color, borderRadius: 20, padding: "5px 14px",
    fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", display: "inline-block",
  });
  const headerRow = (title, action, actionLabel) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
      <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, letterSpacing: "-0.3px" }}>{title}</h3>
      {action && (
        <button onClick={action} style={{ background: "none", border: "none", color: "#C0392B", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", letterSpacing: "0.2px" }}>
          {actionLabel} ›
        </button>
      )}
    </div>
  );

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 36, overflow: "hidden" }}>
        <h1 style={{ color: "#fff", fontSize: 38, fontWeight: 800, letterSpacing: "-1.2px", flexShrink: 0 }}>Dashboard</h1>

        {/* Ticker scadenze scorrevole */}
        {scadenzeUrgenti.length > 0 && (
          <div
            onClick={() => setPage("scadenze")}
            style={{
              flex: 1, overflow: "hidden", cursor: "pointer",
              background: "rgba(239,68,68,0.06)",
              borderRadius: 14, padding: "10px 0",
              border: "1px solid rgba(239,68,68,0.12)",
              position: "relative",
            }}
          >
            {/* Fade edges */}
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 40, background: "linear-gradient(90deg, #141418, transparent)", zIndex: 2, borderRadius: "14px 0 0 14px" }} />
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 40, background: "linear-gradient(90deg, transparent, #141418)", zIndex: 2, borderRadius: "0 14px 14px 0" }} />

            <div style={{
              display: "flex", gap: 0, whiteSpace: "nowrap",
              animation: `tickerScroll ${Math.max(scadenzeUrgenti.length * 4, 15)}s linear infinite`,
              width: "fit-content",
            }}>
              {/* Duplico il contenuto per loop infinito */}
              {[...scadenzeUrgenti, ...scadenzeUrgenti].map((sc, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "0 28px" }}>
                  <span style={{
                    background: sc.scaduto ? "rgba(239,68,68,0.2)" : sc.diff <= 15 ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
                    color: sc.scaduto ? "#ef4444" : sc.diff <= 15 ? "#ef4444" : "#f59e0b",
                    borderRadius: 10, padding: "4px 12px", fontSize: 13, fontWeight: 800,
                  }}>
                    {sc.scaduto ? "SCADUTA" : sc.diff + "gg"}
                  </span>
                  <span style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>
                    {sc.nome}
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, fontWeight: 500 }}>
                    — {sc.label}
                  </span>
                  <span style={{ color: "rgba(255,255,255,0.15)", padding: "0 6px", fontSize: 16 }}>|</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Layout principale — 3 colonne: calendario | centro | destra */}
      <div className="dashboard-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 24, alignItems: "start" }}>

        {/* Colonna sinistra: Calendario */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <CalendarioInterattivo
            oggi={oggi}
            dipendentiMerged={dipendentiMerged}
            promemoria={promemoria}
            addPromemoria={addPromemoria}
            deletePromemoria={deletePromemoria}
            cantieri={cantieri}
            cantieriMeta={cantieriMeta}
            saveCantieriMeta={saveCantieriMeta}
            saveScadenza={saveScadenza}
            setPage={setPage}
            onOpenDipendente={onOpenDipendente}
            schedaDipendenti={schedaDipendenti}
            saveSchedaDipendente={saveSchedaDipendente}
            visiteProgrammate={visiteProgrammate}
            saveVisiteProgrammate={saveVisiteProgrammate}
            formazioneProgrammate={formazioneProgrammate}
            saveFormazioneProgrammate={saveFormazioneProgrammate}
          />
        </div>

        {/* Colonna destra: stat + scadenze + cantieri */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Stat cards — grandi, galleggianti */}
          <div className="dashboard-stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { l: "Dipendenti attivi", v: dipendentiMerged.length, accent: "#C0392B", glow: "rgba(192,57,43,0.08)" },
              { l: "Cantieri attivi", v: cantieri.length, accent: "#f59e0b", glow: "rgba(245,158,11,0.08)" },
            ].map((s, i) => (
              <div key={i} style={{ ...card, padding: "28px 24px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -50, right: -50, width: 140, height: 140, borderRadius: "50%", background: s.glow, filter: "blur(50px)" }} />
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "2px", marginBottom: 16 }}>{s.l}</p>
                <p style={{ color: "#fff", fontSize: 52, fontWeight: 800, letterSpacing: "-3px", lineHeight: 1 }}>{s.v}</p>
              </div>
            ))}
          </div>

          {/* Scadenze Urgenti */}
          {scadenzeTop5.length > 0 && (
            <div style={card}>
              {headerRow("Scadenze Urgenti", () => setPage("scadenze"), "Vedi tutte")}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {scadenzeTop5.map((s, i) => (
                  <div key={i} style={{ ...subCard, display: "flex", alignItems: "center", gap: 14 }}>
                    <span style={pill(
                      s.scaduto ? "#ef4444" : s.diff <= 15 ? "#ef4444" : "#f59e0b",
                      s.scaduto ? "rgba(239,68,68,0.12)" : s.diff <= 15 ? "rgba(239,68,68,0.10)" : "rgba(245,158,11,0.10)"
                    )}>
                      {s.scaduto ? "Scaduta" : `${s.diff}gg`}
                    </span>
                    <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.nome} — {s.label}
                    </p>
                    <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 12, whiteSpace: "nowrap" }}>
                      {new Date(s.data).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ore per Cantiere */}
          <div style={card}>
            {headerRow("Ore per Cantiere", () => setPage("cantieri"), `Tutti (${allCant.length})`)}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {topCant.slice(0, 6).map(([n, h], i) => {
                const colorIdx = i % barColors.length;
                return (
                  <div key={n}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                      <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, fontWeight: 500 }}>{n.split(" - ")[0]}</span>
                      <span style={{ color: "#fff", fontSize: 15, fontWeight: 800 }}>{h}h</span>
                    </div>
                    <div style={{ height: 10, borderRadius: 5, background: "rgba(255,255,255,0.04)" }}>
                      <div style={{
                        height: "100%", borderRadius: 5,
                        width: `${(h / maxCantAll) * 100}%`,
                        background: `linear-gradient(90deg, ${barColors[colorIdx][0]}, ${barColors[colorIdx][1]})`,
                        transition: "width 1s cubic-bezier(0.16, 1, 0.3, 1)",
                        boxShadow: `0 0 16px ${barColors[colorIdx][0]}33`,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mancanti + Ultime — sotto, indipendenti */}
      <div className="dashboard-bottom-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 28, alignItems: "start" }}>

        {/* Mancanti oggi */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>Mancanti oggi</h3>
              <span style={pill("#fff", missing.length > 0 ? "#ef4444" : "#10b981")}>{missing.length}</span>
            </div>
            {missing.length > 4 && (
              <button onClick={() => setShowAllMissing((v) => !v)} style={{ background: "rgba(255,255,255,0.04)", border: "none", borderRadius: 20, padding: "6px 16px", color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }}>
                {showAllMissing ? "Meno" : `Tutti (${missing.length})`}
              </button>
            )}
          </div>
          {missing.length === 0 ? (
            <div style={{ ...subCard, textAlign: "center", padding: 24 }}>
              <p style={{ color: "#10b981", fontSize: 14, fontWeight: 600 }}>Tutti hanno inserito le ore!</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(showAllMissing ? missing : missing.slice(0, 4)).map((d) => (
                <div key={d.id} style={{ ...subCard, display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "rgba(255,255,255,0.4)", flexShrink: 0 }}>{d.avatar}</div>
                  <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: 500 }}>{d.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ultime Registrazioni */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
            <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>Ultime Registrazioni</h3>
            {recentOre.length > 4 && (
              <button onClick={() => setShowAllRecent((v) => !v)} style={{ background: "rgba(255,255,255,0.04)", border: "none", borderRadius: 20, padding: "6px 16px", color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }}>
                {showAllRecent ? "Meno" : `Tutti (${recentOre.length})`}
              </button>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(showAllRecent ? recentOre : recentOre.slice(0, 4)).map((o) => (
              <div key={o.id} style={{ ...subCard, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(192,57,43,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, color: "#C0392B", flexShrink: 0 }}>{getInitials(o.dipendente)}</div>
                  <div>
                    <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, fontWeight: 600 }}>{o.dipendente}</p>
                    <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 12 }}>{o.cantiere.split(" - ")[0]}</p>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ color: "#C0392B", fontSize: 18, fontWeight: 800 }}>{o.ore}h</p>
                  <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 11 }}>{formatDate(o.data, { day: "numeric", month: "short" })}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function RegistroOrePage({ filtered, filterDip, setFilterDip, filterCantiere, setFilterCantiere, filterPeriodo, setFilterPeriodo, onExport, cantieri, dipendenti = [] }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>Registro Ore</h1>
        <button onClick={onExport} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "10px 18px",
          borderRadius: "var(--radius-md)", border: "none",
          background: "linear-gradient(135deg, var(--accent), #059669)",
          color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
          boxShadow: "0 4px 16px var(--accent-glow)", fontFamily: "var(--font)",
        }}><Download size={18} /> Esporta Excel</button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", background: "var(--surface)", padding: 16, borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }}>
        <select value={filterDip} onChange={(e) => setFilterDip(e.target.value)} style={filterSelectStyle}>
          <option value="">Tutti i dipendenti</option>
          {dipendenti.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        <select value={filterCantiere} onChange={(e) => setFilterCantiere(e.target.value)} style={filterSelectStyle}>
          <option value="">Tutti i cantieri</option>
          {cantieri.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterPeriodo} onChange={(e) => setFilterPeriodo(e.target.value)} style={filterSelectStyle}>
          <option value="oggi">Oggi</option>
          <option value="settimana">Settimana</option>
          <option value="mese">Mese</option>
          <option value="tutto">Tutto</option>
        </select>
      </div>

      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                {["Data", "Dipendente", "Cantiere", "Ore", "Note"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "14px 16px", color: "var(--text-muted)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 50).map((o, i) => (
                <tr key={o.id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "rgba(26,34,53,0.3)" }}>
                  <td style={{ padding: "12px 16px", color: "var(--text)", fontSize: 14, whiteSpace: "nowrap" }}>{formatDate(o.data)}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: "var(--primary-glow)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 10, color: "var(--primary)" }}>{getInitials(o.dipendente)}</div>
                      <span style={{ color: "var(--text)", fontSize: 14, fontWeight: 500 }}>{o.dipendente}</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", color: "var(--text-muted)", fontSize: 13 }}>{o.cantiere}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: "var(--primary-glow)", color: "var(--primary)", padding: "4px 10px", borderRadius: 6, fontWeight: 700, fontSize: 14 }}>{o.ore}h</span>
                  </td>
                  <td style={{ padding: "12px 16px", color: o.note ? "var(--warning)" : "var(--text-dim)", fontSize: 13 }}>{o.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 50 && (
          <div style={{ padding: 14, textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>
            Mostrati 50 di {filtered.length} registrazioni
          </div>
        )}
      </div>
    </>
  );
}

function DipendentiPage({ oreData, oggi, dipendenti, dipendentiEliminati = [], setDipendenti, saveScadenza, saveNote, dipendentiPins = {}, saveDipendentiPins = () => {}, promemoria = [], addPromemoria = () => {}, deletePromemoria = () => {}, schedaDipendenti = {}, saveSchedaDipendente = () => {}, openDipId = null, clearOpenDipId = () => {}, visiteProgrammate = {}, saveVisiteProgrammate = () => {}, formazioneProgrammate = {}, saveFormazioneProgrammate = () => {} }) {
  const [selectedDip, setSelectedDip] = useState(null);
  const [editScadenza, setEditScadenza] = useState("");
  const [editPin, setEditPin] = useState("");
  const [searchDip, setSearchDip] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContratto, setNewContratto] = useState("INDET.");
  const [newPromTesto, setNewPromTesto] = useState("");
  const [newPromData, setNewPromData] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const [confirmDeleteDip, setConfirmDeleteDip] = useState(null);
  const [archivedDip, saveArchivedDip] = useAppSettings("archivedDipendenti", () => {
    try { return JSON.parse(localStorage.getItem("archivedDipendenti") || "[]"); } catch { return []; }
  });
  const setArchivedDip = (updater) => {
    const next = typeof updater === "function" ? updater(archivedDip) : updater;
    saveArchivedDip(next);
  };
  const [dipActionHistory, saveDipActionHistory] = useAppSettings("dipActionHistory", () => {
    try { return JSON.parse(localStorage.getItem("dipActionHistory") || "[]"); } catch { return []; }
  });
  const setDipActionHistory = (updater) => {
    const next = typeof updater === "function" ? updater(dipActionHistory) : updater;
    saveDipActionHistory(next);
  };
  const [showDipCronologia, setShowDipCronologia] = useState(false);

  // Auto-open dipendente from Scadenze navigation
  useEffect(() => {
    if (openDipId) {
      const d = dipendenti.find((dip) => dip.id === openDipId);
      if (d) openModal(d);
      clearOpenDipId();
    }
  }, [openDipId]);

  function openModal(d) {
    setSelectedDip(d);
    setEditScadenza(d.scadenza || "");
    setEditPin(dipendentiPins[d.id] || d.pin || "0000");
    setEditingName(false);
    setEditNameValue(d.name);
  }
  function closeModal() { setSelectedDip(null); }

  function saveDipName(dipId, newName) {
    const avatar = newName.split(" ").map(p => p[0]).join("").substring(0, 2).toUpperCase();
    setDipendenti((prev) => prev.map((dp) => dp.id === dipId ? { ...dp, name: newName, avatar } : dp));
    setSelectedDip((prev) => prev ? { ...prev, name: newName, avatar } : prev);
    setEditNameValue(newName);
    // Persisti su Supabase
    const currentOverrides = JSON.parse(localStorage.getItem("dipendentiNomiOverride") || "{}");
    const updated = { ...currentOverrides, [dipId]: newName };
    saveNomiOverride(updated);
  }

  function handleAddDipendente() {
    if (!newName.trim()) return;
    const parts = newName.trim().split(" ");
    const avatar = parts.map((p) => p[0]).join("").substring(0, 2).toUpperCase();
    const newId = Math.max(0, ...dipendenti.map((d) => d.id)) + 1;
    const today = new Date().toISOString().slice(0, 10);
    const newDip = { id: newId, name: newName.trim(), pin: "0000", avatar, contratto: newContratto, scadenza: null, note: "", creatoIl: today };
    setDipendenti((prev) => [...prev, newDip]);
    setShowAddModal(false);
    setNewName(""); setNewContratto("INDET.");
  }

  function handleDeleteDipendente(dip) {
    const today = new Date().toISOString().slice(0, 10);
    setDipendenti((prev) => prev.map((d) => d.id === dip.id ? { ...d, eliminatoIl: today } : d));
    const action = { id: Date.now(), type: "delete_dipendente", label: `Eliminato: ${dip.name}`, dipData: dip, timestamp: Date.now() };
    setDipActionHistory((prev) => [action, ...prev]);
    setConfirmDeleteDip(null);
    if (selectedDip?.id === dip.id) closeModal();
  }

  function handleRestoreDipendente(dip) {
    const { archivedAt, eliminatoIl, ...cleaned } = dip;
    // Se era nell'archivio locale, rimuovilo
    setArchivedDip((prev) => prev.filter((d) => !(d.id === dip.id && d.archivedAt === dip.archivedAt)));
    // Rimuovi eliminatoIl dalla lista Supabase
    setDipendenti((prev) => prev.map((d) => {
      if (d.id === dip.id) {
        const { eliminatoIl: _, ...rest } = d;
        return rest;
      }
      return d;
    }));
    const action = { id: Date.now(), type: "restore_dipendente", label: `Ripristinato: ${dip.name}`, dipData: cleaned, timestamp: Date.now() };
    setDipActionHistory((prev) => [action, ...prev]);
  }

  function handleSaveScadenza() {
    const val = editScadenza || null;
    setDipendenti((prev) => prev.map((d) => d.id === selectedDip.id ? { ...d, scadenza: val } : d));
    setSelectedDip((prev) => ({ ...prev, scadenza: val }));
    saveScadenza(selectedDip.id, val);
  }

  async function handleAddPromemoria() {
    if (!newPromTesto.trim() || !newPromData || !selectedDip) return;
    await addPromemoria({ dipendente_id: selectedDip.id, testo: newPromTesto.trim(), data_scadenza: newPromData });
    setNewPromTesto("");
    setNewPromData("");
  }

  function formatScadenza(s) {
    if (!s) return "INDET.";
    if (s.startsWith("INDET.")) return s;
    const dt = new Date(s);
    if (isNaN(dt)) return s;
    return dt.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  }

  function scadenzaColor(s) {
    if (!s || s.startsWith("INDET.")) return "var(--accent)";
    const dt = new Date(s);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    const diff = Math.floor((dt - oggi) / (1000 * 60 * 60 * 24));
    if (diff <= 30) return "var(--danger)";
    if (diff <= 60) return "#f59e0b";
    return "var(--accent)";
  }

  // Ore per mese per il dipendente selezionato
  function orePerMese(dOre) {
    const map = {};
    dOre.forEach((o) => {
      if (!o.ore) return;
      const key = o.data.substring(0, 7); // "2026-02"
      map[key] = (map[key] || 0) + o.ore;
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }

  // Ore per cantiere per il dipendente selezionato
  function orePerCantiere(dOre) {
    const map = {};
    dOre.forEach((o) => {
      if (!o.ore || !o.cantiere) return;
      map[o.cantiere] = (map[o.cantiere] || 0) + o.ore;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }

  const nomiMesi = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>
          Dipendenti <span style={{ color: "var(--primary)", fontSize: 18, fontWeight: 600 }}>({dipendenti.length})</span>
        </h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", pointerEvents: "none" }} />
            <input
              value={searchDip}
              onChange={(e) => setSearchDip(e.target.value)}
              placeholder="Cerca dipendente..."
              style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none", width: 200 }}
            />
          </div>
          <button onClick={() => setShowAddModal(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: "var(--radius-md)", border: "none", background: "linear-gradient(135deg, #C0392B, #96281B)", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "var(--font)", boxShadow: "0 4px 16px rgba(192,57,43,0.35)" }}>
            <Plus size={16} /> Aggiungi dipendente
          </button>
        </div>
      </div>
      {searchDip && (
        <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 14 }}>
          {dipendenti.filter(d => d.name.toLowerCase().includes(searchDip.toLowerCase())).length} risultat{dipendenti.filter(d => d.name.toLowerCase().includes(searchDip.toLowerCase())).length === 1 ? "o" : "i"} per "<span style={{ color: "var(--text)" }}>{searchDip}</span>"
        </p>
      )}
      <div className="admin-dip-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 18 }}>
        {(searchDip ? dipendenti.filter(d => d.name.toLowerCase().includes(searchDip.toLowerCase())) : dipendenti).map((d) => {
          const dOre = oreData.filter((o) => o.dipendenteId === d.id);
          const has = dOre.some((o) => o.data === oggi);
          const avatarIdx = (d.id - 1) % AVATAR_COLORS.length;
          return (
            <div key={d.id} onClick={() => openModal(d)} style={{
                background: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: 24,
                border: d.note ? "1px solid #f59e0b" : "1px solid var(--border)",
                cursor: "pointer",
                transition: "border-color 0.15s, box-shadow 0.15s",
                boxShadow: d.note ? "0 0 14px rgba(245,158,11,0.35), 0 0 4px rgba(245,158,11,0.2)" : "none",
                position: "relative",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = d.note ? "#fbbf24" : "var(--primary)"; e.currentTarget.style.boxShadow = d.note ? "0 0 22px rgba(245,158,11,0.6), 0 0 8px rgba(245,158,11,0.3)" : "0 0 0 2px rgba(99,102,241,0.2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = d.note ? "#f59e0b" : "var(--border)"; e.currentTarget.style.boxShadow = d.note ? "0 0 14px rgba(245,158,11,0.35), 0 0 4px rgba(245,158,11,0.2)" : "none"; }}>
              <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6, alignItems: "center" }}>
                {d.note && (
                  <div style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: 0.5, textTransform: "uppercase", boxShadow: "0 0 8px rgba(245,158,11,0.5)" }}>
                    NOTE
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteDip(d); }}
                  style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, padding: "3px 6px", cursor: "pointer", color: "var(--danger)", display: "flex", alignItems: "center", justifyContent: "center" }}
                  title="Elimina dipendente"
                ><X size={13} /></button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                <div style={{
                  width: 54, height: 54, borderRadius: "var(--radius-md)",
                  background: `linear-gradient(135deg, ${AVATAR_COLORS[avatarIdx][0]}, ${AVATAR_COLORS[avatarIdx][1]})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 18, color: "#fff", flexShrink: 0,
                }}>{d.avatar}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: "var(--text)", fontSize: 17, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}{d.ruolo ? ` — ${d.ruolo}` : ""}</p>
                  <p style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 3 }}>{d.contratto} · Scad: <span style={{ color: scadenzaColor(d.scadenza), fontWeight: 700 }}>{formatScadenza(d.scadenza)}</span></p>
                </div>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: scadenzaColor(d.scadenza), flexShrink: 0 }} />
              </div>
              <div style={{ background: "var(--surface-light)", borderRadius: "var(--radius-sm)", padding: "12px 14px", minHeight: 48 }}>
                <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Note</p>
                <p style={{ color: d.note ? "var(--text)" : "var(--text-dim)", fontSize: 13, fontStyle: d.note ? "normal" : "italic" }}>
                  {d.note || "Nessuna nota"}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== MODAL DETTAGLIO DIPENDENTE ===== */}
      {selectedDip && (() => {
        const d = selectedDip;
        const dOre = oreData.filter((o) => o.dipendenteId === d.id);
        const mesi = orePerMese(dOre);
        const cantieri = orePerCantiere(dOre);
        const maxCantOre = cantieri.length > 0 ? cantieri[0][1] : 1;
        return (
          <div onClick={closeModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 20, width: "100%", maxWidth: 860, maxHeight: "90vh", overflowY: "auto", border: "1px solid var(--border)", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
              {/* Header */}
              <div style={{ padding: "28px 32px 22px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 18 }}>
                <div style={{ width: 66, height: 66, borderRadius: 16, background: `linear-gradient(135deg, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][0]}, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][1]})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22, color: "#fff", flexShrink: 0 }}>{d.avatar}</div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>{d.name}</p>
                  <p style={{ color: "var(--text-dim)", fontSize: 15 }}>{d.contratto}{d.ruolo ? ` · ${d.ruolo}` : ""} · ID #{d.id}</p>
                </div>
                <button onClick={closeModal} style={{ background: "var(--surface-light)", border: "none", borderRadius: 8, width: 40, height: 40, cursor: "pointer", color: "var(--text-muted)", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              </div>

              <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 26 }}>
                {/* Dati anagrafici */}
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, fontWeight: 700 }}>Dati anagrafici</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {/* Nome editabile — sempre input visibile */}
                    <div style={{ background: "var(--surface-light)", borderRadius: 10, padding: "12px 16px", gridColumn: "1 / -1" }}>
                      <p style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", marginBottom: 5 }}>Nome completo</p>
                      <input
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editNameValue.trim()) {
                            saveDipName(d.id, editNameValue.trim());
                            e.target.blur();
                          }
                        }}
                        onBlur={() => {
                          if (editNameValue.trim() && editNameValue.trim() !== d.name) {
                            saveDipName(d.id, editNameValue.trim());
                          }
                        }}
                        style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", color: "var(--text)", fontSize: 16, fontWeight: 600, outline: "none", width: "100%", fontFamily: "var(--font)", boxSizing: "border-box" }}
                      />
                    </div>
                    {[
                      { label: "ID", value: d.id },
                      { label: "Contratto", value: d.contratto },
                      { label: "Ruolo", value: d.ruolo || "Operaio" },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ background: "var(--surface-light)", borderRadius: 10, padding: "12px 16px" }}>
                        <p style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", marginBottom: 5 }}>{label}</p>
                        <p style={{ color: "var(--text)", fontSize: 16, fontWeight: 600 }}>{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scheda dipendente completa */}
                <SchedaDipendenteSection dipId={d.id} scheda={schedaDipendenti[d.id] || {}} saveSchedaDipendente={saveSchedaDipendente} visiteProgrammate={visiteProgrammate} saveVisiteProgrammate={saveVisiteProgrammate} formazioneProgrammate={formazioneProgrammate} saveFormazioneProgrammate={saveFormazioneProgrammate} />

                {/* Ore per mese */}
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, fontWeight: 700 }}>Ore totali per mese</p>
                  {mesi.length === 0 ? (
                    <p style={{ color: "var(--text-dim)", fontSize: 15 }}>Nessun dato disponibile</p>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10 }}>
                      {mesi.map(([key, ore]) => {
                        const [y, m] = key.split("-");
                        return (
                          <div key={key} style={{ background: "var(--surface-light)", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                            <p style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>{nomiMesi[parseInt(m) - 1]} {y}</p>
                            <p style={{ color: "var(--primary)", fontSize: 26, fontWeight: 700, marginTop: 4 }}>{ore}h</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Note */}
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, fontWeight: 700 }}>Note</p>
                  <div style={{ background: "var(--surface-light)", borderRadius: 10, padding: "16px 18px", display: "flex", gap: 10 }}>
                    <textarea
                      value={d.note || ""}
                      onChange={(e) => setSelectedDip((prev) => ({ ...prev, note: e.target.value }))}
                      placeholder="Aggiungi una nota sul dipendente..."
                      rows={3}
                      style={{ flex: 1, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", color: "var(--text)", fontSize: 15, outline: "none", resize: "vertical", fontFamily: "var(--font)" }}
                    />
                    <button onClick={() => { saveNote(d.id, d.note || ""); setDipendenti((prev) => prev.map((dp) => dp.id === d.id ? { ...dp, note: d.note || "" } : dp)); }} style={{ background: "var(--primary)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 14, fontWeight: 600, alignSelf: "flex-start" }}>Salva</button>
                  </div>
                </div>

                {/* Ore per cantiere */}
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, fontWeight: 700 }}>Ore per cantiere</p>
                  {cantieri.length === 0 ? (
                    <p style={{ color: "var(--text-dim)", fontSize: 15 }}>Nessun dato disponibile</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {cantieri.map(([cant, ore], i) => (
                        <div key={cant}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                            <span style={{ color: "var(--text)", fontSize: 14 }}>{cant}</span>
                            <span style={{ color: "var(--text-dim)", fontSize: 14, fontWeight: 700 }}>{ore}h</span>
                          </div>
                          <div style={{ background: "var(--surface-light)", borderRadius: 4, height: 7 }}>
                            <div style={{ height: 7, borderRadius: 4, width: `${(ore / maxCantOre) * 100}%`, background: `linear-gradient(90deg, ${AVATAR_COLORS[i % AVATAR_COLORS.length][0]}, ${AVATAR_COLORS[i % AVATAR_COLORS.length][1]})` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Link personale dipendente */}
              <div style={{ padding: "20px 32px 28px", borderTop: "1px solid var(--border)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={(e) => { const btn = e.currentTarget; const url = `${window.location.origin}/d/${d.id}`; navigator.clipboard.writeText(url).then(() => { btn.textContent = "✅ Copiato!"; btn.style.background = "rgba(16,185,129,0.2)"; btn.style.borderColor = "rgba(16,185,129,0.5)"; btn.style.color = "#10b981"; setTimeout(() => { btn.innerHTML = "📋 Copia Link"; btn.style.background = "rgba(96,165,250,0.1)"; btn.style.borderColor = "rgba(96,165,250,0.3)"; btn.style.color = "#60a5fa"; }, 2500); }); }}
                  style={{ flex: 1, padding: "12px 16px", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.3)", borderRadius: 10, color: "#60a5fa", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font)" }}
                >📋 Copia Link</button>
                <button
                  onClick={() => { const url = `${window.location.origin}/d/${d.id}`; window.open(`https://wa.me/?text=${encodeURIComponent(`Ciao ${d.name}! Ecco il tuo link per registrare le ore: ${url}`)}`, "_blank"); }}
                  style={{ flex: 1, padding: "12px 16px", background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: 10, color: "#25D366", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font)" }}
                >💬 WhatsApp</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== MODAL AGGIUNGI DIPENDENTE ===== */}
      {showAddModal && (
        <div onClick={() => setShowAddModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 20, width: "100%", maxWidth: 420, border: "1px solid var(--border)", boxShadow: "0 24px 80px rgba(0,0,0,0.5)", padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <p style={{ color: "var(--text)", fontSize: 18, fontWeight: 700 }}>Nuovo dipendente</p>
              <button onClick={() => setShowAddModal(false)} style={{ background: "var(--surface-light)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "var(--text-muted)", fontSize: 16 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", marginBottom: 6 }}>Nome completo</p>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Es: Mario Rossi"
                  style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
              </div>
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", marginBottom: 6 }}>Tipo contratto</p>
                <select value={newContratto} onChange={(e) => setNewContratto(e.target.value)} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", color: "var(--text)", fontSize: 14, outline: "none", fontFamily: "var(--font)" }}>
                  <option value="INDET.">INDET.</option>
                  <option value="DET.">DET.</option>
                </select>
              </div>
              <button onClick={handleAddDipendente} style={{ marginTop: 8, background: "linear-gradient(135deg, var(--primary), var(--accent))", color: "#fff", border: "none", borderRadius: 10, padding: "12px", cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: "var(--font)" }}>Aggiungi</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DIPENDENTI ELIMINATI ===== */}
      {dipendentiEliminati.length > 0 && (
        <div style={{ marginTop: 28, background: "var(--surface)", borderRadius: "var(--radius-md)", border: "1px solid rgba(239,68,68,0.2)", padding: 20 }}>
          <p style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            Dipendenti Eliminati
            <span style={{ background: "rgba(239,68,68,0.15)", color: "var(--danger)", borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{dipendentiEliminati.length}</span>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {dipendentiEliminati.map((d) => (
              <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "rgba(239,68,68,0.07)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(239,68,68,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][0]}, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][1]})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, color: "#fff" }}>{d.avatar}</div>
                  <div>
                    <span style={{ color: "var(--text-muted)", fontSize: 14, textDecoration: "line-through" }}>{d.name}</span>
                    <p style={{ color: "var(--text-dim)", fontSize: 11 }}>{d.contratto} · Eliminato il {new Date(d.eliminatoIl).toLocaleDateString("it-IT")}</p>
                  </div>
                </div>
                <button onClick={() => handleRestoreDipendente(d)} style={{ padding: "7px 16px", borderRadius: "var(--radius-sm)", border: "1px solid var(--accent)", background: "transparent", color: "var(--accent)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 6 }}>Ripristina</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== CRONOLOGIA OPERAZIONI DIPENDENTI ===== */}
      <div style={{ marginTop: 20 }}>
        <button onClick={() => setShowDipCronologia((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", color: "var(--text-muted)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", width: "100%" }}>
          📋 Cronologia operazioni
          <span style={{ marginLeft: "auto", fontSize: 12 }}>{showDipCronologia ? "▲ Chiudi" : `▼ Mostra (${dipActionHistory.length})`}</span>
        </button>
        {showDipCronologia && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 var(--radius-md) var(--radius-md)", overflow: "hidden" }}>
            {dipActionHistory.length === 0 ? (
              <p style={{ color: "var(--text-dim)", fontSize: 13, padding: "20px", textAlign: "center" }}>Nessuna operazione registrata.</p>
            ) : (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {dipActionHistory.map((a) => {
                  const icons = { add_dipendente: "➕", delete_dipendente: "🗑", restore_dipendente: "↩" };
                  const ts = new Date(a.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                  return (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>{icons[a.type] || "•"}</span>
                        <div>
                          <p style={{ color: "var(--text)", fontSize: 13, fontWeight: 500 }}>{a.label}</p>
                          <p style={{ color: "var(--text-dim)", fontSize: 11, marginTop: 2 }}>{ts}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== MODAL CONFERMA ELIMINAZIONE DIPENDENTE ===== */}
      {confirmDeleteDip && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setConfirmDeleteDip(null)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
          <div style={{ position: "relative", background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 400, border: "1px solid var(--border)", zIndex: 1101, margin: "0 16px" }}>
            <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Elimina dipendente</h2>
            <p style={{ color: "var(--text-dim)", fontSize: 14, marginBottom: 8 }}>
              Sei sicuro di voler eliminare <strong style={{ color: "var(--text)" }}>{confirmDeleteDip.name}</strong>?
            </p>
            <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 24 }}>
              Le ore già registrate non verranno cancellate. Potrai ripristinarlo dalla sezione "Dipendenti Archiviati".
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmDeleteDip(null)} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)" }}>Annulla</button>
              <button onClick={() => handleDeleteDipendente(confirmDeleteDip)} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "none", background: "var(--danger)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font)" }}>Elimina</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CantieriPage({ oreData, cantieri, addCantiere, deleteCantiere, restoreCantiere = () => {}, archivedCantieri = [], actionHistory = [], undoAction = () => {}, dipendenti, oreOverride = {}, cantieriMeta = {}, saveCantieriMeta = () => {}, schedaDipendenti = {} }) {
  const [showModal, setShowModal] = useState(false);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [codice, setCodice] = useState("");
  const [dataInizio, setDataInizio] = useState("");
  const [dataFine, setDataFine] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showCronologia, setShowCronologia] = useState(false);
  const [searchCantiere, setSearchCantiere] = useState("");

  // Chiudi menu cliccando fuori
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (!e.target.closest("[data-menu-container]")) setMenuOpen(null);
    };
    document.addEventListener("click", close, true);
    return () => document.removeEventListener("click", close, true);
  }, [menuOpen]);
  const isDateSabato = (dateStr) => new Date(dateStr).getDay() === 6;

  // Ore da Supabase per un dipendente in un cantiere (esclusi sabati)
  const oreLive = (dipId, cantiere) =>
    oreData
      .filter((o) =>
        o.dipendenteId === dipId &&
        o.cantiere === cantiere &&
        o.ore > 0 &&
        !isDateSabato(o.data)
      )
      .reduce((s, o) => s + o.ore, 0);

  // Totale = override manuali (somma tutti i mesi) + dati Supabase
  const oreBusta = (dipId, cantiere) => {
    // Cerca override senza mese (vecchio formato)
    const keySimple = `${dipId}-${cantiere}`;
    if (oreOverride[keySimple] !== undefined && oreOverride[keySimple] !== "") return Number(oreOverride[keySimple]);
    // Somma override per tutti i mesi (formato dipId-cantiere-YYYY-MM)
    const prefix = `${dipId}-${cantiere}-`;
    let totOverride = 0;
    let hasOverride = false;
    for (const [k, v] of Object.entries(oreOverride)) {
      if (k.startsWith(prefix) && v !== "" && v !== undefined) {
        totOverride += Number(v);
        hasOverride = true;
      }
    }
    if (hasOverride) return totOverride;
    return oreLive(dipId, cantiere);
  };

  const handleAdd = () => {
    const label = tipo.trim() ? `${nome.trim()} - ${tipo.trim()}` : nome.trim();
    if (!label) return;
    addCantiere(label);
    // Salva i metadati tramite il gestore condiviso (aggiorna sia React state che localStorage)
    if (codice.trim() || dataInizio.trim() || dataFine.trim() || tipo.trim()) {
      ["codice", "dataInizio", "dataFine", "tipo"].forEach((field) => {
        const val = { codice, dataInizio, dataFine, tipo }[field].trim();
        if (val) saveCantieriMeta(label, field, val);
      });
    }
    setNome(""); setTipo(""); setCodice(""); setDataInizio(""); setDataFine("");
    setShowModal(false);
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>Cantieri</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", pointerEvents: "none" }} />
            <input value={searchCantiere} onChange={(e) => setSearchCantiere(e.target.value)} placeholder="Cerca cantiere..." style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none", width: 200 }} />
          </div>
          <button onClick={() => setShowModal(true)} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "10px 18px",
            borderRadius: "var(--radius-md)", border: "none",
            background: "linear-gradient(135deg, var(--primary), var(--primary-dark))",
            color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
            boxShadow: "0 4px 16px var(--primary-glow)", fontFamily: "var(--font)",
          }}><Plus size={18} /> Nuovo Cantiere</button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {(searchCantiere ? cantieri.filter(c => c.toLowerCase().includes(searchCantiere.toLowerCase())) : cantieri).map((c) => {
          const isOpen = expanded === c;
          // Dipendenti che hanno lavorato in questo cantiere (esclusi sabati)
          const dipCantiere = dipendenti
            .map((d) => ({ ...d, ore: oreBusta(d.id, c) }))
            .filter((d) => d.ore > 0);
          const totOre = dipCantiere.reduce((s, d) => s + d.ore, 0);
          const totCosto = dipCantiere.reduce((s, d) => {
            const costo_h = schedaDipendenti[d.id]?.costo_orario || 0;
            return s + d.ore * costo_h;
          }, 0);

          return (
            <div key={c} style={{ background: "var(--surface)", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", position: "relative" }}>
              {/* Tre puntini in alto a destra */}
              <div data-menu-container="true" style={{ position: "absolute", top: 10, right: 12, zIndex: 60 }} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setMenuOpen(menuOpen === c ? null : c)}
                  style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 20, padding: "2px 8px", borderRadius: 6, lineHeight: 1 }}
                  title="Opzioni"
                >⋮</button>
                {menuOpen === c && (
                  <div style={{ position: "absolute", top: 28, right: 0, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", minWidth: 160, zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", overflow: "hidden" }}>
                    <button
                      onClick={() => { setConfirmDelete(c); setMenuOpen(null); }}
                      style={{ width: "100%", padding: "11px 16px", background: "none", border: "none", color: "var(--danger)", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 8 }}
                    >
                      🗑 Elimina cantiere
                    </button>
                  </div>
                )}
              </div>

              {/* Header cantiere — cliccabile per espandere */}
              <div
                onClick={() => { setExpanded(isOpen ? null : c); setMenuOpen(null); }}
                style={{ padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", gap: 12, flexWrap: "wrap" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "var(--radius-md)", background: "var(--primary-glow)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--primary)", flexShrink: 0 }}>
                    <Building2 size={22} />
                  </div>
                  <div>
                    <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 600 }}>{c}</p>
                    <p style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 2 }}>{dipCantiere.length} operai</p>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ background: "var(--surface-light)", borderRadius: "var(--radius-sm)", padding: "8px 16px", textAlign: "center" }}>
                    <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Ore busta</p>
                    <p style={{ color: "var(--text)", fontSize: 20, fontWeight: 700 }}>{totOre}h</p>
                  </div>
                  {totCosto > 0 && (
                    <div style={{ background: "rgba(16,185,129,0.1)", borderRadius: "var(--radius-sm)", padding: "8px 16px", textAlign: "center", border: "1px solid rgba(16,185,129,0.2)" }}>
                      <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Costo stimato</p>
                      <p style={{ color: "var(--accent)", fontSize: 20, fontWeight: 700 }}>€{totCosto.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                    </div>
                  )}
                  <span style={{ color: "var(--text-muted)", fontSize: 18 }}>{isOpen ? "▲" : "▼"}</span>
                </div>
              </div>

              {/* Dettaglio dipendenti — visibile solo se espanso */}
              {isOpen && (
                <div style={{ borderTop: "1px solid var(--border)", padding: "0 0 16px", overflow: "hidden" }}>

                  {/* ── Metadati cantiere ── */}
                  {(() => {
                    const m = cantieriMeta[c] || {};
                    const fieldStyle = {
                      width: "100%", padding: "9px 12px",
                      background: "var(--surface-light)", border: "1px solid var(--border)",
                      borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13,
                      outline: "none", boxSizing: "border-box", fontFamily: "var(--font)",
                    };
                    const labelStyle = {
                      color: "var(--text-muted)", fontSize: 11, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.5px",
                      display: "block", marginBottom: 5,
                    };
                    return (
                      <div style={{ padding: "14px 20px", background: "rgba(99,102,241,0.05)", borderBottom: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
                        <p style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>📋 Dettagli cantiere</p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={labelStyle}>Codice univoco</label>
                            <input value={m.codice || ""} onChange={(e) => saveCantieriMeta(c, "codice", e.target.value)}
                              placeholder="Es. 7805" style={fieldStyle} />
                          </div>
                          <div>
                            <label style={labelStyle}>Tipo contratto</label>
                            <input value={m.tipo || ""} onChange={(e) => saveCantieriMeta(c, "tipo", e.target.value)}
                              placeholder="Es. SUB, SUBAPP..." style={fieldStyle} />
                          </div>
                          <div>
                            <label style={labelStyle}>Data inizio</label>
                            <input value={m.dataInizio || ""} onChange={(e) => saveCantieriMeta(c, "dataInizio", autoDate(e.target.value))}
                              placeholder="gg/mm/aa" style={fieldStyle} />
                          </div>
                          <div>
                            <label style={labelStyle}>Data fine</label>
                            <input value={m.dataFine || ""} onChange={(e) => saveCantieriMeta(c, "dataFine", autoDate(e.target.value))}
                              placeholder="gg/mm/aa" style={fieldStyle} />
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {dipCantiere.length === 0 ? (
                    <p style={{ color: "var(--text-dim)", fontSize: 13, padding: "20px", textAlign: "center" }}>Nessuna ora registrata per questo cantiere.</p>
                  ) : (
                    <>
                      {/* Intestazione tabella */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px 100px", gap: 8, padding: "10px 20px", background: "var(--surface-light)", borderBottom: "1px solid var(--border)" }}>
                        <span style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Dipendente</span>
                        <span style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "center" }}>Ore busta</span>
                        <span style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "center" }}>€/h</span>
                        <span style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "right" }}>Costo</span>
                      </div>

                      {/* Righe dipendenti */}
                      {dipCantiere.map((d) => {
                        const costo_h = schedaDipendenti[d.id]?.costo_orario || 0;
                        const costo = d.ore * costo_h;
                        return (
                          <div key={d.id} style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px 100px", gap: 8, padding: "10px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)", alignItems: "center" }}>
                            <span style={{ color: "var(--text)", fontSize: 14, fontWeight: 500 }}>{d.name}</span>
                            <span style={{ color: "var(--accent)", fontSize: 15, fontWeight: 700, textAlign: "center" }}>{d.ore}h</span>
                            <span style={{ color: costo_h > 0 ? "var(--text)" : "var(--text-dim)", fontSize: 13, textAlign: "center" }}>
                              {costo_h > 0 ? `€${costo_h.toFixed(2)}` : "—"}
                            </span>
                            <span style={{ color: costo > 0 ? "var(--accent)" : "var(--text-dim)", fontSize: 14, fontWeight: costo > 0 ? 700 : 400, textAlign: "right" }}>
                              {costo > 0 ? `€${costo.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "—"}
                            </span>
                          </div>
                        );
                      })}

                      {/* Totale cantiere */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px 100px", gap: 8, padding: "12px 20px", background: "var(--surface-light)", marginTop: 4, borderTop: "1px solid var(--border)", alignItems: "center" }}>
                        <span style={{ color: "var(--text)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>TOTALE</span>
                        <span style={{ color: "var(--text)", fontSize: 16, fontWeight: 800, textAlign: "center" }}>{totOre}h</span>
                        <span />
                        <span style={{ color: totCosto > 0 ? "#10b981" : "var(--text-dim)", fontSize: 16, fontWeight: 800, textAlign: "right" }}>
                          {totCosto > 0 ? `€${totCosto.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "—"}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---- Cantieri Archiviati (eliminati) ---- */}
      {archivedCantieri.length > 0 && (
        <div style={{ marginTop: 28, background: "var(--surface)", borderRadius: "var(--radius-md)", border: "1px solid rgba(239,68,68,0.2)", padding: 20 }}>
          <p style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            🗄 Cantieri Archiviati
            <span style={{ background: "rgba(239,68,68,0.15)", color: "var(--danger)", borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{archivedCantieri.length}</span>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {archivedCantieri.map((c) => (
              <div key={c} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "rgba(239,68,68,0.07)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(239,68,68,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Building2 size={16} style={{ color: "var(--text-dim)" }} />
                  <span style={{ color: "var(--text-muted)", fontSize: 14, textDecoration: "line-through" }}>{c}</span>
                </div>
                <button
                  onClick={() => restoreCantiere(c)}
                  style={{ padding: "7px 16px", borderRadius: "var(--radius-sm)", border: "1px solid var(--accent)", background: "transparent", color: "var(--accent)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 6 }}
                >↩ Ripristina</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- Cronologia azioni ---- */}
      <div style={{ marginTop: 20 }}>
        <button
          onClick={() => setShowCronologia((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", color: "var(--text-muted)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", width: "100%" }}
        >
          📋 Cronologia operazioni
          <span style={{ marginLeft: "auto", fontSize: 12 }}>{showCronologia ? "▲ Chiudi" : `▼ Mostra (${actionHistory.length})`}</span>
        </button>
        {showCronologia && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 var(--radius-md) var(--radius-md)", overflow: "hidden" }}>
            {actionHistory.length === 0 ? (
              <p style={{ color: "var(--text-dim)", fontSize: 13, padding: "20px", textAlign: "center" }}>Nessuna operazione registrata.</p>
            ) : (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {actionHistory.map((a) => {
                  const icons = { add_cantiere: "➕", delete_cantiere: "🗑", restore_cantiere: "↩" };
                  const colors = { add_cantiere: "var(--accent)", delete_cantiere: "var(--danger)", restore_cantiere: "var(--primary)" };
                  const ts = new Date(a.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                  return (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>{icons[a.type] || "•"}</span>
                        <div>
                          <p style={{ color: a.isUndo ? "var(--text-dim)" : "var(--text)", fontSize: 13, fontWeight: 500, fontStyle: a.isUndo ? "italic" : "normal" }}>{a.label}</p>
                          <p style={{ color: "var(--text-dim)", fontSize: 11, marginTop: 2 }}>{ts}</p>
                        </div>
                      </div>
                      {!a.isUndo && (
                        <button
                          onClick={() => undoAction(a)}
                          style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", whiteSpace: "nowrap" }}
                        >⎌ Annulla</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal conferma eliminazione */}
      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setConfirmDelete(null)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
          <div style={{ position: "relative", background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 400, border: "1px solid var(--border)", zIndex: 201, margin: "0 16px" }}>
            <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Elimina cantiere</h2>
            <p style={{ color: "var(--text-dim)", fontSize: 14, marginBottom: 24 }}>
              Sei sicuro di voler eliminare <strong style={{ color: "var(--text)" }}>{confirmDelete}</strong>?<br />
              <span style={{ color: "var(--text-muted)", fontSize: 12 }}>Le ore già registrate non verranno cancellate.</span>
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmDelete(null)} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)" }}>Annulla</button>
              <button onClick={() => { deleteCantiere(confirmDelete); setConfirmDelete(null); if (expanded === confirmDelete) setExpanded(null); }}
                style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "none", background: "var(--danger)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font)" }}>
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nuovo cantiere */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setShowModal(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
          <div style={{ position: "relative", background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 420, border: "1px solid var(--border)", zIndex: 101, margin: "0 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700 }}>Nuovo Cantiere</h2>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}><X size={22} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Indirizzo / Nome *</label>
                <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Via Roma 15"
                  style={{ width: "100%", padding: "11px 14px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
              </div>
              <div>
                <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Commessa / Cliente</label>
                <input value={tipo} onChange={(e) => setTipo(e.target.value)} placeholder="Es. AHRCOS, MEDIL..."
                  style={{ width: "100%", padding: "11px 14px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
              </div>
              <div>
                <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Codice Univoco</label>
                <input value={codice} onChange={(e) => setCodice(e.target.value)} placeholder="Es. 7805"
                  style={{ width: "100%", padding: "11px 14px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Data Inizio</label>
                  <input value={dataInizio} onChange={(e) => setDataInizio(autoDate(e.target.value))} placeholder="gg/mm/aa"
                    style={{ width: "100%", padding: "11px 14px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
                </div>
                <div>
                  <label style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 8 }}>Data Fine</label>
                  <input value={dataFine} onChange={(e) => setDataFine(autoDate(e.target.value))} placeholder="gg/mm/aa"
                    style={{ width: "100%", padding: "11px 14px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
                </div>
              </div>
              {(nome.trim() || tipo.trim()) && (
                <p style={{ color: "var(--text-dim)", fontSize: 13, background: "var(--surface-light)", padding: "8px 12px", borderRadius: "var(--radius-sm)" }}>
                  Anteprima: <strong style={{ color: "var(--text)" }}>{tipo.trim() ? `${nome.trim()} - ${tipo.trim()}` : nome.trim()}</strong>
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)" }}>Annulla</button>
              <button onClick={handleAdd} disabled={!nome.trim()} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "none", background: nome.trim() ? "linear-gradient(135deg, var(--primary), var(--primary-dark))" : "var(--surface-light)", color: nome.trim() ? "#fff" : "var(--text-dim)", fontSize: 14, fontWeight: 600, cursor: nome.trim() ? "pointer" : "not-allowed", fontFamily: "var(--font)" }}>Aggiungi</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NoteCell({ dipId, dipendenti, saveNote, setDipendenti }) {
  const dip = dipendenti.find((d) => d.id === dipId);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(dip?.note || "");
  const note = dip?.note || "";

  function commit(v) {
    saveNote(dipId, v);
    setDipendenti((p) => p.map((d) => d.id === dipId ? { ...d, note: v } : d));
    setEditing(false);
  }

  if (editing) {
    return (
      <div style={{ display: "flex", gap: 4, width: 220 }}>
        <input autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(val); if (e.key === "Escape") setEditing(false); }}
          onBlur={() => commit(val)}
          style={{ flex: 1, background: "var(--bg)", border: "1px solid var(--primary)", borderRadius: 4, padding: "3px 6px", color: "var(--text)", fontSize: 12, outline: "none", fontFamily: "var(--font)" }} />
      </div>
    );
  }

  return (
    <div
      title={note}
      onClick={() => { setVal(note); setEditing(true); }}
      style={{
        width: 220, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        padding: "3px 6px", cursor: "pointer", fontSize: 12,
        color: note ? "var(--text)" : "var(--text-dim)",
        fontStyle: note ? "normal" : "italic",
        borderRadius: 4, border: "1px solid transparent",
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--border)"}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = "transparent"}
    >
      {note || "—"}
    </div>
  );
}

// Filtra dipendenti per mese: mostra se esisteva in quel mese
function filtraDipendentiPerMese(dipendenti, anno, mese) {
  const inizioMese = `${anno}-${String(mese + 1).padStart(2, "0")}-01`;
  const fineMese = `${anno}-${String(mese + 1).padStart(2, "0")}-${new Date(anno, mese + 1, 0).getDate()}`;
  return dipendenti.filter((d) => {
    if (d.creatoIl && d.creatoIl > fineMese) return false;
    if (d.eliminatoIl && d.eliminatoIl < inizioMese) return false;
    return true;
  });
}

function FileExcelOrePage({ oreData, cantieri, setOreData, upsertOreEntry, deleteOreEntry, dipendenti: tuttiDipendenti, saveNote, setDipendenti }) {
  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth());
  const [activeCell, setActiveCell] = useState(null);
  const [popupCantiere, setPopupCantiere] = useState("");
  const [searchDip, setSearchDip] = useState("");
  const [selectedCells, setSelectedCells] = useState([]);
  const [lastClickedCell, setLastClickedCell] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [headerPopup, setHeaderPopup] = useState(null);

  // Filtra dipendenti per il mese selezionato
  const dipendenti = useMemo(() => filtraDipendentiPerMese(tuttiDipendenti, anno, mese), [tuttiDipendenti, anno, mese]);

  const nomiMesi = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
  const giorniMese = new Date(anno, mese + 1, 0).getDate();
  const giorni = Array.from({ length: giorniMese }, (_, i) => i + 1);
  const giorniSettimana = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
  const oreOptions = [4, 5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 10];

  const isDomenica = (g) => new Date(anno, mese, g).getDay() === 0;
  const isSabato = (g) => new Date(anno, mese, g).getDay() === 6;
  const isWeekend = (g) => isDomenica(g) || isSabato(g);

  const getEntry = (dipId, giorno) => {
    const dateStr = `${anno}-${String(mese + 1).padStart(2, "0")}-${String(giorno).padStart(2, "0")}`;
    return oreData.find((o) => o.dipendenteId === dipId && o.data === dateStr) || null;
  };

  const getTotale = (dipId) => {
    const prefix = `${anno}-${String(mese + 1).padStart(2, "0")}`;
    return oreData
      .filter((o) => o.dipendenteId === dipId && o.data.startsWith(prefix) && o.ore > 0)
      .reduce((s, o) => s + o.ore, 0);
  };

  const getTotaleLavorate = (dipId) => {
    const prefix = `${anno}-${String(mese + 1).padStart(2, "0")}`;
    const tot = oreData
      .filter((o) => {
        if (!(o.dipendenteId === dipId && o.data.startsWith(prefix) && o.ore > 0)) return false;
        const giorno = parseInt(o.data.split("-")[2]);
        // Escludi sabati, domeniche e festivi (FS)
        if (isSabato(giorno) || isDomenica(giorno)) return false;
        if (o.stato === "FS") return false;
        return true;
      })
      .reduce((s, o) => s + o.ore, 0);
    return tot;
  };

  // Filtro dipendenti visibili (per calcolo indice riga)
  const visibleDips = searchDip ? dipendenti.filter(d => d.name.toLowerCase().includes(searchDip.toLowerCase())) : dipendenti;

  const handleCellClick = (e, dipId, giorno) => {
    e.stopPropagation();

    if (e.shiftKey) {
      // SHIFT+CLICK: se non c'è punto di partenza, imposta questo come partenza (niente popup)
      if (!lastClickedCell) {
        setLastClickedCell({ dipId, giorno });
        setSelectedCells([{ dipId, giorno }]);
        setActiveCell(null);
        return;
      }

      // Se c'è già un punto di partenza, seleziona il range e apri popup
      const dipIdsList = visibleDips.map(d => d.id);
      const startDipIdx = dipIdsList.indexOf(lastClickedCell.dipId);
      const endDipIdx = dipIdsList.indexOf(dipId);
      if (startDipIdx === -1 || endDipIdx === -1) return;

      const minDipIdx = Math.min(startDipIdx, endDipIdx);
      const maxDipIdx = Math.max(startDipIdx, endDipIdx);
      const minG = Math.min(lastClickedCell.giorno, giorno);
      const maxG = Math.max(lastClickedCell.giorno, giorno);

      const cells = [];
      for (let di = minDipIdx; di <= maxDipIdx; di++) {
        for (let g = minG; g <= maxG; g++) {
          cells.push({ dipId: dipIdsList[di], giorno: g });
        }
      }
      if (cells.length === 0) return;
      setSelectedCells(cells);

      const rect = e.currentTarget.getBoundingClientRect();
      setPopupCantiere("");
      setActiveCell({ dipId, giorno, x: rect.left, y: rect.bottom + window.scrollY, multi: true });
      return;
    }

    // Click normale (senza Shift) — resetta selezione, apri popup singolo
    setLastClickedCell(null);
    setSelectedCells([]);

    if (activeCell?.dipId === dipId && activeCell?.giorno === giorno) {
      setActiveCell(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const existing = getEntry(dipId, giorno);
    setPopupCantiere(existing?.cantiere || "");
    setActiveCell({ dipId, giorno, x: rect.left, y: rect.bottom + window.scrollY, multi: false });
  };

  // Applica stato a TUTTI i dipendenti per un giorno (click su header)
  const handleHeaderApply = (giorno, stato) => {
    const dateStr = `${anno}-${String(mese + 1).padStart(2, "0")}-${String(giorno).padStart(2, "0")}`;
    // Salva stato precedente per undo
    const previousState = visibleDips.map((dip) => {
      const existing = getEntry(dip.id, giorno);
      if (existing) return { ...existing };
      return { id: `${dip.id}-${dateStr}`, _deleted: true };
    });
    setUndoStack((prev) => [...prev.slice(-20), previousState]);

    // Applica a tutti
    for (const dip of visibleDips) {
      const existing = getEntry(dip.id, giorno);
      const entryId = existing?.id || `${dip.id}-${dateStr}`;
      const entry = {
        id: entryId,
        dipendenteId: dip.id,
        dipendente: dip.name,
        cantiere: existing?.cantiere || "",
        ore: existing?.ore || 0,
        stato,
        data: dateStr,
        note: existing?.note || "",
        timestamp: new Date(dateStr).getTime(),
      };
      upsertOreEntry ? upsertOreEntry(entry) : setOreData((prev) => [...prev.filter((o) => o.id !== entryId), entry]);
    }
    setHeaderPopup(null);
  };

  // Cmd+Z / Ctrl+Z — annulla ultima operazione
  useEffect(() => {
    const handleUndo = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && undoStack.length > 0) {
        e.preventDefault();
        const last = undoStack[undoStack.length - 1];
        setUndoStack((prev) => prev.slice(0, -1));
        for (const saved of last) {
          if (saved._deleted) {
            // Era vuota prima — cancella
            if (deleteOreEntry) deleteOreEntry(saved.id);
            else setOreData((prev) => prev.filter((o) => o.id !== saved.id));
          } else {
            // Ripristina il valore precedente
            if (upsertOreEntry) upsertOreEntry(saved);
            else setOreData((prev) => [...prev.filter((o) => o.id !== saved.id), saved]);
          }
        }
      }
    };
    window.addEventListener("keydown", handleUndo);
    return () => window.removeEventListener("keydown", handleUndo);
  }, [undoStack, deleteOreEntry, upsertOreEntry]);

  const handleCellUpdate = (dipId, giorno, value, cantiereOverride) => {
    // Se multi-selezione, applica a tutte le celle selezionate
    const cellsToUpdate = selectedCells.length > 1 ? selectedCells : [{ dipId, giorno }];

    // Salva stato precedente per undo
    const previousState = cellsToUpdate.map((cell) => {
      const existing = getEntry(cell.dipId, cell.giorno);
      if (existing) return { ...existing };
      const dateStr = `${anno}-${String(mese + 1).padStart(2, "0")}-${String(cell.giorno).padStart(2, "0")}`;
      return { id: `${cell.dipId}-${dateStr}`, _deleted: true };
    });
    setUndoStack((prev) => [...prev.slice(-20), previousState]); // max 20 undo

    for (const cell of cellsToUpdate) {
      const dateStr = `${anno}-${String(mese + 1).padStart(2, "0")}-${String(cell.giorno).padStart(2, "0")}`;
      const dip = dipendenti.find((d) => d.id === cell.dipId);
      if (!dip) continue;
      const existingEntry = getEntry(cell.dipId, cell.giorno);
      const entryId = existingEntry?.id || `${cell.dipId}-${dateStr}`;

      if (value === null) {
        if (existingEntry) {
          deleteOreEntry ? deleteOreEntry(existingEntry.id) : setOreData((prev) => prev.filter((o) => o.id !== existingEntry.id));
        }
        continue;
      }
      const isStatus = typeof value === "string";
      const cantiere = cantiereOverride !== undefined ? cantiereOverride : (existingEntry?.cantiere || "");
      const entry = {
        id: entryId,
        dipendenteId: cell.dipId,
        dipendente: dip.name,
        cantiere,
        ore: isStatus ? (existingEntry?.ore || 0) : value,
        stato: isStatus ? value : null,
        data: dateStr,
        note: existingEntry?.note || "",
        timestamp: new Date(dateStr).getTime(),
      };
      upsertOreEntry ? upsertOreEntry(entry) : setOreData((prev) => [...prev.filter((o) => o.id !== entryId), entry]);
    }
    setActiveCell(null);
    setSelectedCells([]);
  };

  const renderCell = (entry, giorno) => {
    // Domenica: mostra le ore se ci sono, altrimenti nulla
    if (!entry) return null;
    if (entry.stato && STATUS_COLORS[entry.stato]) {
      const sc = STATUS_COLORS[entry.stato];
      if (entry.ore > 0) {
        return (
          <span style={{
            background: sc.bg, color: sc.text,
            padding: "4px 6px", borderRadius: 4,
            fontSize: 14, fontWeight: 800, display: "inline-block", minWidth: 28, textAlign: "center",
            boxShadow: `0 1px 4px ${sc.bg}88`,
          }}>{entry.ore}</span>
        );
      }
      return (
        <span style={{
          background: sc.bg, color: sc.text,
          padding: "4px 5px", borderRadius: 4,
          fontSize: 13, fontWeight: 800, display: "inline-block", minWidth: 28, textAlign: "center",
          boxShadow: `0 1px 4px ${sc.bg}88`,
        }}>{entry.stato}</span>
      );
    }
    if (entry.ore > 0) {
      const col = entry.ore >= 8 ? "#22c55e" : entry.ore >= 6 ? "#f59e0b" : "#60a5fa";
      return (
        <span style={{ color: col, fontWeight: 800, fontSize: 16, letterSpacing: "-0.5px" }}>
          {entry.ore}
        </span>
      );
    }
    return null;
  };

  const weekendBg = "rgba(100,100,130,0.18)";
  const domBg = "rgba(80,80,110,0.28)";

  const cellBg = (g, entry) => {
    if (isDomenica(g)) return domBg;
    if (isSabato(g)) return weekendBg;
    if (entry?.stato && STATUS_COLORS[entry.stato]) return STATUS_COLORS[entry.stato].bg + "22";
    return "transparent";
  };

  return (
    <>
      {/* Chiudi popup cliccando fuori — ma mantieni lastClickedCell per shift-select */}
      {activeCell && (
        <div onClick={() => { setActiveCell(null); setSelectedCells([]); }} style={{ position: "fixed", inset: 0, zIndex: 150 }} />
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>File Excel Ore</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", pointerEvents: "none" }} />
            <input value={searchDip} onChange={(e) => setSearchDip(e.target.value)} placeholder="Cerca dipendente..." style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none", width: 180 }} />
          </div>
          <select value={mese} onChange={(e) => setMese(Number(e.target.value))} style={filterSelectStyle}>
            {nomiMesi.map((n, i) => <option key={i} value={i}>{n}</option>)}
          </select>
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} style={filterSelectStyle}>
            {[2024, 2025, 2026, 2027].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
<button onClick={() => exportExcelOre(oreData, anno, mese, cantieri, dipendenti)} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "10px 18px",
            borderRadius: "var(--radius-md)", border: "none",
            background: "linear-gradient(135deg, var(--accent), #059669)",
            color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
            boxShadow: "0 4px 16px var(--accent-glow)", fontFamily: "var(--font)",
          }}><Download size={18} /> Scarica Excel</button>
        </div>
      </div>

      {/* Popup modifica cella — Excel-like con input diretto */}
      {activeCell && (() => {
        const entry = getEntry(activeCell.dipId, activeCell.giorno);
        const popupLeft = Math.min(activeCell.x, window.innerWidth - 300);
        return (
          <div data-cell-popup onClick={(e) => e.stopPropagation()} style={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 200,
            background: "#1e2435",
            border: "2px solid var(--primary)",
            borderRadius: "var(--radius-md)",
            padding: "16px 18px",
            width: 300,
            boxShadow: "0 12px 40px rgba(0,0,0,0.7)",
          }}>
            {/* Input diretto — auto-focus, digita e premi Invio */}
            {selectedCells.length > 1 && (
              <div style={{ background: "rgba(192,57,43,0.15)", border: "1px solid rgba(192,57,43,0.3)", borderRadius: 8, padding: "8px 12px", marginBottom: 10, textAlign: "center" }}>
                <p style={{ color: "#C0392B", fontSize: 13, fontWeight: 700 }}>Selezione multipla: {selectedCells.length} celle</p>
                <p style={{ color: "var(--text-dim)", fontSize: 11 }}>Il valore verrà applicato a tutte</p>
              </div>
            )}
            <p style={{ color: "var(--text-muted)", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
              ✏️ Ore lavorate — digita e premi Invio
            </p>
            <input
              autoFocus
              type="number"
              min="0"
              max="24"
              step="0.5"
              defaultValue={entry?.ore > 0 ? entry.ore : ""}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = parseFloat(e.target.value);
                  handleCellUpdate(activeCell.dipId, activeCell.giorno, isNaN(v) ? null : v, popupCantiere);
                }
                if (e.key === "Escape") setActiveCell(null);
              }}
              style={{
                width: "100%", padding: "12px 14px", marginBottom: 10,
                background: "var(--bg)", border: "2px solid var(--primary)",
                borderRadius: "var(--radius-sm)", color: "var(--text)",
                fontSize: 24, fontWeight: 800, outline: "none", fontFamily: "var(--font)",
                boxSizing: "border-box", textAlign: "center",
              }}
              placeholder="0"
            />
            {/* Selettore cantiere */}
            <p style={{ color: "var(--text-muted)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5 }}>
              🏗 Cantiere
            </p>
            <select
              value={popupCantiere}
              onChange={(e) => setPopupCantiere(e.target.value)}
              style={{
                width: "100%", padding: "8px 10px", marginBottom: 12,
                background: "var(--surface-light)", border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)", color: popupCantiere ? "var(--text)" : "var(--text-dim)",
                fontSize: 13, fontFamily: "var(--font)", outline: "none", boxSizing: "border-box",
              }}
            >
              <option value="">— Nessun cantiere —</option>
              {cantieri.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {/* Preset veloci */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
              {oreOptions.map((o) => (
                <button key={o} onClick={() => handleCellUpdate(activeCell.dipId, activeCell.giorno, o, popupCantiere)} style={{
                  padding: "7px 10px", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 700,
                  border: entry?.ore === o ? "2px solid var(--primary)" : "1px solid var(--border)",
                  background: entry?.ore === o ? "var(--primary)" : "var(--surface-light)",
                  color: entry?.ore === o ? "#fff" : "var(--text)",
                  fontFamily: "var(--font)",
                }}>{o}h</button>
              ))}
            </div>
            {/* Stato */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 12 }}>
              {Object.entries(STATUS_COLORS).map(([k, v]) => (
                <button key={k} onClick={() => handleCellUpdate(activeCell.dipId, activeCell.giorno, k, popupCantiere)} style={{
                  padding: "7px 12px", borderRadius: 6,
                  border: entry?.stato === k ? "2px solid rgba(255,255,255,0.5)" : "none",
                  background: v.bg, color: v.text, cursor: "pointer", fontWeight: 800, fontSize: 13,
                  fontFamily: "var(--font)",
                }}>{k} — {v.label}</button>
              ))}
            </div>
            <button onClick={() => handleCellUpdate(activeCell.dipId, activeCell.giorno, null)} style={{
              width: "100%", padding: "9px", border: "1px solid rgba(239,68,68,0.4)",
              background: "rgba(239,68,68,0.1)", color: "var(--danger)", cursor: "pointer",
              borderRadius: 6, fontSize: 14, fontWeight: 700, fontFamily: "var(--font)",
            }}>🗑 Cancella / Elimina cella</button>
          </div>
        );
      })()}

      {/* Popup header — applica stato a tutti i dipendenti per un giorno */}
      {headerPopup && (
        <>
          <div onClick={() => setHeaderPopup(null)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
          <div onClick={(e) => e.stopPropagation()} style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            zIndex: 201, background: "#1e2435", border: "2px solid var(--primary)",
            borderRadius: "var(--radius-md)", padding: "20px 22px", width: 320,
            boxShadow: "0 12px 40px rgba(0,0,0,0.7)",
          }}>
            <p style={{ color: "var(--text)", fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
              Giorno {headerPopup.giorno} — {giorniSettimana[new Date(anno, mese, headerPopup.giorno).getDay()]}
            </p>
            <p style={{ color: "var(--text-dim)", fontSize: 12, marginBottom: 16 }}>
              Applica a tutti i {visibleDips.length} dipendenti
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {Object.entries(STATUS_COLORS).map(([k, v]) => (
                <button key={k} onClick={() => handleHeaderApply(headerPopup.giorno, k)} style={{
                  padding: "8px 14px", borderRadius: 8,
                  border: "none", background: v.bg, color: v.text,
                  cursor: "pointer", fontWeight: 800, fontSize: 13,
                  fontFamily: "var(--font)",
                }}>{k} — {v.label}</button>
              ))}
            </div>
            <button onClick={() => setHeaderPopup(null)} style={{
              width: "100%", marginTop: 12, padding: "10px", border: "1px solid var(--border)",
              background: "transparent", color: "var(--text-muted)", cursor: "pointer",
              borderRadius: 8, fontSize: 13, fontFamily: "var(--font)",
            }}>Annulla</button>
          </div>
        </>
      )}

      {/* Tabella presenze */}
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15, userSelect: "none", WebkitUserSelect: "none" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr style={{ borderBottom: "1px solid var(--border)", background: "#1a2035" }}>
                <th style={{ padding: "12px 18px", textAlign: "left", color: "var(--text)", fontWeight: 700, whiteSpace: "nowrap", minWidth: 190, borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "#1a2035", zIndex: 11, fontSize: 15 }}>Dipendente</th>
                {giorni.map((g) => {
                  const dom = isDomenica(g);
                  const sab = isSabato(g);
                  const bg = dom ? domBg : sab ? weekendBg : "transparent";
                  return (
                    <th key={g} onClick={(e) => { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); setHeaderPopup({ giorno: g, x: rect.left, y: rect.bottom + window.scrollY }); }} style={{ padding: "10px 4px", textAlign: "center", color: dom ? "#666" : sab ? "#888" : "var(--text-muted)", fontWeight: 600, minWidth: 38, borderRight: "1px solid var(--border)", background: bg, fontSize: 13, cursor: "pointer" }}>
                      {giorniSettimana[new Date(anno, mese, g).getDay()]}
                    </th>
                  );
                })}
                <th style={{ padding: "10px 12px", textAlign: "center", color: "#60a5fa", fontWeight: 700, whiteSpace: "nowrap", minWidth: 110, fontSize: 14, borderLeft: "2px solid var(--border)", borderRight: "1px solid var(--border)" }}>Totale ore vere</th>
                <th style={{ padding: "10px 12px", textAlign: "center", color: "#34d399", fontWeight: 700, whiteSpace: "nowrap", minWidth: 110, fontSize: 14, borderRight: "1px solid var(--border)" }}>Totale ore busta</th>
                <th style={{ padding: "10px 12px", textAlign: "left", color: "var(--text-muted)", fontWeight: 700, whiteSpace: "nowrap", minWidth: 200, fontSize: 14, borderRight: "1px solid var(--border)" }}>Note</th>
              </tr>
              <tr style={{ borderBottom: "2px solid var(--border)", background: "#151d30" }}>
                <th style={{ padding: "8px 18px", textAlign: "left", color: "var(--text-dim)", fontSize: 14, fontWeight: 600, borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "#151d30", zIndex: 11 }}>
                  {nomiMesi[mese]} {anno}
                </th>
                {giorni.map((g) => {
                  const dom = isDomenica(g);
                  const sab = isSabato(g);
                  const bg = dom ? domBg : sab ? weekendBg : "#151d30";
                  return (
                    <th key={g} style={{ padding: "8px 4px", textAlign: "center", color: dom ? "#555" : sab ? "#777" : "var(--text)", fontWeight: 700, fontSize: 14, borderRight: "1px solid var(--border)", background: bg }}>{g}</th>
                  );
                })}
                <th style={{ borderLeft: "2px solid var(--border)", borderRight: "1px solid var(--border)" }} />
                <th style={{ borderRight: "1px solid var(--border)" }} />
                <th style={{ borderRight: "1px solid var(--border)" }} />
              </tr>
            </thead>
            <tbody>
              {(searchDip ? dipendenti.filter(d => d.name.toLowerCase().includes(searchDip.toLowerCase())) : dipendenti).map((dip, idx) => {
                const tot = getTotale(dip.id);
                const totLav = getTotaleLavorate(dip.id);
                const rowEven = idx % 2 === 0;
                return (
                  <tr key={dip.id} style={{ borderBottom: "1px solid var(--border)", background: rowEven ? "transparent" : "rgba(20,28,50,0.5)" }}>
                    <td style={{ padding: "11px 18px", color: "var(--text)", fontWeight: 700, whiteSpace: "nowrap", borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: rowEven ? "var(--surface)" : "#131929", zIndex: 5, fontSize: 15 }}>{dip.name}</td>
                    {giorni.map((g) => {
                      const entry = getEntry(dip.id, g);
                      const isActive = activeCell?.dipId === dip.id && activeCell?.giorno === g;
                      const isSelected = selectedCells.some(c => c.dipId === dip.id && c.giorno === g);
                      const isLastClicked = lastClickedCell?.dipId === dip.id && lastClickedCell?.giorno === g && !activeCell;
                      return (
                        <td key={g}
                          onClick={(e) => handleCellClick(e, dip.id, g)}
                          style={{
                            padding: "10px 4px", textAlign: "center", fontSize: 15,
                            borderRight: "1px solid var(--border)", minWidth: 38,
                            background: isActive ? "rgba(99,102,241,0.3)" : isSelected ? "rgba(192,57,43,0.2)" : isLastClicked ? "rgba(96,165,250,0.15)" : cellBg(g, entry),
                            cursor: "pointer",
                            outline: isActive ? "3px solid var(--primary)" : isSelected ? "2px solid rgba(192,57,43,0.5)" : isLastClicked ? "2px dashed rgba(96,165,250,0.5)" : "none",
                            outlineOffset: "-2px",
                            transition: "background 0.1s",
                          }}>
                          {renderCell(entry, g)}
                        </td>
                      );
                    })}
                    <td style={{ padding: "11px 12px", textAlign: "center", fontWeight: 800, color: tot > 0 ? "#60a5fa" : "var(--text-dim)", borderLeft: "2px solid var(--border)", borderRight: "1px solid var(--border)", fontSize: 16, background: tot > 0 ? "rgba(96,165,250,0.07)" : "transparent" }}>
                      {tot > 0 ? `${tot}h` : ""}
                    </td>
                    <td style={{ padding: "11px 12px", textAlign: "center", fontWeight: 800, color: totLav > 0 ? "#34d399" : "var(--text-dim)", borderRight: "1px solid var(--border)", fontSize: 16, background: totLav > 0 ? "rgba(52,211,153,0.07)" : "transparent" }}>
                      {totLav > 0 ? `${totLav}h` : ""}
                    </td>
                    <td style={{ padding: "8px 12px", minWidth: 200, borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)" }}>
                      <NoteCell dipId={dip.id} dipendenti={dipendenti} saveNote={saveNote} setDipendenti={setDipendenti} />
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: "2px solid var(--border)", background: "#151d30" }}>
                <td style={{ padding: "12px 18px", color: "var(--text-muted)", fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.5px", borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "#151d30", zIndex: 5 }}>Totale giornaliero</td>
                {giorni.map((g) => {
                  const dateStr = `${anno}-${String(mese + 1).padStart(2, "0")}-${String(g).padStart(2, "0")}`;
                  const tot = oreData.filter((o) => o.data === dateStr && o.ore > 0).reduce((s, o) => s + o.ore, 0);
                  const dom = isDomenica(g);
                  const sab = isSabato(g);
                  return (
                    <td key={g} style={{ padding: "10px 4px", textAlign: "center", color: tot > 0 ? "#fbbf24" : "transparent", fontWeight: 800, fontSize: 14, borderRight: "1px solid var(--border)", background: dom ? domBg : sab ? weekendBg : "transparent" }}>
                      {tot > 0 ? tot : "·"}
                    </td>
                  );
                })}
                <td style={{ padding: "12px 12px", textAlign: "center", fontWeight: 800, color: "#60a5fa", borderLeft: "2px solid var(--border)", borderRight: "1px solid var(--border)", fontSize: 16 }}>
                  {oreData.filter((o) => o.data.startsWith(`${anno}-${String(mese + 1).padStart(2, "0")}`) && o.ore > 0).reduce((s, o) => s + o.ore, 0)}h
                </td>
                <td style={{ padding: "12px 12px", textAlign: "center", fontWeight: 800, color: "#34d399", borderRight: "1px solid var(--border)", fontSize: 16 }}>
                  {dipendenti.reduce((s, d) => s + getTotaleLavorate(d.id), 0)}h
                </td>
                <td style={{ borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)" }} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ marginTop: 16, background: "var(--surface)", borderRadius: "var(--radius-md)", padding: "18px 20px", border: "1px solid var(--border)" }}>
        <p style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14 }}>Legenda — clicca una cella per modificarla</p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { color: "#00B050", label: "≥ 8h lavorati", text: "#fff" },
            { color: "#f59e0b", label: "6-7h lavorati", text: "#fff" },
            { color: "var(--primary)", label: "< 6h lavorati", text: "#fff" },
            { color: weekendBg, label: "Dom / Sab", text: "#AFABAB", border: "1px solid #AFABAB" },
            ...Object.entries(STATUS_COLORS).map(([k, v]) => ({ color: v.bg, label: `${k} = ${v.label}`, text: v.text })),
          ].map((l) => (
            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ padding: "4px 9px", borderRadius: 4, background: l.color, color: l.text, fontSize: 13, fontWeight: 700, minWidth: 24, textAlign: "center", border: l.border || "none" }}>
                {l.label.split(" ")[0]}
              </div>
              <span style={{ color: "var(--text-dim)", fontSize: 13 }}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// FILE EXCEL CANTIERI
// ============================================================================
function FileExcelCantieriPage({ oreData, cantieri, archivedCantieri = [], dipendenti: tuttiDipendenti, oreOverride = {}, setOreOverride = () => {}, commitOreOverride: commitOreOverrideProp, cantieriMeta = {}, saveCantieriMeta = () => {}, schedaDipendenti = {} }) {
  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth());

  // Filtra dipendenti per il mese selezionato
  const dipendenti = useMemo(() => filtraDipendentiPerMese(tuttiDipendenti, anno, mese), [tuttiDipendenti, anno, mese]);
  const [viewMode, setViewMode] = useState("ore"); // "ore" | "costo"
  const [editingCell, setEditingCell] = useState(null);
  const [searchDip, setSearchDip] = useState("");

  const commitOreOverride = (key, value) => {
    if (commitOreOverrideProp) commitOreOverrideProp(key, value);
    setEditingCell(null);
  };

  const nomiMesi = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
  const prefisso = `${anno}-${String(mese + 1).padStart(2, "0")}`;
const isDateSabato = (dateStr) => new Date(dateStr).getDay() === 6;

  const getOreDipCantiere = (dipId, cantiere) => {
    const key = `${dipId}-${cantiere}-${prefisso}`;
    if (oreOverride[key] !== undefined) return Number(oreOverride[key]);
    return oreData
      .filter((o) => Number(o.dipendenteId) === Number(dipId) && o.cantiere === cantiere && o.ore > 0 && o.data.startsWith(prefisso) && !isDateSabato(o.data))
      .reduce((s, o) => s + o.ore, 0);
  };

  const getTotDip = (dipId) => {
    // Se c'è un override manuale delle ore busta, usalo
    const bustaKey = `${dipId}-__BUSTA__-${prefisso}`;
    if (oreOverride[bustaKey] !== undefined && oreOverride[bustaKey] !== "") return Number(oreOverride[bustaKey]);
    return [...cantieri, ...archivedCantieri].reduce((s, c) => s + getOreDipCantiere(dipId, c), 0);
  };

  // Tutti i cantieri attivi sempre visibili + archiviati con dati (grayed out)
  const cantieriArchiviatiConDati = archivedCantieri.filter((c) =>
    dipendenti.some((d) => getOreDipCantiere(d.id, c) > 0)
  );
  const tuttiCantieri = [...cantieri, ...cantieriArchiviatiConDati];

  const dipAttivi = searchDip
    ? dipendenti.filter(d => d.name.toLowerCase().includes(searchDip.toLowerCase()))
    : dipendenti;
  const getTotCantiere = (cantiere) => dipAttivi.reduce((s, d) => s + getOreDipCantiere(d.id, cantiere), 0);
  const grandTot = dipendenti.reduce((s, d) => s + getTotDip(d.id), 0);
  const isArchiviato = (c) => cantieriArchiviatiConDati.includes(c);

  // ===== Navigazione stile Excel: click + frecce/Tab/Enter/Esc =====
  // Coordinate: colonna 0 = "Ore busta", colonne 1..N = tuttiCantieri[c-1]
  const gridScrollRef = useRef(null);
  const navRows = dipAttivi.length;
  const navCols = 1 + tuttiCantieri.length;

  const isNavCellEditable = (r, c) => {
    if (r < 0 || r >= navRows || c < 0 || c >= navCols) return false;
    if (c === 0) return true;
    return !isArchiviato(tuttiCantieri[c - 1]);
  };

  const coordToEditingCell = (r, c) => {
    const dipId = dipAttivi[r].id;
    return { dipId, cantiere: c === 0 ? "__BUSTA__" : tuttiCantieri[c - 1] };
  };

  const editingCellToCoord = () => {
    if (!editingCell || editingCell.meta || editingCell.dipId == null) return null;
    const r = dipAttivi.findIndex((d) => d.id === editingCell.dipId);
    if (r === -1) return null;
    const c = editingCell.cantiere === "__BUSTA__"
      ? 0
      : 1 + tuttiCantieri.indexOf(editingCell.cantiere);
    if (c <= 0 && editingCell.cantiere !== "__BUSTA__") return null;
    return { r, c };
  };

  const moveGridSelection = (dr, dc) => {
    const cur = editingCellToCoord();
    if (!cur) return;
    let r = cur.r + dr;
    let c = cur.c + dc;
    while (r >= 0 && r < navRows && c >= 0 && c < navCols) {
      if (isNavCellEditable(r, c)) {
        setEditingCell(coordToEditingCell(r, c));
        return;
      }
      r += dr;
      c += dc;
    }
  };

  const handleNavKeyDown = (e, cellKey) => {
    if (e.key === "Escape") { setEditingCell(null); return; }
    const input = e.target;
    const pos = input.selectionStart ?? 0;
    const len = (input.value || "").length;

    const commitAndMove = (dr, dc) => {
      e.preventDefault();
      // Committa solo se il valore e' stato effettivamente modificato,
      // altrimenti rischia di sovrascrivere dati reali con override vuoti.
      if (input.value !== input.defaultValue) {
        commitOreOverride(cellKey, input.value);
      }
      moveGridSelection(dr, dc);
    };

    if (e.key === "Enter") { commitAndMove(1, 0); return; }
    if (e.key === "Tab") { commitAndMove(0, e.shiftKey ? -1 : 1); return; }
    if (e.key === "ArrowUp") { commitAndMove(-1, 0); return; }
    if (e.key === "ArrowDown") { commitAndMove(1, 0); return; }
    if (e.key === "ArrowLeft" && pos === 0) { commitAndMove(0, -1); return; }
    if (e.key === "ArrowRight" && pos === len) { commitAndMove(0, 1); return; }
  };

  // Scroll automatico della cella in edit quando esce dalla viewport
  useEffect(() => {
    const coord = editingCellToCoord();
    if (!coord) return;
    const container = gridScrollRef.current;
    if (!container) return;
    const td = container.querySelector(`td[data-r="${coord.r}"][data-c="${coord.c}"]`);
    if (td) td.scrollIntoView({ block: "nearest", inline: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCell]);

  // Stile cella editabile
  const editableCellStyle = (isEditing) => ({
    padding: "5px 8px", background: isEditing ? "rgba(99,102,241,0.15)" : "transparent",
    border: isEditing ? "1px solid var(--primary)" : "1px solid transparent",
    borderRadius: 4, outline: "none", width: "100%", color: "var(--text)",
    fontSize: 11, fontFamily: "var(--font)", textAlign: "center", cursor: "pointer",
    boxSizing: "border-box",
  });

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>File Excel Cantieri</h1>
          <p style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 4 }}>{viewMode === "ore" ? 'Ore busta per dipendente per cantiere — TOT corrisponde a "Totale ore busta"' : "Costo anno fiscale per cantiere (ore × costo orario)"}</p>
          <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
            <button onClick={() => setViewMode("ore")} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--border)", background: viewMode === "ore" ? "var(--primary)" : "var(--surface-light)", color: viewMode === "ore" ? "#fff" : "var(--text-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }}>Ore mensili</button>
            <button onClick={() => setViewMode("costo")} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--border)", background: viewMode === "costo" ? "var(--primary)" : "var(--surface-light)", color: viewMode === "costo" ? "#fff" : "var(--text-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }}>Costo anno fiscale</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* Cerca dipendente */}
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", pointerEvents: "none" }} />
            <input
              value={searchDip}
              onChange={(e) => setSearchDip(e.target.value)}
              placeholder="Cerca dipendente..."
              style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none", width: 180 }}
            />
          </div>
          {/* Selettore mese/anno (solo per vista ore) */}
          {viewMode === "ore" && <select value={mese} onChange={(e) => setMese(Number(e.target.value))} style={{ padding: "8px 12px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none" }}>
            {nomiMesi.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>}
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} style={{ padding: "8px 12px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none" }}>
            {[2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {viewMode === "ore" && <button
            onClick={() => exportExcelCantieri(dipendenti, tuttiCantieri, getOreDipCantiere, getTotDip, nomiMesi[mese], anno, cantieriMeta)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: "var(--radius-md)", border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 16px rgba(16,185,129,0.3)", fontFamily: "var(--font)" }}
          >
            <Download size={16} /> Esporta Excel
          </button>}
        </div>
      </div>

      {/* Vista Costo Anno Fiscale */}
      {viewMode === "costo" && (() => {
        const annoFiscale = anno;
        const hasCosti = dipendenti.some((d) => schedaDipendenti[d.id]?.costo_orario);
        const getOreAnno = (dipId, cantiere) => {
          return oreData.filter((o) => Number(o.dipendenteId) === Number(dipId) && o.cantiere === cantiere && o.ore > 0 && o.data.startsWith(String(annoFiscale))).reduce((s, o) => s + o.ore, 0);
        };
        return (
          <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", padding: 20 }}>
            {!hasCosti && (
              <div style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <AlertTriangle size={18} color="#f59e0b" />
                <p style={{ color: "#f59e0b", fontSize: 13, fontWeight: 600 }}>Coefficienti costo orario da inserire — apri la scheda di ogni dipendente per impostarli.</p>
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#1a1f2e" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left", color: "var(--text-dim)", fontWeight: 600, borderBottom: "2px solid var(--border)" }}>Cantiere</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", color: "var(--text-dim)", fontWeight: 600, borderBottom: "2px solid var(--border)" }}>Ore totali</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", color: "var(--text-dim)", fontWeight: 600, borderBottom: "2px solid var(--border)" }}>Costo €</th>
                  </tr>
                </thead>
                <tbody>
                  {tuttiCantieri.map((c) => {
                    let oreTot = 0;
                    let costoTot = 0;
                    dipendenti.forEach((d) => {
                      const ore = getOreAnno(d.id, c);
                      const costo = schedaDipendenti[d.id]?.costo_orario || 0;
                      oreTot += ore;
                      costoTot += ore * costo;
                    });
                    if (oreTot === 0) return null;
                    return (
                      <tr key={c} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "10px 14px", color: "var(--text)", fontWeight: 500 }}>{c}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", color: "var(--text)" }}>{oreTot}h</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", color: costoTot > 0 ? "var(--accent)" : "var(--text-dim)", fontWeight: 700 }}>{costoTot > 0 ? `€ ${costoTot.toFixed(2)}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Tabella ore mensili */}
      {viewMode === "ore" && <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <div ref={gridScrollRef} style={{ overflowX: "auto", overflowY: "auto", maxHeight: "72vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              {/* Riga 1: Date inizio/fine */}
              <tr style={{ background: "#1a1f2e", borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "8px 16px", textAlign: "left", color: "var(--text-dim)", fontSize: 14, fontWeight: 600, borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "#1a1f2e", zIndex: 11, whiteSpace: "nowrap" }}>DATE INIZIO/FINE</th>
                <th style={{ borderRight: "2px solid var(--border)", minWidth: 80 }} />
                {tuttiCantieri.map((c) => {
                  const m = cantieriMeta[c] || {};
                  const arch = isArchiviato(c);
                  const isEditI = editingCell?.meta === c && editingCell?.field === "dataInizio";
                  const isEditF = editingCell?.meta === c && editingCell?.field === "dataFine";
                  return (
                    <th key={c} style={{ padding: "5px 6px", textAlign: "center", borderRight: "1px solid var(--border)", minWidth: 100, opacity: arch ? 0.55 : 1 }}>
                      <input value={m.dataInizio || ""} onChange={(e) => saveCantieriMeta(c, "dataInizio", autoDate(e.target.value))}
                        onFocus={() => setEditingCell({ meta: c, field: "dataInizio" })} onBlur={() => setEditingCell(null)}
                        placeholder="gg/mm/aa" style={{ ...editableCellStyle(isEditI), fontSize: 13 }} disabled={arch} />
                      <input value={m.dataFine || ""} onChange={(e) => saveCantieriMeta(c, "dataFine", autoDate(e.target.value))}
                        onFocus={() => setEditingCell({ meta: c, field: "dataFine" })} onBlur={() => setEditingCell(null)}
                        placeholder="fine" style={{ ...editableCellStyle(isEditF), fontSize: 13, marginTop: 2 }} disabled={arch} />
                    </th>
                  );
                })}
              </tr>
              {/* Riga 2: Codice univoco */}
              <tr style={{ background: "#1e2435", borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "8px 16px", textAlign: "left", color: "#a3db8e", fontSize: 14, fontWeight: 700, borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "#1e2435", zIndex: 11, whiteSpace: "nowrap" }}>CODICE UNIVOCO</th>
                <th style={{ borderRight: "2px solid var(--border)" }} />
                {tuttiCantieri.map((c) => {
                  const m = cantieriMeta[c] || {};
                  const arch = isArchiviato(c);
                  const isEdit = editingCell?.meta === c && editingCell?.field === "codice";
                  return (
                    <th key={c} style={{ padding: "5px 6px", textAlign: "center", borderRight: "1px solid var(--border)", opacity: arch ? 0.55 : 1 }}>
                      <input value={m.codice || ""} onChange={(e) => saveCantieriMeta(c, "codice", e.target.value)}
                        onFocus={() => setEditingCell({ meta: c, field: "codice" })} onBlur={() => setEditingCell(null)}
                        placeholder="—" style={{ ...editableCellStyle(isEdit), color: "#a3db8e", fontWeight: 700, fontSize: 14 }} disabled={arch} />
                    </th>
                  );
                })}
              </tr>
              {/* Riga 3: Nomi cantieri */}
              <tr style={{ background: "var(--surface-light)", borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "12px 16px", textAlign: "left", color: "var(--text-muted)", fontWeight: 600, whiteSpace: "nowrap", minWidth: 200, borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "var(--surface-light)", zIndex: 11, fontSize: 13 }}>DIPENDENTE</th>
                <th style={{ padding: "12px 12px", textAlign: "center", color: "var(--accent)", fontWeight: 700, whiteSpace: "nowrap", minWidth: 80, borderRight: "2px solid var(--border)", fontSize: 13 }}>ORE BUSTA</th>
                {tuttiCantieri.map((c) => {
                  const arch = isArchiviato(c);
                  return (
                    <th key={c} style={{ padding: "8px 8px", textAlign: "center", color: arch ? "var(--text-dim)" : "var(--text-muted)", fontWeight: 600, minWidth: 100, borderRight: "1px solid var(--border)", fontSize: 13, lineHeight: 1.3, opacity: arch ? 0.6 : 1 }}>
                      {arch && <span style={{ fontSize: 10, color: "var(--danger)", display: "block", marginBottom: 2 }}>ARCHIVIATO</span>}
                      {c.split(" - ")[0]}
                      {c.includes(" - ") && <><br /><span style={{ color: "var(--text-dim)", fontWeight: 400, fontSize: 11 }}>{c.split(" - ").slice(1).join(" - ")}</span></>}
                    </th>
                  );
                })}
              </tr>
              {/* Riga 4: Tipo contratto */}
              <tr style={{ background: "#1a1f2e", borderBottom: "2px solid var(--border)" }}>
                <th style={{ padding: "7px 16px", textAlign: "left", color: "var(--text-dim)", fontSize: 14, fontWeight: 600, borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "#1a1f2e", zIndex: 11, whiteSpace: "nowrap" }}>TIPO DI CONTRATTO</th>
                <th style={{ padding: "7px 12px", textAlign: "center", color: "var(--text-muted)", fontSize: 14, fontWeight: 600, borderRight: "2px solid var(--border)" }}>ORE busta</th>
                {tuttiCantieri.map((c) => {
                  const m = cantieriMeta[c] || {};
                  const arch = isArchiviato(c);
                  const isEdit = editingCell?.meta === c && editingCell?.field === "tipo";
                  return (
                    <th key={c} style={{ padding: "4px 6px", textAlign: "center", borderRight: "1px solid var(--border)", opacity: arch ? 0.55 : 1 }}>
                      <input value={m.tipo || ""} onChange={(e) => saveCantieriMeta(c, "tipo", e.target.value)}
                        onFocus={() => setEditingCell({ meta: c, field: "tipo" })} onBlur={() => setEditingCell(null)}
                        placeholder="tipo" style={{ ...editableCellStyle(isEdit), fontSize: 13, color: "var(--text-dim)" }} disabled={arch} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {dipAttivi.map((dip, idx) => {
                const tot = getTotDip(dip.id);
                const parts = dip.name.split(" ");
                const cognome = parts[0];
                const nome = parts.slice(1).join(" ");
                const rowBg = idx % 2 === 0 ? "var(--surface)" : "var(--surface-dark, #131929)";
                return (
                  <tr key={dip.id} style={{ borderBottom: "1px solid var(--border)", background: idx % 2 === 0 ? "transparent" : "rgba(26,34,53,0.3)" }}>
                    <td style={{ padding: "9px 16px", fontWeight: 500, whiteSpace: "nowrap", borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: rowBg, zIndex: 5 }}>
                      <span style={{ color: "var(--text)", fontWeight: 700, fontSize: 14 }}>{cognome}</span>
                      <span style={{ color: "var(--text-dim)", marginLeft: 6, fontSize: 13 }}>{nome}</span>
                    </td>
                    <td data-r={idx} data-c={0} style={{ padding: 0, textAlign: "center", fontWeight: 800, color: "var(--accent)", fontSize: 16, borderRight: "2px solid var(--border)", position: "relative" }}>
                      {editingCell?.dipId === dip.id && editingCell?.cantiere === "__BUSTA__" ? (
                        <input
                          type="text"
                          inputMode="decimal"
                          autoFocus
                          defaultValue={oreOverride[`${dip.id}-__BUSTA__-${prefisso}`] ?? tot}
                          onBlur={(e) => { if (e.target.value !== e.target.defaultValue) commitOreOverride(`${dip.id}-__BUSTA__-${prefisso}`, e.target.value); else setEditingCell(null); }}
                          onKeyDown={(e) => handleNavKeyDown(e, `${dip.id}-__BUSTA__-${prefisso}`)}
                          style={{ width: "100%", padding: "8px 6px", background: "rgba(99,102,241,0.15)", border: "1px solid var(--primary)", borderRadius: 4, color: "var(--text)", fontSize: 14, fontWeight: 700, textAlign: "center", outline: "none", fontFamily: "var(--font)", boxSizing: "border-box" }}
                        />
                      ) : (
                        <div
                          onClick={() => setEditingCell({ dipId: dip.id, cantiere: "__BUSTA__" })}
                          style={{ padding: "9px 12px", cursor: "pointer" }}
                        >
                          {oreOverride[`${dip.id}-__BUSTA__-${prefisso}`] !== undefined ? oreOverride[`${dip.id}-__BUSTA__-${prefisso}`] : (tot || "")}
                        </div>
                      )}
                    </td>
                    {tuttiCantieri.map((c, ci) => {
                      const key = `${dip.id}-${c}-${prefisso}`;
                      const ore = getOreDipCantiere(dip.id, c);
                      const arch = isArchiviato(c);
                      const isEditing = editingCell?.dipId === dip.id && editingCell?.cantiere === c;
                      const displayVal = oreOverride[key] !== undefined ? oreOverride[key] : (ore || "");
                      return (
                        <td key={c} data-r={idx} data-c={1 + ci} style={{ padding: 0, textAlign: "center", borderRight: "1px solid var(--border)", background: arch ? "rgba(239,68,68,0.05)" : (oreOverride[key] !== undefined && oreOverride[key] !== "") || ore > 0 ? "rgba(99,102,241,0.07)" : "transparent", opacity: arch ? 0.65 : 1, position: "relative" }}>
                          {isEditing ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              autoFocus
                              defaultValue={displayVal}
                              onBlur={(e) => { if (e.target.value !== e.target.defaultValue) commitOreOverride(key, e.target.value); else setEditingCell(null); }}
                              onKeyDown={(e) => handleNavKeyDown(e, key)}
                              style={{
                                width: "100%", height: "100%", padding: "8px 6px",
                                background: "rgba(99,102,241,0.15)", border: "1px solid var(--primary)",
                                borderRadius: 4, outline: "none", color: "var(--primary)",
                                fontSize: 15, fontWeight: 700, fontFamily: "var(--font)",
                                textAlign: "center", boxSizing: "border-box",
                              }}
                            />
                          ) : (
                            <div
                              onClick={() => !arch && setEditingCell({ dipId: dip.id, cantiere: c })}
                              style={{
                                padding: "8px 6px", cursor: arch ? "default" : "pointer", minHeight: 36,
                                color: (oreOverride[key] !== undefined && oreOverride[key] !== "") ? "var(--primary)" : ore > 0 ? "var(--text)" : "transparent",
                                fontWeight: (oreOverride[key] !== undefined && oreOverride[key] !== "") || ore > 0 ? 700 : 400,
                                fontSize: 15,
                              }}
                            >
                              {displayVal !== "" ? displayVal : ""}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Riga totali */}
              <tr style={{ borderTop: "2px solid var(--border)", background: "var(--surface-light)" }}>
                <td style={{ padding: "12px 16px", color: "var(--text-muted)", fontWeight: 700, fontSize: 13, textTransform: "uppercase", borderRight: "1px solid var(--border)", position: "sticky", left: 0, background: "var(--surface-light)", zIndex: 5 }}>TOTALE CANTIERE</td>
                <td style={{ padding: "12px 12px", textAlign: "center", fontWeight: 800, color: "var(--accent)", fontSize: 16, borderRight: "2px solid var(--border)" }}>{grandTot}</td>
                {tuttiCantieri.map((c) => {
                  const tot = getTotCantiere(c);
                  const arch = isArchiviato(c);
                  return (
                    <td key={c} style={{ padding: "12px 8px", textAlign: "center", fontWeight: 700, color: arch ? "var(--text-dim)" : tot > 0 ? "var(--warning)" : "var(--text-dim)", borderRight: "1px solid var(--border)", fontSize: 15, opacity: arch ? 0.65 : 1 }}>
                      {tot > 0 ? tot : ""}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>}
    </>
  );
}

// Helpers stile Excel — Mattone Rosso branding
const XL_RED      = "C0392B";
const XL_RED_DARK = "96281B";
const XL_WHITE    = "FFFFFF";
const XL_GRAY_LT  = "F5F5F5";
const XL_GRAY_MID = "E0E0E0";
const XL_TEXT     = "1A1A1A";

function xlBorder() {
  const s = { style: "thin", color: { argb: "FFCCCCCC" } };
  return { top: s, left: s, bottom: s, right: s };
}
function xlFill(hex) { return { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + hex } }; }
function xlFont(bold, hex, size = 11) { return { bold, color: { argb: "FF" + hex }, size, name: "Calibri" }; }
function xlAlign(h = "left") { return { horizontal: h, vertical: "middle" }; }

async function downloadWorkbook(wb, filename) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function exportExcelCantieri(dipendenti, cantieri, getOreDipCantiere, getTotDip, nomeMese, anno, cantieriMeta = {}) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Mattone Rosso";
  const ws = wb.addWorksheet("ORE CANTIERI");

  const numCols = 3 + cantieri.length;
  const metaLabelStyle = (cell) => {
    cell.fill = xlFill("1C2B3A");
    cell.font = xlFont(true, XL_WHITE, 10);
    cell.alignment = xlAlign("left");
    cell.border = xlBorder();
  };
  const metaValueStyle = (cell, isEmpty) => {
    cell.fill = xlFill(isEmpty ? "1e2538" : "22304a");
    cell.font = xlFont(false, isEmpty ? "445577" : "a8c4e8", 10);
    cell.alignment = xlAlign("center");
    cell.border = xlBorder();
  };

  // Riga 1 — titolo aziendale
  ws.addRow([`MATTONE ROSSO — ORE CANTIERI ${nomeMese.toUpperCase()} ${anno}`]);
  ws.mergeCells(1, 1, 1, numCols);
  const titleCell = ws.getCell("A1");
  titleCell.fill = xlFill(XL_RED);
  titleCell.font = xlFont(true, XL_WHITE, 14);
  titleCell.alignment = xlAlign("center");
  titleCell.border = xlBorder();
  ws.getRow(1).height = 30;

  // Riga 2 — DATE INIZIO / FINE
  const dateRow = ["DATE INIZIO/FINE", "", ""];
  cantieri.forEach((c) => {
    const m = cantieriMeta[c] || {};
    const inizio = m.dataInizio || "";
    const fine = m.dataFine || "";
    dateRow.push(inizio && fine ? `${inizio} → ${fine}` : inizio || fine || "—");
  });
  ws.addRow(dateRow);
  ws.getRow(2).height = 18;
  ws.getRow(2).eachCell({ includeEmpty: true }, (cell, col) => {
    if (col <= 3) metaLabelStyle(cell); else metaValueStyle(cell, !cantieri[col - 4] || !(cantieriMeta[cantieri[col - 4]]?.dataInizio || cantieriMeta[cantieri[col - 4]]?.dataFine));
  });

  // Riga 3 — CODICE UNIVOCO
  const codRow = ["CODICE UNIVOCO", "", ""];
  cantieri.forEach((c) => { codRow.push(cantieriMeta[c]?.codice || "—"); });
  ws.addRow(codRow);
  ws.getRow(3).height = 18;
  ws.getRow(3).eachCell({ includeEmpty: true }, (cell, col) => {
    if (col <= 3) metaLabelStyle(cell); else metaValueStyle(cell, !cantieriMeta[cantieri[col - 4]]?.codice);
  });

  // Riga 4 — TIPO DI CONTRATTO
  const tipoRow = ["TIPO DI CONTRATTO", "", ""];
  cantieri.forEach((c) => { tipoRow.push(cantieriMeta[c]?.tipo || "—"); });
  ws.addRow(tipoRow);
  ws.getRow(4).height = 18;
  ws.getRow(4).eachCell({ includeEmpty: true }, (cell, col) => {
    if (col <= 3) metaLabelStyle(cell); else metaValueStyle(cell, !cantieriMeta[cantieri[col - 4]]?.tipo);
  });

  // Riga 5 — intestazioni dipendenti
  const headers = ["COGNOME", "NOME", "ORE BUSTA", ...cantieri];
  ws.addRow(headers);
  const hRow = ws.getRow(5);
  hRow.height = 22;
  hRow.eachCell((cell) => {
    cell.fill = xlFill(XL_RED_DARK);
    cell.font = xlFont(true, XL_WHITE, 11);
    cell.alignment = xlAlign("center");
    cell.border = xlBorder();
  });

  // Righe dipendenti (da riga 6)
  dipendenti.forEach((dip, idx) => {
    const parts = dip.name.split(" ");
    const riga = [parts[0], parts.slice(1).join(" "), getTotDip(dip.id), ...cantieri.map((c) => getOreDipCantiere(dip.id, c) || 0)];
    ws.addRow(riga);
    const row = ws.getRow(6 + idx);
    row.height = 18;
    const bg = idx % 2 === 0 ? XL_WHITE : XL_GRAY_LT;
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      cell.fill = xlFill(bg);
      cell.font = xlFont(col === 3, XL_TEXT, 11);
      cell.alignment = xlAlign(col <= 2 ? "left" : "center");
      cell.border = xlBorder();
    });
  });

  // Riga totali
  const totRow = ["TOTALE", "", dipendenti.reduce((s, d) => s + getTotDip(d.id), 0),
    ...cantieri.map((c) => dipendenti.reduce((s, d) => s + getOreDipCantiere(d.id, c), 0))];
  ws.addRow(totRow);
  const lastRow = ws.getRow(6 + dipendenti.length);
  lastRow.height = 22;
  lastRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = xlFill(XL_GRAY_MID);
    cell.font = xlFont(true, XL_TEXT, 11);
    cell.alignment = xlAlign("center");
    cell.border = xlBorder();
  });

  // Larghezze colonne
  ws.getColumn(1).width = 22;
  ws.getColumn(2).width = 18;
  ws.getColumn(3).width = 14;
  cantieri.forEach((_, i) => { ws.getColumn(4 + i).width = 18; });

  await downloadWorkbook(wb, `ORE_CANTIERI_${nomeMese.toUpperCase()}_${anno}.xlsx`);
}

// ============================================================================
async function exportExcelOre(oreData, anno, mese, cantieri, dipendenti) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Mattone Rosso";

  const nomiMesi = ["GENNAIO","FEBBRAIO","MARZO","APRILE","MAGGIO","GIUGNO","LUGLIO","AGOSTO","SETTEMBRE","OTTOBRE","NOVEMBRE","DICEMBRE"];
  const nomeMs = nomiMesi[mese];
  const giorniMese = new Date(anno, mese + 1, 0).getDate();
  const giorni = Array.from({ length: giorniMese }, (_, i) => i + 1);
  const giorniSettimana = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];

  // Helper: converte numero colonna (1-based) in lettera Excel (A, B, ... Z, AA, ...)
  const colLetter = (n) => {
    let s = "";
    while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
    return s;
  };

  // ===== Sheet PRESENZE =====
  const ws1 = wb.addWorksheet("PRESENZE");
  const numCols1 = 5 + giorniMese + 4;

  // Titolo
  ws1.addRow([`MATTONE ROSSO — PRESENZE ${nomeMs} ${anno}`]);
  ws1.mergeCells(1, 1, 1, numCols1);
  const t1 = ws1.getCell("A1");
  t1.fill = xlFill(XL_RED); t1.font = xlFont(true, XL_WHITE, 14);
  t1.alignment = xlAlign("center"); t1.border = xlBorder();
  ws1.getRow(1).height = 30;

  // Riga giorni settimana
  const gwRow = ["", "", "", "", "", ...giorni.map((g) => giorniSettimana[new Date(anno, mese, g).getDay()]), "", "", "", ""];
  ws1.addRow(gwRow);
  ws1.getRow(2).eachCell({ includeEmpty: true }, (cell, col) => {
    const dow = col >= 6 ? new Date(anno, mese, col - 5).getDay() : -1;
    const isWE = dow === 0 || dow === 6;
    cell.fill = xlFill(isWE ? "DDDDDD" : XL_RED_DARK);
    cell.font = xlFont(true, isWE ? "888888" : XL_WHITE, 9);
    cell.alignment = xlAlign("center");
    cell.border = xlBorder();
  });
  ws1.getRow(2).height = 16;

  // Riga intestazioni
  const hRow1 = ["N.", "COGNOME", "NOME", "SCADENZA", "", ...giorni, "TOT ORE", "TOT LAV.", "€/ORA", "NOTE"];
  ws1.addRow(hRow1);
  ws1.getRow(3).eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = xlFill(XL_RED); cell.font = xlFont(true, XL_WHITE, 10);
    cell.alignment = xlAlign("center"); cell.border = xlBorder();
  });
  ws1.getRow(3).height = 20;

  // Colori stati (hex senza #, per ExcelJS)
  const STATUS_XL = {
    F:   { bg: "FF33CC", text: "FFFFFF" },
    M:   { bg: "FF9999", text: "333333" },
    PMN: { bg: "A9D18E", text: "333333" },
    Pm:  { bg: "00B050", text: "FFFFFF" },
    PG:  { bg: "F4B084", text: "333333" },
    A:   { bg: "B4C6E7", text: "333333" },
    C:   { bg: "FFF2CC", text: "333333" },
    D:   { bg: "FFFF00", text: "333333" },
    I:   { bg: "A3DBFF", text: "333333" },
    FS:  { bg: "F6C3FF", text: "333333" },
  };

  // Righe dipendenti
  dipendenti.forEach((dip, idx) => {
    const scad = dip.scadenza || "INDET.";
    let totOre = 0, totLav = 0;

    // Ogni cella giorno: { value, stato, dow }
    const cellData = giorni.map((g) => {
      const dateStr = `${anno}-${String(mese+1).padStart(2,"0")}-${String(g).padStart(2,"0")}`;
      const entry = oreData.find((o) => o.dipendenteId === dip.id && o.data === dateStr);
      const dow = new Date(anno, mese, g).getDay();
      const isDom = dow === 0, isSab = dow === 6;
      if (entry) {
        totOre += entry.ore;
        if (!isSab && !isDom && entry.stato !== "FS") totLav += entry.ore;
        if (entry.stato) {
          // stato + eventuale ore → mostra ore se > 0, altrimenti codice stato
          return { value: entry.ore > 0 ? entry.ore : entry.stato, stato: entry.stato, dow };
        }
        return { value: entry.ore > 0 ? entry.ore : "", stato: null, dow };
      }
      return { value: "", stato: null, dow };
    });

    // Formule Excel per totali
    const rowNum = 4 + idx;
    const firstDayCol = colLetter(6);
    const lastDayCol = colLetter(5 + giorniMese);
    const satCols = giorni
      .filter((g) => new Date(anno, mese, g).getDay() === 6)
      .map((g) => `${colLetter(5 + g)}${rowNum}`);
    const totOreFormula = `SUM(${firstDayCol}${rowNum}:${lastDayCol}${rowNum})`;
    const totLavFormula = satCols.length > 0
      ? `${totOreFormula}-SUM(${satCols.join(",")})`
      : totOreFormula;

    ws1.addRow([idx+1, dip.name.split(" ")[0], dip.name.split(" ").slice(1).join(" "), scad, "",
      ...cellData.map((c) => c.value),
      totOre,
      totLav,
      "", dip.note || ""]);

    const row = ws1.getRow(rowNum);
    row.height = 18;
    const bg = idx % 2 === 0 ? XL_WHITE : XL_GRAY_LT;

    // Stile base per tutta la riga
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const g = col - 5;
      const dow = (g >= 1 && g <= giorniMese) ? new Date(anno, mese, g).getDay() : -1;
      const isWE = dow === 0 || dow === 6;
      cell.fill = xlFill(isWE ? "EEEEEE" : bg);
      cell.font = xlFont(col > 5 + giorniMese, XL_TEXT, 10);
      cell.alignment = xlAlign(col <= 4 ? "left" : "center");
      cell.border = xlBorder();
    });

    // Stile specifico per ogni cella giorno (stati + colori ore)
    cellData.forEach((cd, i) => {
      const col = 6 + i;
      const cell = row.getCell(col);
      if (cd.stato && STATUS_XL[cd.stato]) {
        const sc = STATUS_XL[cd.stato];
        cell.fill = xlFill(sc.bg);
        cell.font = { bold: true, color: { argb: "FF" + sc.text }, size: 10, name: "Calibri" };
      } else if (cd.value !== "" && cd.dow !== 0) {
        const ore = parseFloat(cd.value);
        const oreHex = ore >= 8 ? "22C55E" : ore >= 6 ? "F59E0B" : "60A5FA";
        cell.font = { bold: true, color: { argb: "FF" + oreHex }, size: 10, name: "Calibri" };
      }
    });
  });

  // ===== Riga TOTALE GIORNALIERO =====
  const totRowNum = 4 + dipendenti.length;
  const isDomenicaG = (g) => new Date(anno, mese, g).getDay() === 0;
  const isSabatoG = (g) => new Date(anno, mese, g).getDay() === 6;

  const totGiornaliero = giorni.map((g) => {
    const dateStr = `${anno}-${String(mese+1).padStart(2,"0")}-${String(g).padStart(2,"0")}`;
    return oreData.filter((o) => o.data === dateStr && o.ore > 0).reduce((s, o) => s + o.ore, 0);
  });

  const grandTotOreVere = oreData
    .filter((o) => o.data.startsWith(`${anno}-${String(mese+1).padStart(2,"0")}`) && o.ore > 0)
    .reduce((s, o) => s + o.ore, 0);

  const grandTotOreBusta = dipendenti.reduce((sum, dip) => {
    const prefix = `${anno}-${String(mese+1).padStart(2,"0")}`;
    const tot = oreData
      .filter((o) => {
        if (!(o.dipendenteId === dip.id && o.data.startsWith(prefix) && o.ore > 0)) return false;
        const giorno = parseInt(o.data.split("-")[2]);
        if (isSabatoG(giorno) || isDomenicaG(giorno)) return false;
        if (o.stato === "FS") return false;
        return true;
      })
      .reduce((s, o) => s + o.ore, 0);
    return sum + tot;
  }, 0);

  ws1.addRow(["", "TOTALE", "GIORNALIERO", "", "", ...totGiornaliero, grandTotOreVere, grandTotOreBusta, "", ""]);
  const totRow = ws1.getRow(totRowNum);
  totRow.height = 22;
  totRow.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = xlFill(XL_RED_DARK);
    cell.font = xlFont(true, XL_WHITE, 10);
    cell.alignment = xlAlign("center");
    cell.border = xlBorder();
  });
  // Colore giallo per i numeri giornalieri
  giorni.forEach((g, i) => {
    const cell = totRow.getCell(6 + i);
    if (totGiornaliero[i] > 0) {
      cell.font = xlFont(true, "FBBF24", 10);
    }
  });
  // Totale ore vere in blu
  totRow.getCell(6 + giorniMese).font = xlFont(true, "60A5FA", 11);
  // Totale ore busta in verde
  totRow.getCell(7 + giorniMese).font = xlFont(true, "34D399", 11);

  // Larghezze
  ws1.getColumn(1).width = 4; ws1.getColumn(2).width = 14; ws1.getColumn(3).width = 16;
  ws1.getColumn(4).width = 14; ws1.getColumn(5).width = 3;
  giorni.forEach((_, i) => { ws1.getColumn(6+i).width = 5; });
  ws1.getColumn(6+giorniMese).width = 10; ws1.getColumn(7+giorniMese).width = 10;
  ws1.getColumn(8+giorniMese).width = 8; ws1.getColumn(9+giorniMese).width = 22;

  await downloadWorkbook(wb, `ore_${nomeMs.toLowerCase()}_${anno}.xlsx`);
}

// ============================================================================
// Scheda dipendente espansa — certificazioni, PdS, qualifica
// ============================================================================

const CERT_LABELS = [
  { key: "form_gen", label: "Formazione Gen. (5a)", linked: "form_spec" },
  { key: "form_spec", label: "Formazione Spec. (5a)", hidden: true },
  { key: "antincendio", label: "Antincendio/Emergenza (3a)" },
  { key: "primo_soccorso", label: "Primo Soccorso (3a)" },
  { key: "piattaforma", label: "Piattaforma (5a)" },
  { key: "rls", label: "RLS (1a)" },
  { key: "escavatore", label: "Escavatore" },
  { key: "gru", label: "Gru" },
  { key: "lav_quota", label: "Lavori in Quota (5a)" },
  { key: "preposto", label: "Preposto (2a)" },
  { key: "visita_medica", label: "🩺 Scadenza Visita" },
];

// Auto-formatta input testo ora: "1030" → "10:30"
function autoTime(raw) {
  const d = raw.replace(/\D/g, "").substring(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0,2)}:${d.slice(2)}`;
}

// Auto-formatta input testo data: "31032026" → "31/03/2026"
function autoDate(raw) {
  const d = raw.replace(/\D/g, "").substring(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0,2)}/${d.slice(2)}`;
  return `${d.slice(0,2)}/${d.slice(2,4)}/${d.slice(4)}`;
}
// YYYY-MM-DD → DD/MM/YYYY per la visualizzazione nei campi testo
function yyyymmddToDisplay(v) {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split("-");
    return `${d}/${m}/${y}`;
  }
  return v;
}
// DD/MM/YYYY → YYYY-MM-DD per il salvataggio (usato anche da AutomezziPage)
function normDate(v) {
  if (!v || v === "") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
  return v;
}

function scadColor(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
  if (diff < 0) return "var(--danger)";
  if (diff < 30) return "#f59e0b";
  return "var(--accent)";
}

function SchedaDipendenteSection({ dipId, scheda, saveSchedaDipendente, visiteProgrammate = {}, saveVisiteProgrammate, formazioneProgrammate = {}, saveFormazioneProgrammate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [visitaForm, setVisitaForm] = useState({ data: "", ora: "" });
  const [formazioneForm, setFormazioneForm] = useState({ data: "", ora: "" });

  const schedaDateFields = ["data_assunzione","scad_contratto","scad_pds","ricevuta_rinnovo_pds","visita_medica","form_gen","form_spec","antincendio","primo_soccorso","piattaforma","rls","escavatore","gru","lav_quota","preposto"];

  useEffect(() => {
    const converted = {};
    Object.entries(scheda).forEach(([k, v]) => {
      converted[k] = schedaDateFields.includes(k) && v ? yyyymmddToDisplay(v) : v;
    });
    setForm(converted);
    setEditing(false);
    const vp = visiteProgrammate[dipId] || {};
    setVisitaForm({ data: vp.data ? yyyymmddToDisplay(vp.data) : "", ora: vp.ora || "" });
    const fp = formazioneProgrammate[dipId] || {};
    setFormazioneForm({ data: fp.data ? yyyymmddToDisplay(fp.data) : "", ora: fp.ora || "" });
  }, [dipId, scheda]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Converte DD/MM/YYYY → YYYY-MM-DD, ritorna il valore originale se già YYYY-MM-DD o null se vuoto
  const normalizeDate = (v) => {
    if (!v || v === "") return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // già YYYY-MM-DD
    const m = v.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
    if (m) return `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`;
    return v; // lascia com'è
  };

  const save = async () => {
    const cleaned = {};
    const dateFields = ["data_assunzione","scad_contratto","scad_pds","ricevuta_rinnovo_pds","visita_medica","form_gen","form_spec","antincendio","primo_soccorso","piattaforma","rls","escavatore","gru","lav_quota","preposto"];
    Object.entries(form).forEach(([k, v]) => {
      if (v === "" || v === undefined) { cleaned[k] = null; return; }
      cleaned[k] = dateFields.includes(k) ? normalizeDate(v) : v;
    });
    cleaned.form_spec = cleaned.form_gen || null;
    await saveSchedaDipendente(dipId, cleaned);
    // Salva visita programmata separatamente
    if (saveVisiteProgrammate) {
      const normData = normalizeDate(visitaForm.data);
      const next = { ...visiteProgrammate };
      if (normData || visitaForm.ora) {
        next[dipId] = { data: normData || null, ora: visitaForm.ora || null };
      } else {
        delete next[dipId];
      }
      await saveVisiteProgrammate(next);
    }
    // Salva formazione programmata separatamente
    if (saveFormazioneProgrammate) {
      const normData = normalizeDate(formazioneForm.data);
      const next = { ...formazioneProgrammate };
      if (normData || formazioneForm.ora) {
        next[dipId] = { data: normData || null, ora: formazioneForm.ora || null };
      } else {
        delete next[dipId];
      }
      await saveFormazioneProgrammate(next);
    }
    setEditing(false);
  };

  const fieldStyle = { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", color: "var(--text)", fontSize: 14, outline: "none", fontFamily: "var(--font)", width: "100%" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Scheda dipendente</p>
        <button onClick={() => editing ? save() : setEditing(true)} style={{ background: editing ? "var(--accent)" : "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 16px", cursor: "pointer", color: editing ? "#fff" : "var(--text-muted)", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          {editing ? <><Check size={14} /> Salva</> : <><Edit3 size={14} /> Modifica</>}
        </button>
      </div>

      <div style={{ background: "var(--surface-light)", borderRadius: 12, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Qualifica + Tipo contratto + Data assunzione */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Qualifica</p>
            {editing ? <input value={form.qualifica || ""} onChange={(e) => set("qualifica", e.target.value)} style={fieldStyle} /> : <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 600 }}>{scheda.qualifica || "—"}</p>}
          </div>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Tipo contratto</p>
            {editing ? (
              <select value={form.tipo_contratto || ""} onChange={(e) => set("tipo_contratto", e.target.value)} style={{ ...fieldStyle, appearance: "auto" }}>
                <option value="">—</option>
                <option value="INDET.">INDET.</option>
                <option value="DET.">DET.</option>
                <option value="APP.INDET">APP.INDET</option>
                <option value="APPRENDISTATO">APPRENDISTATO</option>
              </select>
            ) : <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 600 }}>{scheda.tipo_contratto || "—"}</p>}
          </div>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Data assunzione</p>
            {editing ? <input type="text" placeholder="GG/MM/AAAA" value={form.data_assunzione || ""} onChange={(e) => set("data_assunzione", autoDate(e.target.value))} style={fieldStyle} /> : <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 600 }}>{scheda.data_assunzione ? new Date(scheda.data_assunzione).toLocaleDateString("it-IT") : "—"}</p>}
          </div>
        </div>

        {/* Scadenza contratto + PdS */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Scad. contratto</p>
            {editing ? <input type="text" placeholder="GG/MM/AAAA" value={form.scad_contratto || ""} onChange={(e) => set("scad_contratto", autoDate(e.target.value))} style={fieldStyle} /> : <p style={{ color: scadColor(scheda.scad_contratto) || "var(--text)", fontSize: 15, fontWeight: 700 }}>{scheda.scad_contratto ? new Date(scheda.scad_contratto).toLocaleDateString("it-IT") : "Indeterminato"}</p>}
          </div>
          <div>
            <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Scad. PdS</p>
            {editing ? <input type="text" placeholder="GG/MM/AAAA" value={form.scad_pds || ""} onChange={(e) => set("scad_pds", autoDate(e.target.value))} style={fieldStyle} /> : <p style={{ color: scadColor(scheda.scad_pds) || "var(--text)", fontSize: 15, fontWeight: 700 }}>{scheda.scad_pds ? new Date(scheda.scad_pds).toLocaleDateString("it-IT") : "Illimitato"}</p>}
          </div>
        </div>

        {/* Ricevuta rinnovo PdS */}
        {(editing || scheda.ricevuta_rinnovo_pds || scheda.note_pds) && (
          <div style={{ background: scheda.ricevuta_rinnovo_pds ? "rgba(255,192,0,0.12)" : "transparent", borderRadius: 8, padding: scheda.ricevuta_rinnovo_pds ? "12px 14px" : 0, border: scheda.ricevuta_rinnovo_pds ? "1px solid rgba(255,192,0,0.3)" : "none" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <p style={{ color: "#FFC000", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 700 }}>Ricevuta rinnovo PdS</p>
                {editing ? <input type="text" placeholder="GG/MM/AAAA" value={form.ricevuta_rinnovo_pds || ""} onChange={(e) => set("ricevuta_rinnovo_pds", autoDate(e.target.value))} style={fieldStyle} /> : <p style={{ color: "#FFC000", fontSize: 15, fontWeight: 700 }}>{scheda.ricevuta_rinnovo_pds ? new Date(scheda.ricevuta_rinnovo_pds).toLocaleDateString("it-IT") : "—"}</p>}
              </div>
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Note PdS</p>
                {editing ? <input value={form.note_pds || ""} onChange={(e) => set("note_pds", e.target.value)} style={fieldStyle} /> : <p style={{ color: "var(--text)", fontSize: 14 }}>{scheda.note_pds || "—"}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Certificazioni */}
        <div>
          <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", marginBottom: 12, fontWeight: 700 }}>Certificazioni e scadenze</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            {CERT_LABELS.filter((c) => !c.hidden).map(({ key, label, linked, viewOnly }) => {
              const val = scheda[key];
              const hasIt = val !== null && val !== undefined;
              const okColor = key === "visita_medica" ? "#06b6d4" : "var(--accent)";
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  {editing && !viewOnly ? (
                    <>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 3, fontWeight: 600 }}>{label}</p>
                        <input type="text" placeholder="GG/MM/AAAA" value={form[key] || ""} onChange={(e) => { const v = autoDate(e.target.value); set(key, v || null); if (linked) set(linked, v || null); }} style={{ ...fieldStyle, width: "100%" }} />
                      </div>
                      <button onClick={() => { set(key, null); if (linked) set(linked, null); }} style={{ background: "none", border: "none", color: "var(--text-dim)", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", marginTop: 16 }}>✕</button>
                    </>
                  ) : (
                    <>
                      <span style={{ width: 12, height: 12, borderRadius: "50%", background: hasIt ? (scadColor(val) || okColor) : "var(--text-dim)", flexShrink: 0, opacity: hasIt ? 1 : 0.3 }} />
                      <span style={{ fontSize: 15, color: "var(--text-muted)", flex: 1 }}>{label}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: hasIt ? (scadColor(val) || (key === "visita_medica" ? "#06b6d4" : "var(--text)")) : "var(--text-dim)" }}>
                        {hasIt ? new Date(val).toLocaleDateString("it-IT") : "N/P"}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Prossima Visita Medica — appuntamento programmato (separato dalla scadenza certificato) */}
        <div style={{ background: "rgba(6,182,212,0.08)", borderRadius: 10, padding: "14px 16px", border: "1px solid rgba(6,182,212,0.2)" }}>
          <p style={{ color: "#06b6d4", fontSize: 12, textTransform: "uppercase", marginBottom: 8, fontWeight: 700, letterSpacing: 0.5 }}>🩺 Prossima visita medica</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: 11, marginBottom: 4, fontWeight: 600 }}>Data appuntamento</p>
              {editing ? (
                <input type="text" placeholder="GG/MM/AAAA" value={visitaForm.data} onChange={(e) => setVisitaForm((p) => ({ ...p, data: autoDate(e.target.value) }))} style={fieldStyle} />
              ) : (
                <p style={{ color: visiteProgrammate[dipId]?.data ? "#06b6d4" : "var(--text-dim)", fontSize: 15, fontWeight: 700 }}>
                  {visiteProgrammate[dipId]?.data ? new Date(visiteProgrammate[dipId].data).toLocaleDateString("it-IT") : "Da programmare"}
                </p>
              )}
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: 11, marginBottom: 4, fontWeight: 600 }}>Orario</p>
              {editing ? (
                <input type="text" placeholder="HH:MM" value={visitaForm.ora} onChange={(e) => setVisitaForm((p) => ({ ...p, ora: autoTime(e.target.value) }))} style={fieldStyle} />
              ) : (
                <p style={{ color: visiteProgrammate[dipId]?.ora ? "#06b6d4" : "var(--text-dim)", fontSize: 15, fontWeight: 700 }}>
                  {visiteProgrammate[dipId]?.ora || "—"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Prossima Formazione */}
        <div style={{ background: "rgba(245,158,11,0.08)", borderRadius: 10, padding: "14px 16px", border: "1px solid rgba(245,158,11,0.2)" }}>
          <p style={{ color: "#f59e0b", fontSize: 12, textTransform: "uppercase", marginBottom: 8, fontWeight: 700, letterSpacing: 0.5 }}>📋 Prossima formazione</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: 11, marginBottom: 4, fontWeight: 600 }}>Data</p>
              {editing ? (
                <input type="text" placeholder="GG/MM/AAAA" value={formazioneForm.data} onChange={(e) => setFormazioneForm((p) => ({ ...p, data: autoDate(e.target.value) }))} style={fieldStyle} />
              ) : (
                <p style={{ color: formazioneProgrammate[dipId]?.data ? "#f59e0b" : "var(--text-dim)", fontSize: 15, fontWeight: 700 }}>
                  {formazioneProgrammate[dipId]?.data ? new Date(formazioneProgrammate[dipId].data).toLocaleDateString("it-IT") : "Da programmare"}
                </p>
              )}
            </div>
            <div>
              <p style={{ color: "var(--text-muted)", fontSize: 11, marginBottom: 4, fontWeight: 600 }}>Orario</p>
              {editing ? (
                <input type="text" placeholder="HH:MM" value={formazioneForm.ora} onChange={(e) => setFormazioneForm((p) => ({ ...p, ora: autoTime(e.target.value) }))} style={fieldStyle} />
              ) : (
                <p style={{ color: formazioneProgrammate[dipId]?.ora ? "#f59e0b" : "var(--text-dim)", fontSize: 15, fontWeight: 700 }}>
                  {formazioneProgrammate[dipId]?.ora || "—"}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Costo orario */}
        <div>
          <p style={{ color: "var(--text-muted)", fontSize: 12, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Costo orario</p>
          {editing ? <input type="number" step="0.01" value={form.costo_orario || ""} onChange={(e) => set("costo_orario", e.target.value ? parseFloat(e.target.value) : null)} style={{ ...fieldStyle, width: 120 }} placeholder="€/h" /> : <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 600 }}>{scheda.costo_orario ? `€ ${scheda.costo_orario.toFixed(2)}` : "Da inserire"}</p>}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Pagina Scadenze — tutte le scadenze urgenti dipendenti + automezzi
// ============================================================================

function ScadenzePage({ dipendenti = [], schedaDipendenti = {}, automezzi = [], newKeys = [], setPage, onOpenDipendente = () => {}, onOpenAutomezzo = () => {} }) {
  const [scadenzeStati, saveScadenzeStati] = useAppSettings("scadenzeStati", {});
  const [showEseguiti, setShowEseguiti] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState(null); // null = tutte, "scadenza" | "visita" | "automezzo"

  // Cronologia operazioni — sincronizzata via Supabase, auto-elimina dopo 7 giorni
  const [scadenzeHistoryRaw, saveScadenzeHistory] = useAppSettings("scadenzeHistory", () => {
    try {
      const saved = JSON.parse(localStorage.getItem("scadenzeHistory") || "[]");
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return saved.filter(e => e.timestamp > cutoff);
    } catch { return []; }
  });
  // Filter stale entries on read
  const scadenzeHistory = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return (scadenzeHistoryRaw || []).filter(e => e.timestamp > cutoff);
  }, [scadenzeHistoryRaw]);
  const [showHistory, setShowHistory] = useState(false);

  const addHistory = (a) => {
    const next = [{ id: Date.now(), key: a.key, nome: a.nome, label: a.label, tipo: a.tipo, timestamp: Date.now() }, ...scadenzeHistory].slice(0, 50);
    saveScadenzeHistory(next);
  };

  const undoHistory = (entry) => {
    saveScadenzeStati({ ...scadenzeStati, [entry.key]: null });
    const next = scadenzeHistory.filter(e => e.id !== entry.id);
    saveScadenzeHistory(next);
  };
  const alerts = useMemo(() => {
    const list = [];
    const today = new Date();

    dipendenti.forEach((d) => {
      const s = schedaDipendenti[d.id];
      if (!s) return;

      const checkDip = (dateStr, label, preavvisoDays, categoria) => {
        if (!dateStr) return;
        const dt = new Date(dateStr);
        const diff = (dt - today) / (1000 * 60 * 60 * 24);
        if (diff > preavvisoDays) return;
        list.push({
          tipo: "dipendente",
          nome: d.name,
          dipId: d.id,
          key: `dip-${d.id}-${label}`,
          label,
          categoria,
          data: dt,
          diff: Math.round(diff),
          color: diff < 0 ? "var(--danger)" : "#f59e0b",
          scaduto: diff < 0,
        });
      };

      // 30 giorni preavviso — scadenze (rosso)
      checkDip(s.form_gen, "Formazione Gen. + Spec.", 30, "scadenza");
      checkDip(s.antincendio, "Antincendio/Emergenza", 30, "scadenza");
      checkDip(s.primo_soccorso, "Primo Soccorso", 30, "scadenza");
      checkDip(s.piattaforma, "Piattaforma", 30, "scadenza");
      checkDip(s.rls, "RLS", 30, "scadenza");
      checkDip(s.escavatore, "Escavatore", 30, "scadenza");
      checkDip(s.gru, "Gru", 30, "scadenza");
      checkDip(s.lav_quota, "Lavori in Quota", 30, "scadenza");
      checkDip(s.preposto, "Preposto", 30, "scadenza");
      checkDip(s.scad_pds, "Permesso di Soggiorno", 30, "scadenza");
      checkDip(s.scad_contratto, "Scadenza Contratto", 30, "contratto");

      // 30 giorni preavviso — visita medica
      checkDip(s.visita_medica, "Visita Medica", 30, "visita");
    });

    automezzi.forEach((a) => {
      const checkAuto = (dateStr, label) => {
        if (!dateStr) return;
        const dt = new Date(dateStr);
        const diff = (dt - today) / (1000 * 60 * 60 * 24);
        if (diff > 30) return;
        list.push({
          tipo: "automezzo",
          nome: `${a.modello} (${a.targa})`,
          autoId: a.id,
          key: `auto-${a.id}-${label}`,
          label,
          categoria: "automezzo",
          data: dt,
          diff: Math.round(diff),
          color: diff < 0 ? "var(--danger)" : "#f59e0b",
          scaduto: diff < 0,
        });
      };

      checkAuto(a.scad_assicurazione, "Assicurazione");
      checkAuto(a.scad_revisione, "Revisione");
      checkAuto(a.scad_tagliando, "Tagliando");

      // Bollo: mese testuale → scadenza fine mese (alert 45 giorni prima)
      if (a.mese_bollo) {
        const mesi = { gennaio:0, febbraio:1, marzo:2, aprile:3, maggio:4, giugno:5, luglio:6, agosto:7, settembre:8, ottobre:9, novembre:10, dicembre:11 };
        const meseIdx = mesi[a.mese_bollo.toLowerCase().trim()];
        if (meseIdx !== undefined) {
          const anno = today.getFullYear();
          const scadBollo = new Date(anno, meseIdx + 1, 0);
          if (scadBollo < today) scadBollo.setFullYear(anno + 1); // se già passato, anno prossimo
          const diff = (scadBollo - today) / (1000 * 60 * 60 * 24);
          if (diff <= 45) {
            list.push({
              tipo: "automezzo", nome: `${a.modello} (${a.targa})`, autoId: a.id,
              key: `auto-${a.id}-Bollo`, label: `Bollo (${a.mese_bollo})`, categoria: "automezzo",
              data: scadBollo, diff: Math.round(diff),
              color: diff < 0 ? "var(--danger)" : "#f59e0b", scaduto: diff < 0,
            });
          }
        }
      }
    });

    list.sort((a, b) => a.diff - b.diff);
    return list;
  }, [dipendenti, schedaDipendenti, automezzi]);

  const setStato = (key, stato) => {
    saveScadenzeStati({ ...scadenzeStati, [key]: stato });
  };

  const alertsFiltrati = filtroCategoria ? alerts.filter((a) => a.categoria === filtroCategoria) : alerts;
  const alertsAttivi = alertsFiltrati.filter((a) => scadenzeStati[a.key] !== "eseguito");
  const alertsEseguiti = alertsFiltrati.filter((a) => scadenzeStati[a.key] === "eseguito");

  // Colori per categoria (come leggenda calendario)
  const categoriaColor = { scadenza: "#ef4444", contratto: "#f59e0b", visita: "#8b5cf6", automezzo: "#06b6d4" };

  const renderAlert = (a, attenuato = false) => {
    const stato = scadenzeStati[a.key];
    const isVisto = stato === "visualizzato";
    const dotColor = categoriaColor[a.categoria] || "#ef4444";
    const isNew = !attenuato && newKeys.includes(a.key);
    return (
      <div key={a.key} style={{
        background: attenuato ? "rgba(255,255,255,0.02)" : (isNew ? "rgba(245,158,11,0.07)" : "var(--surface)"),
        borderRadius: "var(--radius-md)", padding: "16px 20px",
        border: attenuato ? "1px solid var(--border)" : (isNew ? "1px solid rgba(245,158,11,0.55)" : `1px solid ${a.scaduto ? "rgba(239,68,68,0.3)" : "rgba(245,158,11,0.3)"}`),
        display: "flex", alignItems: "center", gap: 16,
        cursor: "pointer", opacity: attenuato ? 0.5 : 1,
        transition: "opacity 0.2s",
        boxShadow: isNew ? "0 0 16px rgba(245,158,11,0.12)" : "none",
      }}
        onClick={() => {
          if (a.tipo === "dipendente") onOpenDipendente(a.dipId);
          else onOpenAutomezzo(a.autoId);
        }}>
        <div style={{ width: 12, height: 12, borderRadius: "50%", background: attenuato ? "var(--text-dim)" : dotColor, flexShrink: 0, boxShadow: attenuato ? "none" : `0 0 8px ${dotColor}66` }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: attenuato ? "var(--text-dim)" : "var(--text)", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {a.tipo === "dipendente" ? "👤" : "🚛"} {a.nome}
            {isNew && (
              <span style={{ background: "#f59e0b", color: "#000", borderRadius: 6, padding: "1px 8px", fontSize: 11, fontWeight: 800, letterSpacing: "0.3px" }}>NUOVA</span>
            )}
          </p>
          <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 3 }}>{a.label}</p>
        </div>
        <div style={{ textAlign: "right", marginRight: 8 }}>
          <p style={{ color: attenuato ? "var(--text-dim)" : a.color, fontSize: 16, fontWeight: 700 }}>
            {a.data.toLocaleDateString("it-IT")}
          </p>
          <p style={{ color: attenuato ? "var(--text-dim)" : a.color, fontSize: 13, fontWeight: 600 }}>
            {a.scaduto ? `Scaduto da ${Math.abs(a.diff)} gg` : a.diff === 0 ? "Scade oggi!" : `Tra ${a.diff} gg`}
          </p>
        </div>
        {!attenuato && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setStato(a.key, isVisto ? null : "visualizzato")}
              title="Visualizzato"
              style={{
                padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: isVisto ? "1px solid rgba(59,130,246,0.4)" : "1px solid var(--border)",
                background: isVisto ? "rgba(59,130,246,0.15)" : "var(--surface-light)",
                color: isVisto ? "#60a5fa" : "var(--text-muted)",
                fontFamily: "var(--font)",
              }}
            >Visto</button>
            <button
              onClick={() => { setStato(a.key, "eseguito"); addHistory(a); }}
              title="Eseguito"
              style={{
                padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: "1px solid rgba(16,185,129,0.3)",
                background: "rgba(16,185,129,0.1)",
                color: "#10b981",
                fontFamily: "var(--font)",
              }}
            >Fatto</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>
            Scadenze <span style={{ color: alertsAttivi.length > 0 ? "var(--danger)" : "var(--accent)", fontSize: 18 }}>({alertsAttivi.length})</span>
          </h1>
          <select
            value={filtroCategoria || ""}
            onChange={(e) => setFiltroCategoria(e.target.value || null)}
            style={{
              padding: "7px 14px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: filtroCategoria ? "rgba(192,57,43,0.10)" : "var(--surface)",
              border: filtroCategoria ? "1px solid rgba(192,57,43,0.35)" : "1px solid var(--border)",
              color: "var(--text)", cursor: "pointer", fontFamily: "var(--font)",
              outline: "none",
            }}
          >
            <option value="">Tutte</option>
            <option value="scadenza">Scadenze</option>
            <option value="contratto">Contratti</option>
            <option value="visita">Visite mediche</option>
            <option value="automezzo">Automezzi</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
          {[["#ef4444","Scadenza"],["#f59e0b","Contratto"],["#8b5cf6","Visita medica"],["#06b6d4","Automezzo"]].map(([c,l]) => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ width: 11, height: 11, borderRadius: "50%", background: c, display: "inline-block" }} />
              <span style={{ color: "var(--text-muted)", fontSize: 14, fontWeight: 500 }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cronologia operazioni */}
      <div style={{ marginBottom: 20, background: "var(--surface)", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", overflow: "hidden" }}>
        <button onClick={() => setShowHistory(h => !h)} style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "12px 16px", background: "none", border: "none", cursor: "pointer",
          fontFamily: "var(--font)",
        }}>
          <span style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            📋 Cronologia operazioni
          </span>
          <span style={{ color: "var(--text-dim)", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
            {scadenzeHistory.length > 0 && <span style={{ background: "rgba(16,185,129,0.15)", color: "#10b981", borderRadius: 10, padding: "2px 8px", fontWeight: 700 }}>{scadenzeHistory.length}</span>}
            {showHistory ? "▲ Nascondi" : "▼ Mostra"}
          </span>
        </button>
        {showHistory && (
          <div style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
            {scadenzeHistory.length === 0 ? (
              <p style={{ color: "var(--text-dim)", fontSize: 13, textAlign: "center", padding: "16px 0" }}>Nessuna operazione registrata</p>
            ) : scadenzeHistory.map(entry => {
              const data = new Date(entry.timestamp);
              const giorni = Math.floor((Date.now() - entry.timestamp) / (1000 * 60 * 60 * 24));
              return (
                <div key={entry.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <div>
                    <p style={{ color: "var(--text)", fontSize: 13, fontWeight: 600 }}>
                      {entry.tipo === "dipendente" ? "👤" : "🚛"} {entry.nome} — <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>{entry.label}</span>
                    </p>
                    <p style={{ color: "var(--text-dim)", fontSize: 11, marginTop: 2 }}>
                      Contrassegnato come "Fatto" · {giorni === 0 ? "oggi" : `${giorni} ${giorni === 1 ? "giorno" : "giorni"} fa`} · si elimina dopo 7 giorni
                    </p>
                  </div>
                  <button onClick={() => undoHistory(entry)} style={{
                    background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)",
                    borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600,
                    color: "#f59e0b", cursor: "pointer", fontFamily: "var(--font)", whiteSpace: "nowrap",
                  }}>↩ Ripristina</button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {alertsAttivi.length === 0 && alertsEseguiti.length === 0 ? (
        <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 40, border: "1px solid var(--border)", textAlign: "center" }}>
          <p style={{ color: "var(--accent)", fontSize: 18, fontWeight: 600 }}>Nessuna scadenza urgente</p>
          <p style={{ color: "var(--text-dim)", fontSize: 14, marginTop: 6 }}>Tutte le certificazioni e scadenze sono in regola.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {alertsAttivi.map((a) => renderAlert(a))}

          {alertsEseguiti.length > 0 && (
            <>
              <button onClick={() => setShowEseguiti(!showEseguiti)} style={{
                background: "none", border: "none", color: "var(--text-dim)", fontSize: 14,
                cursor: "pointer", padding: "12px 0", fontFamily: "var(--font)", fontWeight: 600,
                textAlign: "left",
              }}>
                {showEseguiti ? "▼" : "▶"} {alertsEseguiti.length} scadenz{alertsEseguiti.length === 1 ? "a" : "e"} completat{alertsEseguiti.length === 1 ? "a" : "e"}
              </button>
              {showEseguiti && alertsEseguiti.map((a) => (
                <div key={a.key} style={{ position: "relative" }}>
                  {renderAlert(a, true)}
                  <button onClick={() => setStato(a.key, null)} style={{
                    position: "absolute", top: 8, right: 8, background: "var(--surface-light)",
                    border: "1px solid var(--border)", borderRadius: 6, padding: "3px 8px",
                    fontSize: 10, color: "var(--text-muted)", cursor: "pointer", fontFamily: "var(--font)",
                  }}>Ripristina</button>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

// ============================================================================
// Pagina Automezzi — lista veicoli con modifica e alert
// ============================================================================

function AutomezziPage({ automezzi = [], saveAutomezzo, addAutomezzo, deleteAutomezzo, openAutoId = null, clearOpenAutoId = () => {} }) {
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({});
  const [highlightId, setHighlightId] = useState(null);
  const [confirmDeleteAuto, setConfirmDeleteAuto] = useState(null);
  const [archivedAuto, saveArchivedAuto] = useAppSettings("archivedAutomezzi", () => {
    try { return JSON.parse(localStorage.getItem("archivedAutomezzi") || "[]"); } catch { return []; }
  });
  const setArchivedAuto = (updater) => {
    const next = typeof updater === "function" ? updater(archivedAuto) : updater;
    saveArchivedAuto(next);
  };
  const [autoActionHistory, saveAutoActionHistory] = useAppSettings("autoActionHistory", () => {
    try { return JSON.parse(localStorage.getItem("autoActionHistory") || "[]"); } catch { return []; }
  });
  const setAutoActionHistory = (updater) => {
    const next = typeof updater === "function" ? updater(autoActionHistory) : updater;
    saveAutoActionHistory(next);
  };
  const [showAutoCronologia, setShowAutoCronologia] = useState(false);

  function handleDeleteAutomezzo(a) {
    deleteAutomezzo(a.id);
    const archived = { ...a, archivedAt: new Date().toISOString() };
    setArchivedAuto((prev) => [archived, ...prev]);
    const action = { id: Date.now(), type: "delete_automezzo", label: `Eliminato: ${a.modello} (${a.targa})`, autoData: a, timestamp: Date.now() };
    setAutoActionHistory((prev) => [action, ...prev]);
    setConfirmDeleteAuto(null);
  }

  async function handleRestoreAutomezzo(a) {
    const { archivedAt, ...restored } = a;
    await addAutomezzo(restored);
    setArchivedAuto((prev) => prev.filter((x) => !(x.id === a.id && x.archivedAt === a.archivedAt)));
    const action = { id: Date.now(), type: "restore_automezzo", label: `Ripristinato: ${a.modello} (${a.targa})`, autoData: restored, timestamp: Date.now() };
    setAutoActionHistory((prev) => [action, ...prev]);
  }

  // Auto-open automezzo from Scadenze navigation
  useEffect(() => {
    if (openAutoId) {
      setHighlightId(openAutoId);
      clearOpenAutoId();
      const el = document.getElementById(`automezzo-${openAutoId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setHighlightId(null), 3000);
    }
  }, [openAutoId]);

  const autoDateKeys = ["scad_assicurazione", "scad_revisione", "scad_tagliando"];
  const startEdit = (a) => {
    const f = { ...a };
    if (f.mese_bollo) f.mese_bollo = f.mese_bollo.charAt(0).toUpperCase() + f.mese_bollo.slice(1).toLowerCase();
    // Converti date YYYY-MM-DD → DD/MM/YYYY per i campi testo
    autoDateKeys.forEach(k => { if (f[k]) f[k] = yyyymmddToDisplay(f[k]); });
    setEditId(a.id); setForm(f);
  };
  const cancelEdit = () => { setEditId(null); setForm({}); };
  const saveEdit = async () => {
    const cleaned = { ...form };
    // Normalizza date DD/MM/YYYY → YYYY-MM-DD prima del salvataggio
    autoDateKeys.forEach(k => { if (cleaned[k]) cleaned[k] = normDate(cleaned[k]); });
    await saveAutomezzo(editId, cleaned);
    setEditId(null);
  };
  const handleAdd = async () => { await addAutomezzo(newForm); setShowAdd(false); setNewForm({}); };

  const fieldStyle = { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 8px", color: "var(--text)", fontSize: 12, outline: "none", fontFamily: "var(--font)", width: "100%" };

  const downloadExcel = async () => {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = "Mattone Rosso";
    const ws = wb.addWorksheet("AUTOMEZZI");

    const COLS = [
      { header: "N.",               key: "n",    width: 5  },
      { header: "PROPRIETARIO",     key: "prop", width: 13 },
      { header: "MODELLO",          key: "mod",  width: 20 },
      { header: "TARGA",            key: "tar",  width: 12 },
      { header: "TIPO",             key: "tipo", width: 14 },
      { header: "ASSICURATORE",     key: "ass",  width: 16 },
      { header: "SCAD. ASSIC.",     key: "sassic", width: 16 },
      { header: "MESE BOLLO",       key: "bollo", width: 13 },
      { header: "SCAD. REVISIONE",  key: "srev",  width: 16 },
      { header: "SCAD. TAGLIANDO",  key: "stag",  width: 16 },
      { header: "NOTE",             key: "note",  width: 28 },
    ];
    ws.columns = COLS.map(c => ({ width: c.width }));

    // Riga 1 — titolo
    ws.mergeCells(1, 1, 1, COLS.length);
    const titleCell = ws.getCell("A1");
    titleCell.value = "MATTONE ROSSO — VEICOLI AZIENDALI";
    titleCell.font = xlFont(true, XL_WHITE, 14);
    titleCell.fill = xlFill(XL_RED);
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.border = xlBorder();
    ws.getRow(1).height = 30;

    // Riga 2 — intestazioni
    const hRow = ws.getRow(2);
    hRow.height = 22;
    COLS.forEach((c, ci) => {
      const cell = hRow.getCell(ci + 1);
      cell.value = c.header;
      cell.font = xlFont(true, XL_WHITE, 10);
      cell.fill = xlFill("96281B");
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = xlBorder();
    });

    // Helper colore scadenza
    const scadXl = (dateStr) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
      if (diff < 0)  return { fg: "FDE8E8", text: "C0392B" }; // scaduta — rosso
      if (diff < 45) return { fg: "FFF8E8", text: "D97706" }; // in scadenza — arancione
      return null;
    };

    const fmtDate = (iso) => {
      if (!iso) return "";
      const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
    };

    // Righe dati
    automezzi.forEach((a, idx) => {
      const isEven = idx % 2 === 0;
      const bg = isEven ? XL_WHITE : XL_GRAY_LT;
      const rowNum = 3 + idx;
      const vals = [
        String(idx + 1),
        a.chi_paga || "",
        a.modello || "",
        a.targa || "",
        a.tipo_veicolo || "",
        a.broker || "",
        fmtDate(a.scad_assicurazione),
        a.mese_bollo || "",
        fmtDate(a.scad_revisione),
        fmtDate(a.scad_tagliando),
        a.note || "",
      ];
      const row = ws.getRow(rowNum);
      row.height = 18;
      vals.forEach((v, ci) => {
        const cell = row.getCell(ci + 1);
        cell.value = v;
        cell.border = xlBorder();
        cell.alignment = ci === 10 ? xlAlign("left") : xlAlign("center");
        // Colore scadenze (colonne 7, 9, 10 = indici 6, 8, 9)
        const scadKeys = [a.scad_assicurazione, null, null, null, null, null, a.scad_assicurazione, null, a.scad_revisione, a.scad_tagliando];
        const sc = [6, 8, 9].includes(ci) ? scadXl([a.scad_assicurazione, a.scad_revisione, a.scad_tagliando][[6,8,9].indexOf(ci)]) : null;
        if (sc) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF" + sc.fg } };
          cell.font = xlFont(true, sc.text, 10);
        } else {
          cell.fill = xlFill(bg);
          cell.font = xlFont(ci === 0, XL_TEXT, 10);
        }
      });
    });

    // Riga totale veicoli
    const totRow = ws.getRow(3 + automezzi.length);
    totRow.height = 20;
    ws.mergeCells(3 + automezzi.length, 1, 3 + automezzi.length, COLS.length);
    const totCell = totRow.getCell(1);
    totCell.value = `TOTALE VEICOLI: ${automezzi.length}`;
    totCell.font = xlFont(true, XL_WHITE, 11);
    totCell.fill = xlFill(XL_RED);
    totCell.alignment = { horizontal: "center", vertical: "middle" };
    totCell.border = xlBorder();

    ws.views = [{ state: "frozen", xSplit: 0, ySplit: 2 }];
    await downloadWorkbook(wb, "automezzi-mattone-rosso.xlsx");
  };

  const autoFields = [
    { key: "chi_paga", label: "Proprietario" },
    { key: "broker", label: "Assicuratore" },
    { key: "tipo_veicolo", label: "Tipo" },
    { key: "modello", label: "Modello" },
    { key: "targa", label: "Targa" },
    { key: "scad_assicurazione", label: "Scad. Assicurazione", type: "autodate" },
    { key: "mese_bollo", label: "Mese bollo", type: "select_mese" },
    { key: "scad_revisione", label: "Scad. Revisione", type: "autodate" },
    { key: "scad_tagliando", label: "Scad. Tagliando", type: "autodate" },
    { key: "note", label: "Note" },
  ];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>Automezzi <span style={{ color: "var(--primary)", fontSize: 18 }}>({automezzi.length})</span></h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={downloadExcel} style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "var(--surface-light)", color: "var(--text-muted)", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "var(--font)" }}>
            <FileSpreadsheet size={15} /> Excel
          </button>
          <button onClick={() => setShowAdd(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: "var(--radius-md)", border: "none", background: "linear-gradient(135deg, #C0392B, #96281B)", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "var(--font)" }}>
            <Plus size={16} /> Aggiungi veicolo
          </button>
        </div>
      </div>

      <div className="admin-auto-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 16 }}>
        {automezzi.map((a) => {
          const isEditing = editId === a.id;
          const assColor = scadColor(a.scad_assicurazione);
          const revColor = scadColor(a.scad_revisione);
          return (
            <div key={a.id} id={`automezzo-${a.id}`} style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 20, border: highlightId === a.id ? "2px solid var(--primary)" : "1px solid var(--border)", boxShadow: highlightId === a.id ? "0 0 20px rgba(99,102,241,0.3)" : "none", transition: "border-color 0.3s, box-shadow 0.3s" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: a.chi_paga === "PVT" ? "rgba(245,158,11,0.15)" : "rgba(192,57,43,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Truck size={20} color={a.chi_paga === "PVT" ? "#f59e0b" : "var(--primary)"} />
                  </div>
                  <div>
                    <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 700 }}>{a.modello}</p>
                    <p style={{ color: "var(--text-dim)", fontSize: 12 }}>{a.targa} · {a.chi_paga} · {a.broker}</p>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {isEditing ? (
                    <>
                      <button onClick={saveEdit} style={{ background: "var(--accent)", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", color: "#fff", fontSize: 11, fontWeight: 600 }}>Salva</button>
                      <button onClick={cancelEdit} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", color: "var(--text-muted)", fontSize: 11 }}>Annulla</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(a)} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "var(--text-muted)" }}><Edit3 size={13} /></button>
                      <button onClick={() => setConfirmDeleteAuto(a)} style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "var(--danger)" }}><X size={13} /></button>
                    </>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {autoFields.map(({ key, label, type }) => (
                    <div key={key}>
                      <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", marginBottom: 3 }}>{label}</p>
                      {type === "select_mese" ? (
                        <select value={form[key] || ""} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value || null }))} style={{ ...fieldStyle, appearance: "auto" }}>
                          <option value="">—</option>
                          {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      ) : type === "autodate" ? (
                        <input type="text" placeholder="GG/MM/AAAA" value={form[key] || ""} onChange={(e) => setForm((p) => ({ ...p, [key]: autoDate(e.target.value) || null }))} style={fieldStyle} />
                      ) : (
                        <input type="text" value={form[key] || ""} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value || null }))} style={fieldStyle} />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  <div>
                    <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase" }}>Assicurazione</p>
                    <p style={{ color: assColor || "var(--text)", fontSize: 13, fontWeight: 600 }}>{a.scad_assicurazione ? new Date(a.scad_assicurazione).toLocaleDateString("it-IT") : "—"}</p>
                  </div>
                  <div>
                    <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase" }}>Revisione</p>
                    <p style={{ color: revColor || "var(--text)", fontSize: 13, fontWeight: 600 }}>{a.scad_revisione ? new Date(a.scad_revisione).toLocaleDateString("it-IT") : "—"}</p>
                  </div>
                  <div>
                    <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase" }}>Bollo</p>
                    <p style={{ color: "var(--text)", fontSize: 13, fontWeight: 500 }}>{a.mese_bollo || "—"}</p>
                  </div>
                  {a.note && (
                    <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
                      <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase" }}>Note</p>
                      <p style={{ color: "var(--text)", fontSize: 12 }}>{a.note}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal aggiungi veicolo */}
      {showAdd && (
        <div onClick={() => setShowAdd(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 16, width: "100%", maxWidth: 500, padding: 24, border: "1px solid var(--border)" }}>
            <h3 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Nuovo veicolo</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {autoFields.map(({ key, label, type }) => (
                <div key={key}>
                  <p style={{ color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase", marginBottom: 3 }}>{label}</p>
                  {type === "select_mese" ? (
                    <select value={newForm[key] || ""} onChange={(e) => setNewForm((p) => ({ ...p, [key]: e.target.value || null }))} style={{ ...fieldStyle, appearance: "auto" }}>
                      <option value="">—</option>
                      {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ) : (
                    <input type={type || "text"} value={newForm[key] || ""} onChange={(e) => setNewForm((p) => ({ ...p, [key]: e.target.value || null }))} style={fieldStyle} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <button onClick={() => setShowAdd(false)} style={{ background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 16px", cursor: "pointer", color: "var(--text-muted)", fontSize: 13 }}>Annulla</button>
              <button onClick={handleAdd} style={{ background: "var(--primary)", border: "none", borderRadius: 8, padding: "8px 20px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}>Aggiungi</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== AUTOMEZZI ARCHIVIATI ===== */}
      {archivedAuto.length > 0 && (
        <div style={{ marginTop: 28, background: "var(--surface)", borderRadius: "var(--radius-md)", border: "1px solid rgba(239,68,68,0.2)", padding: 20 }}>
          <p style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            🗄 Automezzi Archiviati
            <span style={{ background: "rgba(239,68,68,0.15)", color: "var(--danger)", borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{archivedAuto.length}</span>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {archivedAuto.map((a, i) => (
              <div key={`${a.id}-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "rgba(239,68,68,0.07)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(239,68,68,0.15)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Truck size={16} style={{ color: "var(--text-dim)" }} />
                  <div>
                    <span style={{ color: "var(--text-muted)", fontSize: 14, textDecoration: "line-through" }}>{a.modello} — {a.targa}</span>
                    <p style={{ color: "var(--text-dim)", fontSize: 11 }}>{a.chi_paga} · Eliminato il {new Date(a.archivedAt).toLocaleDateString("it-IT")}</p>
                  </div>
                </div>
                <button onClick={() => handleRestoreAutomezzo(a)} style={{ padding: "7px 16px", borderRadius: "var(--radius-sm)", border: "1px solid var(--accent)", background: "transparent", color: "var(--accent)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 6 }}>↩ Ripristina</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== CRONOLOGIA OPERAZIONI AUTOMEZZI ===== */}
      <div style={{ marginTop: 20 }}>
        <button onClick={() => setShowAutoCronologia((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", color: "var(--text-muted)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)", width: "100%" }}>
          📋 Cronologia operazioni
          <span style={{ marginLeft: "auto", fontSize: 12 }}>{showAutoCronologia ? "▲ Chiudi" : `▼ Mostra (${autoActionHistory.length})`}</span>
        </button>
        {showAutoCronologia && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 var(--radius-md) var(--radius-md)", overflow: "hidden" }}>
            {autoActionHistory.length === 0 ? (
              <p style={{ color: "var(--text-dim)", fontSize: 13, padding: "20px", textAlign: "center" }}>Nessuna operazione registrata.</p>
            ) : (
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                {autoActionHistory.map((a) => {
                  const icons = { add_automezzo: "➕", delete_automezzo: "🗑", restore_automezzo: "↩" };
                  const ts = new Date(a.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                  return (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 16 }}>{icons[a.type] || "•"}</span>
                        <div>
                          <p style={{ color: "var(--text)", fontSize: 13, fontWeight: 500 }}>{a.label}</p>
                          <p style={{ color: "var(--text-dim)", fontSize: 11, marginTop: 2 }}>{ts}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== MODAL CONFERMA ELIMINAZIONE AUTOMEZZO ===== */}
      {confirmDeleteAuto && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setConfirmDeleteAuto(null)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
          <div style={{ position: "relative", background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 400, border: "1px solid var(--border)", zIndex: 1101, margin: "0 16px" }}>
            <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Elimina veicolo</h2>
            <p style={{ color: "var(--text-dim)", fontSize: 14, marginBottom: 8 }}>
              Sei sicuro di voler eliminare <strong style={{ color: "var(--text)" }}>{confirmDeleteAuto.modello}</strong> ({confirmDeleteAuto.targa})?
            </p>
            <p style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 24 }}>
              Potrai ripristinarlo dalla sezione "Automezzi Archiviati".
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmDeleteAuto(null)} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)" }}>Annulla</button>
              <button onClick={() => handleDeleteAutomezzo(confirmDeleteAuto)} style={{ flex: 1, padding: "11px", borderRadius: "var(--radius-md)", border: "none", background: "var(--danger)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font)" }}>Elimina</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Pagina Classi — gestione corsi e visite mediche programmate
// ============================================================================

const CLASSI_TIPI = [
  { value: "visita", label: "Visita medica", fields: ["visita_medica"], anni: 1, soglia: 30 },
  { value: "formazione", label: "Formazione generica + specifica", fields: ["form_gen", "form_spec"], anni: 5, soglia: 45 },
  { value: "antincendio", label: "Antincendio / emergenza", fields: ["antincendio"], anni: 3, soglia: 45 },
  { value: "primo_soccorso", label: "Primo soccorso", fields: ["primo_soccorso"], anni: 3, soglia: 45 },
  { value: "piattaforma", label: "Piattaforma aerea", fields: ["piattaforma"], anni: 5, soglia: 45 },
  { value: "rls", label: "RLS", fields: ["rls"], anni: 1, soglia: 45 },
  { value: "lav_quota", label: "Lavori in quota", fields: ["lav_quota"], anni: 5, soglia: 45 },
];

function ClassiPage({ dipendenti = [], schedaDipendenti = {}, visiteProgrammate = {}, saveVisiteProgrammate = () => {}, formazioneProgrammate = {}, saveFormazioneProgrammate = () => {}, classi = [], classiDipendenti = [], getDipendentiClasse = () => [], saveClasse = async () => {}, deleteClasse = async () => {}, addDipendenteClasse = async () => {}, removeDipendenteClasse = async () => {}, marcaCompletata = async () => {} }) {
  const [tab, setTab] = useState("suggerite");
  const [showForm, setShowForm] = useState(false);
  const [editClasse, setEditClasse] = useState(null);
  const [formTipo, setFormTipo] = useState("");
  const [formData, setFormData] = useState("");
  const [formOra, setFormOra] = useState("");
  const [formLuogo, setFormLuogo] = useState("");
  const [formFormatore, setFormFormatore] = useState("");
  const [formNote, setFormNote] = useState("");
  const [formDipSelezionati, setFormDipSelezionati] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(null);
  const [presentiCheck, setPresentiCheck] = useState({});
  const [notificaMsg, setNotificaMsg] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [expandedArchivio, setExpandedArchivio] = useState(null);

  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);

  function diffGiorni(dateStr) {
    if (!dateStr) return 9999;
    const d = new Date(dateStr);
    return Math.floor((d - oggi) / (1000 * 60 * 60 * 24));
  }

  function urgenzaColor(gg) {
    if (gg < 0) return { bg: "rgba(239,68,68,0.15)", text: "#ef4444", border: "rgba(239,68,68,0.4)", label: "Scaduto" };
    if (gg <= 30) return { bg: "rgba(245,158,11,0.15)", text: "#f59e0b", border: "rgba(245,158,11,0.4)", label: "Imminente" };
    return { bg: "rgba(96,165,250,0.15)", text: "#60a5fa", border: "rgba(96,165,250,0.4)", label: "Prossima" };
  }

  function urgenzaBorderColor(classe) {
    if (!classe.data) return "var(--border)";
    const gg = diffGiorni(classe.data);
    if (gg <= 15) return "#ef4444";
    if (gg <= 30) return "#f59e0b";
    return "var(--border)";
  }

  // ---- TAB 1: Suggerite — raggruppa dipendenti per scadenza imminente ----
  // Un dipendente scompare dal gruppo "tipo" se e' gia' iscritto a una classe
  // in programma (non completata) dello stesso tipo: il corso copre la sua
  // scadenza, quindi non ha senso suggerirlo di nuovo finche' non viene
  // completato o rimosso.
  const suggeriti = useMemo(() => {
    const groups = [];
    for (const tipo of CLASSI_TIPI) {
      const classiInCorsoTipo = classi.filter((c) => !c.completata && c.tipo === tipo.value);
      const dipGiaIscrittiTipo = new Set();
      for (const c of classiInCorsoTipo) {
        for (const cd of classiDipendenti) {
          if (cd.classe_id === c.id) dipGiaIscrittiTipo.add(cd.dipendente_id);
        }
      }

      const dipScadenti = [];
      for (const d of dipendenti) {
        if (dipGiaIscrittiTipo.has(d.id)) continue;
        const scheda = schedaDipendenti[d.id];
        if (!scheda) continue;
        // Per formazione: prendi il min tra form_gen e form_spec
        // Ignora dipendenti che non hanno mai avuto quel corso (campo vuoto/null)
        let minGg = 9999;
        let scadStr = null;
        let haData = false;
        for (const field of tipo.fields) {
          const val = scheda[field];
          if (val) {
            haData = true;
            const gg = diffGiorni(val);
            if (gg < minGg) { minGg = gg; scadStr = val; }
          }
        }
        if (haData && minGg <= tipo.soglia) {
          dipScadenti.push({ ...d, scadGg: minGg, scadStr });
        }
      }
      if (dipScadenti.length >= 1) {
        dipScadenti.sort((a, b) => a.scadGg - b.scadGg);
        groups.push({ tipo, dipendenti: dipScadenti });
      }
    }
    return groups;
  }, [dipendenti, schedaDipendenti, classi, classiDipendenti]);

  // ---- Form helpers ----
  function openForm(presetTipo, presetDip) {
    setEditClasse(null);
    setFormTipo(presetTipo || "");
    setFormData("");
    setFormOra("");
    setFormLuogo("");
    setFormFormatore("");
    setFormNote("");
    setFormDipSelezionati(presetDip || []);
    setShowForm(true);
  }

  function openEditForm(classe) {
    setEditClasse(classe);
    setFormTipo(classe.tipo || "");
    setFormData(classe.data ? yyyymmddToDisplay(classe.data) : "");
    setFormOra(classe.ora || "");
    setFormLuogo(classe.luogo || "");
    setFormFormatore(classe.formatore || "");
    setFormNote(classe.note || "");
    const dips = classiDipendenti.filter((d) => d.classe_id === classe.id).map((d) => d.dipendente_id);
    setFormDipSelezionati(dips);
    setShowForm(true);
  }

  function closeForm() { setShowForm(false); setEditClasse(null); }

  async function handleSaveClasse(notify) {
    if (!formTipo) return;
    setSaving(true);
    const classeData = {
      tipo: formTipo,
      data: normDate(formData) || null,
      ora: formOra || null,
      luogo: formLuogo || null,
      formatore: formFormatore || null,
      note: formNote || null,
      completata: false,
    };
    if (editClasse) classeData.id = editClasse.id;
    const result = await saveClasse(classeData);
    if (!result.success) { setSaving(false); return; }
    const classeId = result.data.id;

    // Sync dipendenti — rimuovi vecchi, aggiungi nuovi
    if (editClasse) {
      const old = classiDipendenti.filter((d) => d.classe_id === classeId).map((d) => d.dipendente_id);
      for (const dipId of old) {
        if (!formDipSelezionati.includes(dipId)) await removeDipendenteClasse(classeId, dipId);
      }
      for (const dipId of formDipSelezionati) {
        if (!old.includes(dipId)) {
          const dip = dipendenti.find((d) => d.id === dipId);
          await addDipendenteClasse(classeId, dipId, dip?.name || "");
        }
      }
    } else {
      for (const dipId of formDipSelezionati) {
        const dip = dipendenti.find((d) => d.id === dipId);
        await addDipendenteClasse(classeId, dipId, dip?.name || "");
      }
    }

    // Notifica dipendenti se richiesto
    if (notify && classeData.data) {
      handleNotifica(classeId, classeData);
    }

    setSaving(false);
    closeForm();
  }

  function handleNotifica(classeId, classeOverride) {
    const classe = classeOverride || classi.find((c) => c.id === classeId);
    if (!classe || !classe.data) return;
    const dips = classiDipendenti.filter((d) => d.classe_id === classeId);
    const dipIds = dips.length > 0 ? dips.map((d) => d.dipendente_id) : formDipSelezionati;

    if (classe.tipo === "visita") {
      const next = { ...visiteProgrammate };
      for (const dipId of dipIds) {
        next[dipId] = { data: classe.data, ora: classe.ora || "" };
      }
      saveVisiteProgrammate(next);
    } else {
      const next = { ...formazioneProgrammate };
      for (const dipId of dipIds) {
        next[dipId] = { data: classe.data, ora: classe.ora || "" };
      }
      saveFormazioneProgrammate(next);
    }
    setNotificaMsg(`Notificati ${dipIds.length} dipendenti`);
    setTimeout(() => setNotificaMsg(null), 3000);
  }

  // ---- Completamento classe ----
  function openCompleteModal(classe) {
    const dips = classiDipendenti.filter((d) => d.classe_id === classe.id);
    const checks = {};
    dips.forEach((d) => { checks[d.dipendente_id] = true; });
    setPresentiCheck(checks);
    setConfirmComplete(classe);
  }

  async function handleConfirmComplete() {
    if (!confirmComplete) return;
    const presentiIds = Object.entries(presentiCheck).filter(([, v]) => v).map(([k]) => Number(k));
    await marcaCompletata(confirmComplete.id, presentiIds, {
      visiteProgrammate, saveVisiteProgrammate,
      formazioneProgrammate, saveFormazioneProgrammate,
    });
    setConfirmComplete(null);
  }

  // ---- Classi filtrate per tab ----
  const classiInProgramma = classi.filter((c) => !c.completata).sort((a, b) => {
    if (!a.data && !b.data) return 0;
    if (!a.data) return 1;
    if (!b.data) return -1;
    return a.data.localeCompare(b.data);
  });
  const classiArchivio = classi.filter((c) => c.completata).sort((a, b) => {
    if (!a.data && !b.data) return 0;
    if (!a.data) return 1;
    if (!b.data) return -1;
    return b.data.localeCompare(a.data);
  });

  // ---- Assenti da ripianificare ----
  const assentiDaRipianificare = useMemo(() => {
    const result = [];
    for (const c of classiArchivio) {
      const dips = classiDipendenti.filter((d) => d.classe_id === c.id && !d.presente);
      for (const d of dips) {
        const tipoInfo = CLASSI_TIPI.find((t) => t.value === c.tipo);
        const scheda = schedaDipendenti[d.dipendente_id];
        let ancoraScaduto = true;
        if (scheda && tipoInfo) {
          for (const field of tipoInfo.fields) {
            if (scheda[field] && diffGiorni(scheda[field]) > tipoInfo.soglia) ancoraScaduto = false;
          }
        }
        if (ancoraScaduto) {
          result.push({ ...d, tipo: c.tipo, tipoLabel: tipoInfo?.label || c.tipo, classeData: c.data });
        }
      }
    }
    return result;
  }, [classiArchivio, classiDipendenti, schedaDipendenti]);

  // ---- Contatore notificati per classe ----
  function countNotificati(classe) {
    const dips = classiDipendenti.filter((d) => d.classe_id === classe.id);
    const source = classe.tipo === "visita" ? visiteProgrammate : formazioneProgrammate;
    let count = 0;
    for (const d of dips) {
      if (source[d.dipendente_id]) count++;
    }
    return { count, total: dips.length };
  }

  const tabStyle = (active) => ({
    padding: "10px 20px", borderRadius: "var(--radius-md) var(--radius-md) 0 0",
    border: "1px solid var(--border)", borderBottom: active ? "2px solid var(--primary)" : "1px solid var(--border)",
    background: active ? "var(--surface)" : "transparent",
    color: active ? "var(--text)" : "var(--text-muted)",
    fontWeight: active ? 700 : 500, fontSize: 14, cursor: "pointer", fontFamily: "var(--font)",
  });

  const cardStyle = { background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 20, border: "1px solid var(--border)", marginBottom: 14 };
  const btnPrimary = { padding: "8px 16px", borderRadius: "var(--radius-md)", border: "none", background: "var(--primary)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" };
  const btnOutline = { padding: "8px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-muted)", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "var(--font)" };

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
          <GraduationCap size={24} /> Classi
        </h1>
        <button onClick={() => openForm()} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}>
          <Plus size={16} /> Nuova classe
        </button>
      </div>

      {/* Notifica verde */}
      {notificaMsg && (
        <div style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.4)", borderRadius: "var(--radius-md)", padding: "12px 16px", marginBottom: 16, color: "#10b981", fontWeight: 600, fontSize: 14 }}>
          <Check size={16} style={{ verticalAlign: "middle", marginRight: 8 }} />{notificaMsg}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid var(--border)" }}>
        <button onClick={() => setTab("suggerite")} style={tabStyle(tab === "suggerite")}>Suggerite</button>
        <button onClick={() => setTab("programma")} style={tabStyle(tab === "programma")}>
          In programma {classiInProgramma.length > 0 && <span style={{ marginLeft: 6, background: "var(--primary)", color: "#fff", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{classiInProgramma.length}</span>}
        </button>
        <button onClick={() => setTab("archivio")} style={tabStyle(tab === "archivio")}>Archivio</button>
      </div>

      {/* ===== TAB SUGGERITE ===== */}
      {tab === "suggerite" && (
        <div>
          {suggeriti.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", padding: 40 }}>
              <p style={{ color: "var(--text-muted)", fontSize: 15 }}>Nessuna classe suggerita al momento.</p>
              <p style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 6 }}>Le classi vengono suggerite quando 2+ dipendenti hanno scadenze imminenti per lo stesso tipo.</p>
            </div>
          ) : suggeriti.map((g) => (
            <div key={g.tipo.value} style={{ ...cardStyle }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <p style={{ color: "var(--text)", fontSize: 16, fontWeight: 700 }}>{g.tipo.label}</p>
                  <p style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 2 }}>Rinnovo: {g.tipo.anni} ann{g.tipo.anni === 1 ? "o" : "i"}</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    background: urgenzaColor(Math.min(...g.dipendenti.map((d) => d.scadGg))).bg,
                    color: urgenzaColor(Math.min(...g.dipendenti.map((d) => d.scadGg))).text,
                    border: `1px solid ${urgenzaColor(Math.min(...g.dipendenti.map((d) => d.scadGg))).border}`,
                    borderRadius: 8, padding: "4px 12px", fontSize: 13, fontWeight: 700,
                  }}>{g.dipendenti.length} dipendenti</span>
                  <button onClick={() => openForm(g.tipo.value, g.dipendenti.map((d) => d.id))} style={btnPrimary}>Crea classe</button>
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {g.dipendenti.map((d) => {
                  const u = urgenzaColor(d.scadGg);
                  return (
                    <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 6, background: u.bg, border: `1px solid ${u.border}`, borderRadius: 8, padding: "6px 12px" }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: `linear-gradient(135deg, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][0]}, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][1]})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 10, color: "#fff" }}>{d.avatar}</div>
                      <span style={{ color: u.text, fontSize: 13, fontWeight: 600 }}>{d.name}</span>
                      <span style={{ color: u.text, fontSize: 11, opacity: 0.7 }}>
                        {d.scadGg < 0 ? "Scaduto" : d.scadStr ? yyyymmddToDisplay(d.scadStr) : "N/D"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== TAB IN PROGRAMMA ===== */}
      {tab === "programma" && (
        <div>
          {classiInProgramma.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", padding: 40 }}>
              <p style={{ color: "var(--text-muted)", fontSize: 15 }}>Nessuna classe in programma.</p>
              <p style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 6 }}>Crea una nuova classe dal pulsante in alto o dalla tab "Suggerite".</p>
            </div>
          ) : classiInProgramma.map((c) => {
            const tipoInfo = CLASSI_TIPI.find((t) => t.value === c.tipo);
            const dips = classiDipendenti.filter((d) => d.classe_id === c.id);
            const { count: notCount, total: notTotal } = countNotificati(c);
            return (
              <div key={c.id} style={{ ...cardStyle, borderLeft: `4px solid ${urgenzaBorderColor(c)}` }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <p style={{ color: "var(--text)", fontSize: 16, fontWeight: 700 }}>{tipoInfo?.label || c.tipo}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8, color: "var(--text-muted)", fontSize: 13 }}>
                      {c.data && <span>📅 {yyyymmddToDisplay(c.data)}</span>}
                      {c.ora && <span>🕐 {c.ora}</span>}
                      {c.luogo && <span>📍 {c.luogo}</span>}
                      {c.formatore && <span>👤 {c.formatore}</span>}
                    </div>
                    {/* Avatar dipendenti */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12 }}>
                      {dips.map((d) => {
                        const dip = dipendenti.find((x) => x.id === d.dipendente_id);
                        const avatarIdx = (d.dipendente_id - 1) % AVATAR_COLORS.length;
                        return (
                          <div key={d.id} title={d.dipendente_nome} style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${AVATAR_COLORS[avatarIdx][0]}, ${AVATAR_COLORS[avatarIdx][1]})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 10, color: "#fff" }}>
                            {dip?.avatar || d.dipendente_nome?.substring(0, 2).toUpperCase()}
                          </div>
                        );
                      })}
                      <span style={{ display: "flex", alignItems: "center", fontSize: 12, color: "var(--text-dim)", marginLeft: 6 }}>Notificati: {notCount}/{notTotal}</span>
                    </div>
                  </div>
                  {/* Azioni */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <button onClick={() => openEditForm(c)} style={btnOutline}>Modifica</button>
                    <button onClick={() => handleNotifica(c.id)} style={{ ...btnOutline, borderColor: "rgba(6,182,212,0.4)", color: "#06b6d4" }}>Notifica dipendenti</button>
                    <button onClick={() => openCompleteModal(c)} style={{ ...btnPrimary, background: "var(--accent)" }}>Segna completata</button>
                    <button onClick={() => setConfirmDeleteId(c.id)} style={{ ...btnOutline, borderColor: "rgba(239,68,68,0.3)", color: "var(--danger)", fontSize: 12 }}>Elimina</button>
                  </div>
                </div>
                {c.note && <p style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 10, fontStyle: "italic" }}>Note: {c.note}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== TAB ARCHIVIO ===== */}
      {tab === "archivio" && (
        <div>
          {classiArchivio.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", padding: 40 }}>
              <p style={{ color: "var(--text-muted)", fontSize: 15 }}>Nessuna classe completata.</p>
            </div>
          ) : classiArchivio.map((c) => {
            const tipoInfo = CLASSI_TIPI.find((t) => t.value === c.tipo);
            const dips = classiDipendenti.filter((d) => d.classe_id === c.id);
            const presenti = dips.filter((d) => d.presente);
            const assenti = dips.filter((d) => !d.presente);
            const isExpanded = expandedArchivio === c.id;
            return (
              <div key={c.id} style={{ ...cardStyle, opacity: 0.75 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <p style={{ color: "var(--text)", fontSize: 15, fontWeight: 600 }}>
                      {tipoInfo?.label || c.tipo}
                      {c.data && <span style={{ color: "var(--text-dim)", fontWeight: 400, marginLeft: 10 }}>{yyyymmddToDisplay(c.data)}</span>}
                    </p>
                    <p style={{ color: "var(--text-dim)", fontSize: 12, marginTop: 4 }}>
                      Presenti: {presenti.length}/{dips.length}
                      {assenti.length > 0 && <span style={{ color: "#ef4444", marginLeft: 8 }}>{assenti.length} assent{assenti.length === 1 ? "e" : "i"}</span>}
                    </p>
                  </div>
                  <button onClick={() => setExpandedArchivio(isExpanded ? null : c.id)} style={btnOutline}>
                    {isExpanded ? "Chiudi" : "Dettagli"}
                  </button>
                </div>
                {isExpanded && (
                  <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {dips.map((d) => (
                      <span key={d.id} style={{
                        padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                        background: d.presente ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)",
                        color: d.presente ? "#10b981" : "#ef4444",
                        border: `1px solid ${d.presente ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                      }}>
                        {d.dipendente_nome} {d.presente ? "✓" : "— assente"}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Assenti da ripianificare */}
          {assentiDaRipianificare.length > 0 && (
            <div style={{ marginTop: 24, background: "var(--surface)", borderRadius: "var(--radius-md)", border: "1px solid rgba(239,68,68,0.25)", padding: 20 }}>
              <p style={{ color: "var(--text-muted)", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 14 }}>
                Assenti da ripianificare <span style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", borderRadius: 8, padding: "2px 8px", fontSize: 11 }}>{assentiDaRipianificare.length}</span>
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {assentiDaRipianificare.map((d, i) => (
                  <div key={`${d.dipendente_id}-${d.classe_id}-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "rgba(239,68,68,0.07)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(239,68,68,0.15)" }}>
                    <div>
                      <span style={{ color: "var(--text)", fontSize: 14, fontWeight: 600 }}>{d.dipendente_nome}</span>
                      <span style={{ color: "var(--text-dim)", fontSize: 12, marginLeft: 10 }}>{d.tipoLabel}</span>
                    </div>
                    <button onClick={() => openForm(d.tipo, [d.dipendente_id])} style={{ ...btnOutline, fontSize: 12 }}>Aggiungi alla prossima</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== MODAL FORM CLASSE ===== */}
      {showForm && (
        <div onClick={closeForm} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 20, width: "100%", maxWidth: 720, maxHeight: "92vh", overflowY: "auto", border: "1px solid var(--border)", boxShadow: "0 24px 80px rgba(0,0,0,0.5)", padding: "36px 32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
              <p style={{ color: "var(--text)", fontSize: 24, fontWeight: 800 }}>{editClasse ? "Modifica classe" : "Nuova classe"}</p>
              <button onClick={closeForm} style={{ background: "var(--surface-light)", border: "none", borderRadius: 10, width: 40, height: 40, cursor: "pointer", color: "var(--text-muted)", fontSize: 20, fontWeight: 700 }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Tipo */}
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Tipo *</p>
                <select value={formTipo} onChange={(e) => setFormTipo(e.target.value)} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", color: "var(--text)", fontSize: 17, outline: "none", fontFamily: "var(--font)" }}>
                  <option value="">— Seleziona tipo —</option>
                  {CLASSI_TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {/* Data, Ora in riga */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Data</p>
                  <input type="text" placeholder="GG/MM/AAAA" value={formData} onChange={(e) => setFormData(autoDate(e.target.value))} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", color: "var(--text)", fontSize: 17, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Ora</p>
                  <input type="text" placeholder="HH:MM" value={formOra} onChange={(e) => setFormOra(autoTime(e.target.value))} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", color: "var(--text)", fontSize: 17, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
                </div>
              </div>
              {/* Luogo, Formatore */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Luogo</p>
                  <input type="text" value={formLuogo} onChange={(e) => setFormLuogo(e.target.value)} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", color: "var(--text)", fontSize: 17, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
                </div>
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Formatore / Dottore</p>
                  <input type="text" value={formFormatore} onChange={(e) => setFormFormatore(e.target.value)} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", color: "var(--text)", fontSize: 17, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" }} />
                </div>
              </div>
              {/* Note */}
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Note</p>
                <textarea value={formNote} onChange={(e) => setFormNote(e.target.value)} rows={2} style={{ width: "100%", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: 10, padding: "14px 16px", color: "var(--text)", fontSize: 17, outline: "none", boxSizing: "border-box", fontFamily: "var(--font)", resize: "vertical" }} />
              </div>

              {/* Selezione dipendenti */}
              {formTipo && (
                <div>
                  <p style={{ color: "var(--text-muted)", fontSize: 13, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Dipendenti ({formDipSelezionati.length} selezionati)</p>
                  <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface-light)" }}>
                    {dipendenti.map((d) => {
                      const selected = formDipSelezionati.includes(d.id);
                      const tipoInfo = CLASSI_TIPI.find((t) => t.value === formTipo);
                      const scheda = schedaDipendenti[d.id];
                      let scadGg = 9999;
                      let scadLabel = "";
                      let haData = false;
                      if (scheda && tipoInfo) {
                        for (const field of tipoInfo.fields) {
                          const val = scheda[field];
                          if (val) {
                            haData = true;
                            const gg = diffGiorni(val);
                            if (gg < scadGg) { scadGg = gg; scadLabel = yyyymmddToDisplay(val); }
                          }
                        }
                      }
                      if (!haData) scadLabel = "";
                      const scadDateColor = !haData ? "var(--text-dim)" : scadGg < 0 ? "#ef4444" : scadGg <= (tipoInfo?.soglia || 45) ? "#f59e0b" : "var(--text-dim)";
                      return (
                        <div key={d.id} onClick={() => setFormDipSelezionati((prev) => selected ? prev.filter((id) => id !== d.id) : [...prev, d.id])} style={{
                          display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer",
                          background: selected ? "rgba(192,57,43,0.12)" : "transparent",
                          borderBottom: "1px solid var(--border)",
                        }}>
                          <div style={{ width: 26, height: 26, borderRadius: 6, border: selected ? "2.5px solid var(--primary)" : "2.5px solid var(--border)", background: selected ? "var(--primary)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {selected && <Check size={15} color="#fff" />}
                          </div>
                          <div style={{ width: 36, height: 36, borderRadius: 9, background: `linear-gradient(135deg, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][0]}, ${AVATAR_COLORS[(d.id - 1) % AVATAR_COLORS.length][1]})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#fff", flexShrink: 0 }}>{d.avatar}</div>
                          <span style={{ color: "var(--text)", fontSize: 16, fontWeight: 600, flex: 1 }}>{d.name}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: scadDateColor }}>
                            {!haData ? "—" : scadGg < 0 ? "Scaduto" : scadLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pulsanti */}
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button onClick={closeForm} style={{ ...btnOutline, flex: 1, padding: "14px 16px", fontSize: 16, fontWeight: 600 }}>Annulla</button>
                <button onClick={() => handleSaveClasse(false)} disabled={saving || !formTipo} style={{ ...btnPrimary, flex: 1, padding: "14px 16px", fontSize: 16, opacity: saving || !formTipo ? 0.5 : 1 }}>
                  {saving ? "Salvando..." : "Salva classe"}
                </button>
                {formData && (
                  <button onClick={() => handleSaveClasse(true)} disabled={saving || !formTipo} style={{ ...btnPrimary, flex: 1, padding: "14px 16px", fontSize: 16, background: "#06b6d4", opacity: saving || !formTipo ? 0.5 : 1 }}>
                    {saving ? "..." : "Salva e notifica"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL SEGNA COMPLETATA ===== */}
      {confirmComplete && (() => {
        const tipoInfo = CLASSI_TIPI.find((t) => t.value === confirmComplete.tipo);
        const dips = classiDipendenti.filter((d) => d.classe_id === confirmComplete.id);
        const oggiStr = new Date().toISOString().split("T")[0];
        const nuovaScad = tipoInfo ? (() => { const d = new Date(); d.setFullYear(d.getFullYear() + tipoInfo.anni); return d.toISOString().split("T")[0]; })() : null;
        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={() => setConfirmComplete(null)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
            <div style={{ position: "relative", background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 480, border: "1px solid var(--border)", zIndex: 1101, maxHeight: "85vh", overflowY: "auto" }}>
              <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Segna completata</h2>
              <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 20 }}>{tipoInfo?.label} — {confirmComplete.data ? yyyymmddToDisplay(confirmComplete.data) : "data non impostata"}</p>
              <p style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", marginBottom: 10 }}>Presenze</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
                {dips.map((d) => {
                  const checked = presentiCheck[d.dipendente_id] ?? false;
                  return (
                    <div key={d.id} onClick={() => setPresentiCheck((p) => ({ ...p, [d.dipendente_id]: !p[d.dipendente_id] }))} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer",
                      background: checked ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.06)",
                      border: `1px solid ${checked ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.2)"}`,
                      borderRadius: "var(--radius-sm)",
                    }}>
                      <div style={{ width: 22, height: 22, borderRadius: 5, border: checked ? "2px solid #10b981" : "2px solid #ef4444", background: checked ? "#10b981" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {checked && <Check size={14} color="#fff" />}
                      </div>
                      <span style={{ color: "var(--text)", fontSize: 14, fontWeight: 500, flex: 1 }}>{d.dipendente_nome}</span>
                      {checked ? (
                        <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>Nuova scad: {nuovaScad ? yyyymmddToDisplay(nuovaScad) : "—"}</span>
                      ) : (
                        <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>Assente — da ripianificare</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setConfirmComplete(null)} style={{ ...btnOutline, flex: 1 }}>Annulla</button>
                <button onClick={handleConfirmComplete} style={{ ...btnPrimary, flex: 1, background: "var(--accent)" }}>Conferma e aggiorna</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== MODAL CONFERMA ELIMINAZIONE CLASSE ===== */}
      {confirmDeleteId && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={() => setConfirmDeleteId(null)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)" }} />
          <div style={{ position: "relative", background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 400, border: "1px solid var(--border)", zIndex: 1101 }}>
            <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Elimina classe</h2>
            <p style={{ color: "var(--text-dim)", fontSize: 14, marginBottom: 24 }}>Sei sicuro di voler eliminare questa classe?</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmDeleteId(null)} style={{ ...btnOutline, flex: 1 }}>Annulla</button>
              <button onClick={() => {
                // Rimuovi dalle programmate i dipendenti di questa classe
                const classe = classi.find(c => c.id === confirmDeleteId);
                if (classe) {
                  const dips = classiDipendenti.filter(d => d.classe_id === confirmDeleteId);
                  if (classe.tipo === "visita") {
                    const next = { ...visiteProgrammate };
                    dips.forEach(d => delete next[d.dipendente_id]);
                    saveVisiteProgrammate(next);
                  } else {
                    const next = { ...formazioneProgrammate };
                    dips.forEach(d => delete next[d.dipendente_id]);
                    saveFormazioneProgrammate(next);
                  }
                }
                deleteClasse(confirmDeleteId);
                setConfirmDeleteId(null);
              }} style={{ ...btnPrimary, flex: 1, background: "var(--danger)" }}>Elimina</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NotifichePage({ missing }) {
  return (
    <>
      <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Notifiche</h1>
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", padding: 20, border: "1px solid var(--border)" }}>
        <h2 style={{ color: "var(--text)", fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Ore mancanti oggi — {missing.length} dipendenti
        </h2>
        {missing.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <p style={{ color: "var(--accent)", fontSize: 16, fontWeight: 600 }}>Tutti i dipendenti hanno inserito le ore!</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {missing.map((d) => (
              <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "var(--danger-glow)", borderRadius: "var(--radius-md)", border: "1px solid rgba(239,68,68,0.2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "var(--radius-sm)", background: "var(--surface-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: "var(--text-muted)" }}>{d.avatar}</div>
                  <span style={{ color: "var(--text)", fontSize: 15, fontWeight: 500 }}>{d.name}</span>
                </div>
                <span style={{ background: "var(--danger)", color: "#fff", padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600 }}>Mancante</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const filterSelectStyle = {
  padding: "8px 12px",
  background: "var(--surface-light)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  color: "var(--text)",
  fontSize: 13,
  fontFamily: "var(--font)",
};

// ============================================================================
// FILE EXCEL VISITE — Pagina lista dipendenti visite e contratti
// ============================================================================

const CAMPI_RIGA = ["c","d","e","f","g","h","i","j","k","l","visite","ass","scadC","pds"];

function cellColor(val) {
  const sc = scadenzaColor(val);
  if (sc) return sc;
  if (!val || val === "") return null;
  if (val === "CESSATO") return { bg: "rgba(239,68,68,0.18)", text: "#ef4444" };
  if (val.includes("INDET") || val === "illimitato" || val === "ILLIMITATO") return { bg: "rgba(59,130,246,0.10)", text: "#60a5fa" };
  return null;
}

function isStars(v) { return v && [...v].every(c => c === "*") ; }

function FileExcelVisitePage({ dipendenti: dipList = [], schedaDipendenti = {}, saveSchedaDipendente = () => {} }) {
  const [search, setSearch] = useState("");
  const [zoom, setZoom] = useState(1.15);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  // Valore iniziale della cella al momento del focus: serve per committare
  // solo se l'utente ha effettivamente modificato qualcosa.
  const initialEditValueRef = useRef("");

  // Mapping colonna → chiave Supabase
  const FIELD_MAP = {
    c: "form_gen", d: "form_spec", e: "antincendio", f: "primo_soccorso",
    g: "piattaforma", h: "rls", i: "escavatore", j: "gru",
    k: "lav_quota", l: "preposto", visite: "visita_medica",
    ass: "data_assunzione", scadC: "scad_contratto", pds: "scad_pds", note: "note_pds",
  };
  const DATE_FIELDS = new Set(["c","d","e","f","g","h","i","j","k","l","visite","ass","scadC","pds"]);
  const READONLY_FIELDS = new Set(["n","nome"]);

  // YYYY-MM-DD → DD/MM/YYYY senza shift timezone
  const fmtD = (isoStr) => {
    if (!isoStr) return "*****";
    const m = String(isoStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return isoStr; // già DD/MM/YYYY o testo speciale
  };

  // DD/MM/YYYY → YYYY-MM-DD, null se non è una data
  const toISO = (str) => {
    if (!str || str.trim() === "" || isStars(str)) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const m = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
    if (m) return `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
    return null;
  };

  // Costruisce le righe dalla scheda Supabase in tempo reale
  const tableData = useMemo(() => {
    return [...dipList]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((dip, idx) => {
        const s = schedaDipendenti[dip.id] || {};
        const isAutista = s.qualifica && s.qualifica.toUpperCase().includes("AUTISTA");
        const formVal = isAutista ? s.qualifica : fmtD(s.form_gen);
        const scadCVal = s.scad_contratto ? fmtD(s.scad_contratto) : (s.tipo_contratto || dip.contratto || "INDET.");
        return {
          _dipId: dip.id,
          n: String(idx + 1),
          nome: dip.name.toUpperCase(),
          c: formVal,
          d: formVal,
          e: fmtD(s.antincendio),
          f: fmtD(s.primo_soccorso),
          g: fmtD(s.piattaforma),
          h: fmtD(s.rls),
          i: fmtD(s.escavatore),
          j: fmtD(s.gru),
          k: fmtD(s.lav_quota),
          l: fmtD(s.preposto),
          visite: fmtD(s.visita_medica),
          ass: s.data_assunzione ? fmtD(s.data_assunzione) : (dip.ass || "—"),
          scadC: scadCVal,
          pds: fmtD(s.scad_pds),
          note: s.note_pds || "",
        };
      });
  }, [dipList, schedaDipendenti]);

  const rows = search
    ? tableData.filter(r => r.nome.toLowerCase().includes(search.toLowerCase()))
    : tableData;

  const FIELDS = ["n","nome","c","d","e","f","g","h","i","j","k","l","visite","ass","scadC","pds","note"];

  const startEdit = (ri, field, currentVal) => {
    if (READONLY_FIELDS.has(field)) return;
    setEditingCell({ ri, field, dipId: rows[ri]._dipId });
    const initial = isStars(currentVal) ? "" : (currentVal || "");
    setEditValue(initial);
    initialEditValueRef.current = initial;
  };

  const commitEdit = async () => {
    if (!editingCell) return;
    // Skip: se il valore non e' cambiato rispetto all'entrata in cella,
    // non scrivere nulla (altrimenti la navigazione con frecce passerebbe
    // toISO su testi non-data e azzererebbe campi tipo "INDET." in scadC).
    if (editValue === initialEditValueRef.current) { setEditingCell(null); return; }
    const { field, dipId } = editingCell;
    const supaKey = FIELD_MAP[field];
    if (!supaKey) { setEditingCell(null); return; }

    const update = {};
    if (DATE_FIELDS.has(field)) {
      update[supaKey] = toISO(editValue);
      if (field === "c") update.form_spec = toISO(editValue); // form_gen e form_spec aggiornati insieme
    } else {
      update[supaKey] = editValue.trim() || null;
    }

    await saveSchedaDipendente(dipId, update);
    setEditingCell(null);
  };

  // ===== Navigazione stile Excel: frecce/Tab/Enter/Esc =====
  // Le colonne 0 ("n") e 1 ("nome") sono readonly e vengono saltate.
  const gridScrollRef = useRef(null);

  const isNavCellEditable = (ri, ci) => {
    if (ri < 0 || ri >= rows.length || ci < 0 || ci >= FIELDS.length) return false;
    return !READONLY_FIELDS.has(FIELDS[ci]);
  };

  const moveGridSelection = (dr, dc) => {
    if (!editingCell) return;
    const curRi = editingCell.ri;
    const curCi = FIELDS.indexOf(editingCell.field);
    if (curCi < 0) return;
    let ri = curRi + dr;
    let ci = curCi + dc;
    while (ri >= 0 && ri < rows.length && ci >= 0 && ci < FIELDS.length) {
      if (isNavCellEditable(ri, ci)) {
        const field = FIELDS[ci];
        const currentVal = rows[ri][field] || "";
        setEditingCell({ ri, field, dipId: rows[ri]._dipId });
        const initial = isStars(currentVal) ? "" : (currentVal || "");
        setEditValue(initial);
        initialEditValueRef.current = initial;
        return;
      }
      ri += dr;
      ci += dc;
    }
  };

  const handleNavKeyDown = async (e) => {
    if (e.key === "Escape") { setEditingCell(null); return; }
    const input = e.target;
    const pos = input.selectionStart ?? 0;
    const len = (input.value || "").length;

    const commitAndMove = async (dr, dc) => {
      e.preventDefault();
      await commitEdit();
      moveGridSelection(dr, dc);
    };

    if (e.key === "Enter") { await commitAndMove(1, 0); return; }
    if (e.key === "Tab") { await commitAndMove(0, e.shiftKey ? -1 : 1); return; }
    if (e.key === "ArrowUp") { await commitAndMove(-1, 0); return; }
    if (e.key === "ArrowDown") { await commitAndMove(1, 0); return; }
    if (e.key === "ArrowLeft" && pos === 0) { await commitAndMove(0, -1); return; }
    if (e.key === "ArrowRight" && pos === len) { await commitAndMove(0, 1); return; }
  };

  // Scroll automatico della cella in edit quando esce dalla viewport
  useEffect(() => {
    if (!editingCell) return;
    const container = gridScrollRef.current;
    if (!container) return;
    const ci = FIELDS.indexOf(editingCell.field);
    if (ci < 0) return;
    const td = container.querySelector(`td[data-r="${editingCell.ri}"][data-c="${ci}"]`);
    if (td) td.scrollIntoView({ block: "nearest", inline: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCell]);

  const thStyle = {
    padding: "8px 6px", textAlign: "center", color: "#c8d0e0", fontSize: Math.round(10 * zoom),
    fontWeight: 700, borderRight: "1px solid #2a3550", whiteSpace: "pre-line",
    background: "#1a1f2e", lineHeight: 1.3, textTransform: "uppercase", letterSpacing: 0.4,
  };

  const tdBase = (isEditing, isSticky, bgEven, bgOdd, isEven) => ({
    padding: 0, fontSize: Math.round(11 * zoom), textAlign: "center",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    position: isSticky ? "sticky" : "static",
    background: isEditing ? "rgba(99,102,241,0.2)" : isSticky ? (isEven ? bgEven : bgOdd) : "transparent",
    outline: isEditing ? "2px solid var(--primary)" : "none",
    zIndex: isSticky ? 1 : "auto",
    cursor: "pointer",
    transition: "background 0.1s",
  });

  const inputStyle = {
    width: "100%", height: "100%", padding: "5px 8px",
    background: "transparent", border: "none", outline: "none",
    color: "var(--text)", fontSize: Math.round(11 * zoom), fontFamily: "var(--font)",
    textAlign: "center", boxSizing: "border-box",
  };

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 700 }}>File Excel Visite</h1>
          <p style={{ color: "var(--text-dim)", fontSize: 13, marginTop: 4 }}>Lista dipendenti — formazioni, visite mediche e scadenze contratti · Clicca su qualsiasi cella per modificarla</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca dipendente..."
            style={{ padding: "8px 12px", background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13, fontFamily: "var(--font)", outline: "none", width: 180 }}
          />
          {/* Controllo zoom */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--surface-light)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: "4px 8px" }}>
            <button onClick={() => setZoom(z => Math.max(0.7, +(z - 0.1).toFixed(1)))} style={{ background: "none", border: "none", color: "var(--text)", fontSize: 16, cursor: "pointer", fontFamily: "var(--font)", lineHeight: 1, padding: "0 4px" }}>−</button>
            <span style={{ color: "var(--text-muted)", fontSize: 12, minWidth: 36, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(2, +(z + 0.1).toFixed(1)))} style={{ background: "none", border: "none", color: "var(--text)", fontSize: 16, cursor: "pointer", fontFamily: "var(--font)", lineHeight: 1, padding: "0 4px" }}>+</button>
          </div>
          <button
            onClick={() => exportExcelVisite(tableData)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: "var(--radius-md)", border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 16px rgba(16,185,129,0.3)", fontFamily: "var(--font)" }}
          >
            <Download size={16} /> Esporta Excel
          </button>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
        {[["#ef4444","Scaduta / Cessato"],["#f59e0b","Scade entro 3 mesi"],["#60a5fa","Indeterminato"]].map(([c,l]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }} />
            <span style={{ color: "var(--text-dim)", fontSize: 12 }}>{l}</span>
          </div>
        ))}
      </div>

      {/* Tabella full-width */}
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", overflow: "hidden" }}
        onClick={e => { if (!e.target.closest("input")) commitEdit(); }}>
        <div ref={gridScrollRef} style={{ overflowX: "auto", overflowY: "auto", maxHeight: "74vh" }}>
          <table style={{ borderCollapse: "collapse", fontSize: Math.round(12 * zoom), width: "100%", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: Math.round(36 * zoom) }} />
              <col style={{ width: Math.round(160 * zoom) }} />
              {[90,90,90,90,90,80,70,70,100,100,105,115,120,110,220].map((w,i) => <col key={i} style={{ width: Math.round(w * zoom) }} />)}
            </colgroup>
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                <th colSpan={17} style={{ background: "#8B1A1A", color: "#fff", padding: "10px 16px", textAlign: "center", fontWeight: 700, fontSize: 13, letterSpacing: 0.5, borderBottom: "2px solid #6b1414" }}>
                  LISTA DIPENDENTI × VISITE E CONTRATTI — MATTONE ROSSO
                </th>
              </tr>
              <tr>
                <th style={{ ...thStyle, position: "sticky", left: 0, zIndex: 12 }}>N.</th>
                <th style={{ ...thStyle, position: "sticky", left: 36, zIndex: 12, textAlign: "left", paddingLeft: 10 }}>DIPENDENTE</th>
                {COLONNE_VISITE.slice(2).map((c, i) => <th key={i} style={thStyle}>{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const isEven = ri % 2 === 0;
                const bgEven = "#1e2235"; const bgOdd = "#191d2e";
                return (
                  <tr key={ri} style={{ background: isEven ? "rgba(255,255,255,0.02)" : "transparent", height: Math.round(32 * zoom) }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.06)"}
                    onMouseLeave={e => e.currentTarget.style.background = isEven ? "rgba(255,255,255,0.02)" : "transparent"}>
                    {FIELDS.map((field, fi) => {
                      const v = row[field] || "";
                      const isEditing = editingCell?.ri === ri && editingCell?.field === field;
                      const isSticky = fi === 0 || fi === 1;
                      const stickyLeft = fi === 0 ? 0 : fi === 1 ? 36 : undefined;
                      const stars = isStars(v);
                      // data assunzione non va colorata (è una data storica, non una scadenza)
                      const sc = field === "ass" ? null : cellColor(v);
                      const isNote = fi === 16;
                      return (
                        <td key={field}
                          data-r={ri} data-c={fi}
                          style={{
                            ...tdBase(isEditing, isSticky, bgEven, bgOdd, isEven),
                            left: stickyLeft,
                            background: isEditing ? "rgba(99,102,241,0.2)"
                              : sc ? sc.bg
                              : isSticky ? (isEven ? bgEven : bgOdd)
                              : "transparent",
                          }}
                          onClick={() => startEdit(ri, field, v)}
                        >
                          {isEditing ? (
                            <input
                              autoFocus
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={handleNavKeyDown}
                              style={{ ...inputStyle, textAlign: isNote || fi === 1 ? "left" : "center" }}
                            />
                          ) : (
                            <span style={{
                              display: "block", padding: isNote || fi === 1 ? "5px 8px" : "5px 4px",
                              textAlign: isNote || fi === 1 ? "left" : "center",
                              whiteSpace: isNote ? "normal" : "nowrap",
                              overflow: "hidden", textOverflow: "ellipsis",
                              color: fi === 0 ? "#e05252"
                                : stars ? "#555e7a"
                                : sc ? sc.text
                                : v ? "var(--text)" : "rgba(255,255,255,0.15)",
                              fontWeight: fi <= 1 ? 600 : sc && !stars ? 600 : 400,
                              fontSize: stars ? Math.round(10 * zoom) : Math.round(11 * zoom),
                            }}>
                              {v || "·"}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

async function exportExcelVisite(tableData = []) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Mattone Rosso";
  const ws = wb.addWorksheet("VISITE E CONTRATTI");

  // Larghezze colonne
  ws.columns = [
    { width: 5 }, { width: 22 }, { width: 11 }, { width: 11 },
    { width: 11 }, { width: 11 }, { width: 11 }, { width: 9 },
    { width: 9 }, { width: 9 }, { width: 12 }, { width: 12 },
    { width: 12 }, { width: 14 }, { width: 16 }, { width: 13 }, { width: 30 },
  ];

  // Riga titolo
  ws.mergeCells("A1:Q1");
  const titleRow = ws.getRow(1);
  titleRow.height = 28;
  const tc = ws.getCell("A1");
  tc.value = "LISTA DIPENDENTI × VISITE E CONTRATTI — MATTONE ROSSO";
  tc.font = xlFont(true, XL_WHITE, 13);
  tc.fill = xlFill("8B1A1A");
  tc.alignment = { horizontal: "center", vertical: "middle" };

  // Riga intestazioni
  const hRow = ws.getRow(2);
  hRow.height = 36;
  COLONNE_VISITE.forEach((lbl, ci) => {
    const cell = hRow.getCell(ci + 1);
    cell.value = lbl;
    cell.font = xlFont(true, "C8D0E0", 9);
    cell.fill = xlFill("1A1F2E");
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = xlBorder();
  });

  // Dati
  tableData.forEach((row, ri) => {
    const vals = [row.n, row.nome, row.c, row.d, row.e, row.f, row.g, row.h, row.i, row.j, row.k, row.l, row.visite, row.ass, row.scadC, row.pds, row.note];
    const exRow = ws.getRow(ri + 3);
    exRow.height = 16;
    const isEven = ri % 2 === 0;
    vals.forEach((v, ci) => {
      const cell = exRow.getCell(ci + 1);
      cell.value = v || null;
      cell.border = xlBorder();
      cell.alignment = ci === 1 || ci === 16 ? xlAlign("left") : xlAlign("center");
      const sc = scadenzaColor(v);
      const stars = v && [...v].every(c => c === "*");
      if (ci === 0) { cell.font = xlFont(true, "C0392B", 9); }
      else if (ci === 1) { cell.font = xlFont(true, XL_TEXT, 9); cell.fill = xlFill(isEven ? "FAF0F0" : "FFF5F5"); }
      else if (stars) { cell.font = xlFont(false, "8090AA", 9); }
      else if (sc) { cell.font = xlFont(true, sc.text.replace("#",""), 9); cell.fill = { type:"pattern", pattern:"solid", fgColor:{ argb: sc.text.replace("#","") === "ef4444" ? "FFFDE8E8" : "FFFFF8E8" } }; }
      else if (v && (v.includes("INDET") || v === "ILLIMITATO" || v === "illimitato")) { cell.font = xlFont(true, "2563EB", 9); }
      else if (v === "CESSATO") { cell.font = xlFont(true, "EF4444", 9); }
      else { cell.font = xlFont(false, XL_TEXT, 9); cell.fill = xlFill(isEven ? XL_GRAY_LT : XL_WHITE); }
    });
  });

  ws.views = [{ state: "frozen", xSplit: 2, ySplit: 2 }];
  await downloadWorkbook(wb, "lista-dipendenti-visite.xlsx");
}
